/**
 * Směny — plán, šablony a náklady.
 *
 * Záložky Docházka, PIN přepustky a Nepřítomnost bydlí ve vlastních souborech
 * vedle; tady zůstalo to, co sdílí jedno období, jeden fetch a jednu sadu
 * modálů. Rozdělit i to znamená protáhnout padesát props skrz tři komponenty —
 * počká na etapu, ve které se plán stejně přepisuje nad `ModulLayout`.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import useSwipeTabs from '../../hooks/useSwipeTabs';
import { cardStyle as buildCardStyle } from '../../lib/uiTokens';
import VratitSmenuModal from '../../components/VratitSmenuModal';
import ProhoditSmenuModal from '../../components/ProhoditSmenuModal';
import { useTheme } from '../../contexts/ThemeContext';
import { muze } from '../../lib/moduly';
import TabBar from '../../components/layout/TabBar';
import NastaveniModulu, { NastaveniTlacitko } from '../../components/layout/NastaveniModulu';
import RoleSprava from '../../components/smeny/RoleSprava';
import SidePanel from '../../components/SidePanel';
import Dropdown from '../../components/receptura/Dropdown';
import ListToolbar, { PohledPill } from '../../components/layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../../components/layout/ListFiltr';
import Absence from './Absence';
import Dochazka from './Dochazka';
import FazeEditor from './casti/FazeEditor';
import PauzyEditor from './casti/PauzyEditor';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, Users, TrendingUp, Unlock, Edit2, Trash2, X, RefreshCw, RotateCcw, ArrowLeftRight, LayoutTemplate, Banknote, CalendarDays, CheckCircle2, AlertCircle, List, LayoutGrid, ClipboardCheck, Coffee, UserX, ListChecks, Activity, Truck, Crown } from 'lucide-react';
import { stripDiacritics, EDITOVATELNE_FAZE, initFaze, barvaSmen, csrf, vypocetHodin, fmtH, fmtKc, toLocalDateStr, todayStr, dnyLabels, stavMeta, TYP_CONFIG } from './spolecne';
import { StatCard, Modal, inputSt, Label, FormGrid, ModalActions, SmenaChip } from './casti/ui';

// Formulář směny stojí v inspektoru, ale jeho tlačítka v patičce panelu —
// tedy mimo <form>. Spojuje je tohle id přes atribut `form` na submitu.
const ID_FORMULARE_SMENY = 'formular-smeny';

// ─────────────────────────────────────────────────────────────
export default function Smeny({ user, initialTab }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);
    const [isNarrow, setIsNarrow] = useState(window.innerWidth < 1100);
    useEffect(() => {
        const fn = () => setIsNarrow(window.innerWidth < 1100);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    // Role rozhoduje jen o tom, co vrací backend (admin dostane i nepublikované
    // směny). Co uživatel v modulu vidí a smí měnit, řídí přístupy (šablona +
    // výjimky, Nastavení → Uživatelé).
    // Koncepty plánu vidí každý, kdo smí plán upravovat (shodně se serverem).
    const vidiKoncepty = ['superadmin', 'admin'].includes(user?.role) || muze(user, 'smeny.plan', 'upravuje');
    // Plán sloučil Přehled a Editor, takže i jejich dva klíče jsou teď jeden.
    // Akcent modulu se liší podle motivu — ve světlém je tmavší, aby držel
    // kontrast na bílé. Dřív byl natvrdo tmavý tón v obou.
    const SECTION_COLOR = barvaSmen(isDark);
    const upravujePlan = muze(user, 'smeny.plan', 'upravuje');

    // ── State ──
    // Staré prokliky (notifikace, piny, svalová paměť) nesou `prehled` nebo
    // `editor`; obojí je dnes Plán, jen s jiným výchozím zobrazením.
    // Staré adresy zůstávají funkční. `pin-prepustky` je teď pohled v Docházce,
    // `checklisty` odešly do vlastního modulu — ze Směn je pustíme na Plán,
    // ať z uloženého odkazu nezbyde prázdná obrazovka.
    const ALIAS_ZALOZEK = { prehled: 'plan', editor: 'plan', 'pin-prepustky': 'dochazka', checklisty: 'plan', naklady: 'dochazka', sablony: 'plan' };
    const [tab, setTab] = useState(ALIAS_ZALOZEK[initialTab] || initialTab || 'plan');

    // Zobrazení Plánu. `mrizka` je bývalý Editor — pracuje i s rozpracovanými
    // směnami; `kalendar` a `seznam` bývalý Přehled, který ukazuje publikované,
    // tedy to, co reálně vidí lidi. Mřížku dostane jen ten, kdo smí plánovat:
    // read-only uživateli by drag&drop a „+ Směna" svítily nadarmo.
    const [planPohled, setPlanPohled] = useState(initialTab === 'prehled' ? 'kalendar' : null);
    const jePlan = tab === 'plan';
    // Mobil má jediné zobrazení — kalendář (tam kreslený jako seznam dnů).
    // Plánovat se na telefonu nebude; mřížka a tabulka potřebují šířku, kterou
    // nemá, a přepínač by jen zabíral místo v liště.
    const pohled = isMobile ? 'kalendar' : (planPohled ?? (upravujePlan ? 'mrizka' : 'kalendar'));
    // Plánovač na mobilu upravuje přímo v kalendáři, a každá úprava je draft.
    // Kalendář z publikovaných by mu upravenou směnu schoval až do publikace,
    // takže tady kreslí z rozpracovaného plánu (jako mřížka) a drafty značí.
    const kalendarSRozpracovanymi = isMobile && upravujePlan;
    // Proklik s konkrétní záložkou (notifikace o nepřítomnosti) i do už otevřených Směn
    useEffect(() => {
        if (initialTab) setTab(ALIAS_ZALOZEK[initialTab] || initialTab);
    }, [initialTab]);
    // Musí sedět s tím, co pro výchozí tab nastaví efekt níž (Přehled = měsíc).
    // S 'tyden' se po mountu změnilo období a všechno se stáhlo podruhé.
    const [zobrazeni, setZobrazeni] = useState('mesic');
    const [smeny, setSmeny] = useState([]);
    const [sablony, setSablony] = useState([]);
    const [zamestnanci, setZamestnanci] = useState([]);
    const [kariery, setKariery] = useState([]);
    const [vsechnyRole, setVsechnyRole] = useState([]);
    const [nastaveniSmen, setNastaveniSmen] = useState(false);
    const [laborCost, setLaborCost] = useState(null);
    const [otevrene, setOtevrene] = useState([]);
    const [publishedSmeny, setPublishedSmeny] = useState([]); // publikované směny pro tab Přehled
    const [dnesTick, setDnesTick] = useState(0); // ruční spouštěč scrollu na dnešek (tlačítko Dnes)
    const [nedostupnosti, setNedostupnosti] = useState([]);
    const [fixniPlaty, setFixniPlaty] = useState([]);
    const [error, setError] = useState(null);

    const [vybranyDen, setVybranyDen] = useState(() => todayStr());
    const [tydenOd, setTydenOd] = useState(() => {
        const d = new Date(); const dow = d.getDay(); d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
        return toLocalDateStr(d);
    });
    const [mesic, setMesic] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    // Modals
    const [smenaModal, setSmenaModal] = useState(false);
    const [detailSmena, setDetailSmena] = useState(null); // vybraná směna — otevře inspektor

    // Horní hrana bloku s daty — tam, kde začíná pravý sloupec. Inspektor z ní
    // vyjíždí, takže lícuje se sloupcem, který překrývá, a ovládací lišta nad
    // ním (hledání, měsíc, pohledy) zůstává odkrytá a použitelná.
    //
    // Neposloucháme scroll: panel je `fixed` a lepit ho na rolující řádek by
    // znamenalo, že se při odrolování lišty vyšplhá nahoru a pak zarazí o nulu.
    // Hodnota se přeměří při přepnutí záložky/zobrazení a při změně okna —
    // což jsou jediné chvíle, kdy se horní hrana obsahu opravdu hne.
    const obsahRef = useRef(null);
    const [inspektorTop, setInspektorTop] = useState(0);
    const zmerObsah = useCallback(() => {
        const el = obsahRef.current;
        if (!el) return;
        setInspektorTop(Math.max(0, Math.round(el.getBoundingClientRect().top)));
    }, []);
    // Rozepsaná hodnota polí v detailu. Bez ní by se do inputu nedalo psát —
    // `detailSmena` se přepisuje z odpovědi serveru až po uložení.
    const [detailForm, setDetailForm] = useState(null);
    const [otevrenaModal, setOtevrenaModal] = useState(false);
    const [sablonModal, setSablonModal] = useState(false);
    const [checklistSablony, setChecklistSablony] = useState([]);
    const [genModal, setGenModal] = useState(false);

    // Forms
    const emptySmena = { datum: '', kariera_id: '', zamestnanec_id: '', cas_od: '', cas_do: '', hodin: '', pauzy: [], aktivity: [], faze: initFaze(), role_ids: [], min_uroven_kod: 'NOVICE', poznamka: '', otevrena: false, manazer_smeny: false, pozaduje_vozidlo: false, pozaduje_pokladnu: false };
    const emptyOtevrena = { datum: '', kariera_id: '', cas_od: '', cas_do: '', hodin: '', kapacita: 1, role_ids: [], min_uroven_kod: 'NOVICE', poznamka: '' };
    const emptySablon = { nazev: '', kod: '', kariera_id: '', role_ids: [], min_uroven_kod: 'NOVICE', cas_od: '', cas_do: '', hodin: '', pauzy: [], aktivity: [], faze: initFaze(), dny_v_tydnu: [1,2,3,4,5], delka_cyklu: 1, dny_v_cyklu: null, svatek: false, barva: '#F59E0B', manazer_smeny: false, pozaduje_vozidlo: false, pozaduje_pokladnu: false };

    const [smenaForm, setSmenaForm] = useState(emptySmena);
    const [editingSmena, setEditingSmena] = useState(null);
    const [existujiciManazer, setExistujiciManazer] = useState(null);
    const [otevrenaForm, setOtevrenaForm] = useState(emptyOtevrena);
    const [editingOtevrena, setEditingOtevrena] = useState(null);
    const [vratitSmenaAdmin, setVratitSmenaAdmin] = useState(null);
    const [prohoditSmena, setProhoditSmena] = useState(null);
    const [publikovatModal, setPublikovatModal] = useState(false);
    const [publikovatForm, setPublikovatForm] = useState({ od: '', do: '', mode: 'teď', naplanovat_na: '' });
    const [planovanePublikace, setPlanovanePublikace] = useState([]);
    const [planovanePublikaceExpand, setPlanovanePublikaceExpand] = useState(false);
    const [publikaceOpen, setPublikaceOpen] = useState(false);
    const [rozbalenoVPublikaci, setRozbalenoVPublikaci] = useState(null);
    const publikaceRef = React.useRef(null);
    const [sablonForm, setSablonForm] = useState(emptySablon);
    const [genForm, setGenForm] = useState({ od: '', do: '' });
    const [genPrirazeni, setGenPrirazeni] = useState([{ sablon_id: '', zamestnanec_id: '' }]);
    const [genVysledek, setGenVysledek] = useState(null);
    const [genLoading, setGenLoading] = useState(false);
    const [genMode, setGenMode] = useState('sablony'); // 'sablony' | 'prirazeni'
    const [genSelZam, setGenSelZam] = useState([]); // selected zamestnanec_ids for Z přiřazení
    const [saving, setSaving] = useState(false);
    const [prirazeni, setPrirazeni] = useState([]);
    // Šablony & automatika: hledání, pohled, vybraná šablona a formulář přiřazení.
    const [sablonyHledani, setSablonyHledani] = useState('');
    const [sablonyPohled, setSablonyPohled] = useState('aktivni');
    const [sablonyPanel, setSablonyPanel] = useState(false);  // katalog šablon v inspektoru
    const [vybranaSablona, setVybranaSablona] = useState(null);
    // Graf v panelu „Období“ — hodiny, nebo koruny. Rozpad nákladů po dnech
    // se sem přestěhoval ze zrušené záložky Náklady.
    const [grafJednotka, setGrafJednotka] = useState('hodiny');
    const [novePrirazeni, setNovePrirazeni] = useState({ zamestnanec_id: '', platnost_od: todayStr() });
    const [referencniDatum, setReferencniDatum] = useState('2026-01-05');
    const [pracovniDoba, setPracovniDoba] = useState([]);
    const [svatkyData, setSvatkyData] = useState(new Set());

    // Svátky potřebuje i Přehled (podbarvení dnů), pracovní doba je jen pro
    // denní/týdenní osu v Editoru — ta se dotáhne až s tabem editor.
    useEffect(() => {
        const rok = new Date().getFullYear();
        fetch(`/api/ciselniky/svatky?rok=${rok}`).then(r => r.ok ? r.json() : []).then(d => setSvatkyData(new Set(d))).catch(() => {});
    }, []);

    const jeSvatek = (datum) => svatkyData.has(datum);

    const getPracDobaProDen = (datum) => {
        const dayOfWeek = new Date(datum + 'T12:00:00').getDay();
        const den = dayOfWeek === 0 ? 7 : dayOfWeek;
        return pracovniDoba.find(d => d.den === den) || { cas_od: '08:00', cas_do: '22:00', otevreno: true };
    };

    // ── Computed ──
    // Vždy fetchovat celý měsíc (potřeba pro Otevřené, Náklady, Editor měsíc)
    const getObdobi = useCallback(() => {
        // V týdenním režimu rozšiř na měsíce, do kterých týden zasahuje (může být i 2 měsíce).
        // Bez toho fetch vracel jen měsíc dle `mesic`, který se při navigaci týdnů neaktualizoval —
        // editor pak neviděl směny ani schválené nepřítomnosti přes přelom měsíce.
        if (zobrazeni === 'tyden') {
            const start = new Date(tydenOd + 'T12:00:00');
            const end = new Date(start); end.setDate(end.getDate() + 6);
            const odD = new Date(start.getFullYear(), start.getMonth(), 1);
            const doD = new Date(end.getFullYear(), end.getMonth() + 1, 0);
            return { od: toLocalDateStr(odD), do: toLocalDateStr(doD) };
        }
        const [rok, mes] = mesic.split('-').map(Number);
        return {
            od: `${rok}-${String(mes).padStart(2, '0')}-01`,
            do: `${rok}-${String(mes).padStart(2, '0')}-${new Date(rok, mes, 0).getDate()}`,
        };
    }, [mesic, tydenOd, zobrazeni]);

    const getObdobiLabel = () => {
        if (zobrazeni === 'den') {
            return new Date(vybranyDen + 'T12:00:00').toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        }
        if (zobrazeni === 'tyden') {
            // Z `tydenOd`, ne z `getObdobi()` — to se pro fetch rozšiřuje na celé
            // měsíce, takže hlavička týdne hlásila „1. 9. – 30. 9.“.
            const zac = new Date(tydenOd + 'T12:00:00');
            const kon = new Date(zac); kon.setDate(kon.getDate() + 6);
            const fmt = (d) => d.toLocaleDateString('cs', { day: 'numeric', month: 'short' });
            return `${fmt(zac)} – ${fmt(kon)}`;
        }
        const [rok, mes] = mesic.split('-').map(Number);
        return new Date(rok, mes - 1).toLocaleDateString('cs', { month: 'long', year: 'numeric' });
    };

    const posun = (smer) => {
        if (zobrazeni === 'den') {
            const d = new Date(vybranyDen + 'T12:00:00'); d.setDate(d.getDate() + smer);
            setVybranyDen(toLocalDateStr(d));
        } else if (zobrazeni === 'tyden') {
            const d = new Date(tydenOd + 'T12:00:00'); d.setDate(d.getDate() + smer * 7);
            setTydenOd(toLocalDateStr(d));
        } else {
            const [rok, mes] = mesic.split('-').map(Number);
            const d = new Date(rok, mes - 1 + smer, 1);
            setMesic(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
    };

    // ── Detail směny: uložení, akce ──
    // Ukládá se po opuštění pole, ne tlačítkem: dokud se nepublikuje, je to
    // stejně jen draft, který nikdo nevidí. Odpověď serveru se vrací zpátky do
    // `detailSmena`, aby sloupec ukazoval to, co je opravdu uložené.
    const ulozDetail = async (zmeny) => {
        if (!detailSmena?.id) return;
        setSaving(true); setError(null);
        try {
            const res = await fetch(`/api/shifts/${detailSmena.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify(zmeny),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.error || 'Změnu se nepodařilo uložit'); return false; }
            setDetailSmena(data);
            refreshTab();
            return true;
        } catch (e) {
            setError('Změnu se nepodařilo uložit');
            return false;
        } finally { setSaving(false); }
    };

    // Uvolnit = sundat z člověka, směnu nechat. Častěji než mazat potřebuje
    // plánovač právě tohle — směna zůstane v rozpisu jako volný flek.
    const uvolniSmenu = async (sm) => {
        const datum = sm.datum?.split?.('T')?.[0] || String(sm.datum).split('T')[0];
        await fetch(`/api/shifts/${sm.id}`, {
            method: 'DELETE', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
        });
        const res = await fetch('/api/shifts/otevrena', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({
                datum,
                kariera_id: sm.kariera_id ?? sm.sablon?.kariera_id,
                sablon_id: sm.sablon_id,
                cas_od: (sm.cas_od || '').slice(0, 5),
                cas_do: (sm.cas_do || '').slice(0, 5),
                hodin: sm.hodin,
                kapacita: 1,
                poznamka: sm.poznamka,
                manazer_smeny: !!sm.manazer_smeny,
                pozaduje_vozidlo: !!sm.pozaduje_vozidlo,
                pozaduje_pokladnu: !!sm.pozaduje_pokladnu,
            }),
        });
        return res.ok;
    };

    const handleUvolnit = async (sm) => {
        setSaving(true); setError(null);
        try {
            if (!(await uvolniSmenu(sm))) { setError('Směnu se nepodařilo uvolnit'); return; }
            setDetailSmena(null);
            refreshTab();
        } catch (e) { setError('Směnu se nepodařilo uvolnit'); }
        finally { setSaving(false); }
    };

    // Duplikovat = otevře nový formulář předvyplněný podle téhle směny, jen bez
    // data — den si plánovač vybere sám, jinak by se kopie schovala pod originál.
    const handleDuplikovat = (sm) => {
        setSmenaForm({
            ...emptySmena,
            datum: '',
            kariera_id: sm.kariera_id ?? sm.sablon?.kariera_id ?? '',
            sablon_id: sm.sablon_id ?? '',
            zamestnanec_id: sm.zamestnanec_id ?? '',
            cas_od: (sm.cas_od || '').slice(0, 5),
            cas_do: (sm.cas_do || '').slice(0, 5),
            hodin: sm.hodin,
            min_uroven_kod: sm.min_uroven_kod || sm.sablon?.min_uroven_kod || 'NOVICE',
            poznamka: sm.poznamka || '',
            manazer_smeny: !!sm.manazer_smeny,
            pozaduje_vozidlo: !!sm.pozaduje_vozidlo,
            pozaduje_pokladnu: !!sm.pozaduje_pokladnu,
            faze: sm.faze || [],
            pauzy: sm.pauzy || [],
        });
        setEditingSmena(null);
        setDetailSmena(null);
        setSmenaModal(true);
        setError(null);
    };

    const pondeliTydne = (datum) => {
        const d = new Date((typeof datum === 'string' ? datum : toLocalDateStr(datum)) + 'T12:00:00');
        const dow = d.getDay();
        d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
        return toLocalDateStr(d);
    };

    const jdiNaDnes = () => {
        const d = new Date();
        setVybranyDen(toLocalDateStr(d));
        setTydenOd(pondeliTydne(d));
        setMesic(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        // Když už na aktuálním měsíci jsme, `mesic` se nezmění a scroll efekt by
        // se nespustil — tikneme mu tedy ručně, tlačítko Dnes má odrolovat vždy.
        prehledScrollDoRef.current = Date.now() + 4000;
        setDnesTick(t => t + 1);
    };

    const dnyTydne = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(tydenOd + 'T12:00:00'); d.setDate(d.getDate() + i);
        const datum = toLocalDateStr(d);
        return { datum, den: ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'][i], cislo: d.getDate(), jeVikend: i >= 5, jeDnes: datum === todayStr(), jeSvatek: jeSvatek(datum) };
    });

    const getMesicniDny = () => {
        const [rok, mes] = mesic.split('-').map(Number);
        const posledni = new Date(rok, mes, 0).getDate();
        let startDay = new Date(rok, mes - 1, 1).getDay();
        startDay = startDay === 0 ? 6 : startDay - 1;
        const dny = [];
        for (let i = 0; i < startDay; i++) dny.push(null);
        for (let d = 1; d <= posledni; d++) {
            const datum = `${rok}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dow = new Date(rok, mes - 1, d).getDay();
            dny.push({ datum, cislo: d, jeVikend: dow === 0 || dow === 6, jeDnes: datum === new Date().toISOString().split('T')[0] });
        }
        return dny;
    };

    // ── Fetch ──
    // Poll běží pořád dokola; kdyby každá odpověď volala setState, překreslil by se
    // celý strom Přehledu/Editoru (stovky dlaždic) i když se nic nezměnilo. Držíme
    // si proto poslední serializovanou odpověď a do state saháme jen při reálné změně.
    const lastPayloadRef = React.useRef({});
    const setIfChanged = useCallback((key, setter) => (data) => {
        const json = JSON.stringify(data);
        if (lastPayloadRef.current[key] === json) return;
        lastPayloadRef.current[key] = json;
        setter(data);
    }, []);

    const fetchSmeny = useCallback(() => {
        const { od, do: doo } = getObdobi();
        fetch(`/api/shifts?od=${od}&do=${doo}`).then(r => r.json()).then(setIfChanged('smeny', setSmeny)).catch(() => {});
    }, [getObdobi, setIfChanged]);

    const fetchLaborCost = useCallback(() => {
        const { od, do: doo } = getObdobi();
        fetch(`/api/shifts/labor-cost?od=${od}&do=${doo}`).then(r => r.json()).then(setIfChanged('laborCost', setLaborCost)).catch(() => {});
    }, [getObdobi, setIfChanged]);

    const fetchOtevrene = useCallback(() => {
        const { od, do: doo } = getObdobi();
        fetch(`/api/shifts/otevrene?od=${od}&do=${doo}`).then(r => r.json()).then(setIfChanged('otevrene', setOtevrene)).catch(() => {});
    }, [getObdobi, setIfChanged]);

    const fetchNedostupnosti = useCallback(() => {
        const { od, do: doo } = getObdobi();
        fetch(`/api/shifts/nedostupnosti?od=${od}&do=${doo}&stav=schvaleno`).then(r => r.json()).then(setIfChanged('nedostupnosti', setNedostupnosti)).catch(() => {});
    }, [getObdobi, setIfChanged]);

    const fetchPlanovanePublikace = useCallback(() => {
        fetch('/api/shifts/planovane-publikace').then(r => r.json()).then(setIfChanged('publikace', setPlanovanePublikace)).catch(() => {});
    }, [setIfChanged]);

    const fetchSablony = () => fetch('/api/shifts/sablony').then(r => r.json()).then(setSablony).catch(() => {});
    const fetchFixniPlaty = () => fetch('/api/konfigurace/vsechny-fixni-platy').then(r => r.json()).then(setFixniPlaty).catch(() => {});

    // Pozice manažerské směny mezi manažery téhož dne (např. "1/2"). Vrátí null,
    // pokud není manažer nebo je v dni jen jeden manažer.
    // Přehled ukazuje jen publikované směny — číslovat manažery podle `smeny`
    // (obsahuje i drafty) by tam dávalo jiné pořadí, než co je vidět.
    const getManazerPozice = useCallback((s) => {
        if (!s?.manazer_smeny) return null;
        const datumKey = (s.datum || '').split('T')[0];
        if (!datumKey) return null;
        const zdroj = (jePlan && pohled !== 'mrizka') ? publishedSmeny : smeny;
        const manazeri = zdroj
            .filter(x => x.manazer_smeny && (x.datum || '').split('T')[0] === datumKey && x.stav !== 'zrusena')
            .sort((a, b) => (a.cas_od || '').localeCompare(b.cas_od || ''));
        if (manazeri.length <= 1) return null;
        const idx = manazeri.findIndex(x => x.id === s.id);
        if (idx < 0) return null;
        return `${idx + 1}/${manazeri.length}`;
    }, [smeny, publishedSmeny, tab]);

    const ManazerKorunka = ({ s, size = 12 }) => {
        if (!s?.manazer_smeny) return null;
        const poz = getManazerPozice(s);
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#FF8A00', flexShrink: 0 }} title={poz ? `Manažer směny ${poz}` : 'Manažer směny'}>
                <Crown size={size} strokeWidth={2.5} aria-label="Manažer směny" />
                {poz && <span style={{ fontSize: Math.max(9, size - 4), fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{poz}</span>}
            </span>
        );
    };

    // Vyžaduje směna auto? Override pole s.pozaduje_vozidlo má přednost,
    // jinak dědí ze šablony (s.sablon?.pozaduje_vozidlo).
    const smenaPozadujeAuto = (s) => (s?.pozaduje_vozidlo ?? s?.sablon?.pozaduje_vozidlo) === true;
    const AutoIkona = ({ s, size = 12 }) => {
        if (!smenaPozadujeAuto(s)) return null;
        return <Truck size={size} style={{ color: '#3B82F6', flexShrink: 0 }} aria-label="Vyžaduje auto" />;
    };
    // Pokladna — analogie auta (override v pln_smeny > šablona).
    const smenaPozadujePokladnu = (s) => (s?.pozaduje_pokladnu ?? s?.sablon?.pozaduje_pokladnu) === true;
    const fetchPrirazeni = () => fetch('/api/shifts/sablony/prirazeni').then(r => r.json()).then(setPrirazeni).catch(() => {});
    const fetchPublished = useCallback(() => {
        const { od, do: doo } = getObdobi();
        // Kdo nevidí koncepty, dostane z /api/shifts stejná (publikovaná) data i bez published=1 —
        // druhý dotaz by byl doslova duplicitní request na celý měsíc.
        if (!vidiKoncepty) { fetch(`/api/shifts?od=${od}&do=${doo}`).then(r => r.json()).then(d => { setIfChanged('smeny', setSmeny)(d); setIfChanged('published', setPublishedSmeny)(d); }).catch(() => {}); return; }
        fetch(`/api/shifts?od=${od}&do=${doo}&published=1`).then(r => r.json()).then(setIfChanged('published', setPublishedSmeny)).catch(() => {});
    }, [getObdobi, vidiKoncepty, setIfChanged]);

    // Refresh dat, která právě otevřený tab reálně potřebuje. Dřív se po každé
    // mutaci i v každém pollu tahalo všech šest endpointů bez ohledu na tab:
    // Přehled tak čekal ve frontě za těžkým /shifts/labor-cost (používá ho jen tab
    // Náklady) a /api/shifts se stahovalo dvakrát na celý měsíc. Při přepnutí tabu
    // se data dotáhnou, protože `tab` je v závislostech.
    const refreshTab = useCallback(() => {
        if (tab === 'plan') {
            // Plán potřebuje obojí: mřížka pracuje s rozpracovanými směnami,
            // kalendář a seznam ukazují publikované. Stáhne se to naráz, aby
            // přepnutí zobrazení nečekalo na další kolo fetchů.
            fetchPublished(); fetchSmeny(); fetchOtevrene(); fetchNedostupnosti(); fetchPlanovanePublikace();
            fetchLaborCost();   // panel „Období“ staví plán proti skutečnosti
        }
    }, [tab, fetchSmeny, fetchLaborCost, fetchOtevrene, fetchPublished, fetchNedostupnosti, fetchPlanovanePublikace]);

    // Auto-refresh — aby uživatelé viděli, když si někdo vezme otevřenou směnu apod.
    // Polling 15 s (dřív 5 s — na měsíční přehled se stovkami směn to byl zbytečný
    // tlak na server), jen pokud je záložka aktivní a není otevřený žádný editační
    // modal (aby se nerušila rozpracovaná editace). Návrat na záložku refreshne ihned.
    useEffect(() => {
        const tick = () => {
            if (document.visibilityState !== 'visible') return;
            const editing = smenaModal || otevrenaModal || sablonModal || genModal
                || publikovatModal || !!vratitSmenaAdmin || !!prohoditSmena || !!detailSmena;
            if (editing) return;
            refreshTab();
        };
        const iv = setInterval(tick, 15000);
        const onVis = () => { if (document.visibilityState === 'visible') tick(); };
        document.addEventListener('visibilitychange', onVis);
        return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
    }, [refreshTab, smenaModal, otevrenaModal, sablonModal, genModal, publikovatModal, vratitSmenaAdmin, prohoditSmena, detailSmena]);

    // Číselníky, které potřebuje i Přehled (barvy/názvy šablon, jména, kariéry).
    // Zbytek se dotahuje až v tabu, který ho reálně používá — při otevření stránky
    // šlo na server 16 requestů naráz a směny čekaly ve frontě za nimi.
    useEffect(() => {
        fetch('/api/ciselniky/kariery').then(r => r.json()).then(setKariery);
        fetch('/api/zamestnanci').then(r => r.json()).then(setZamestnanci);
        fetchSablony();
    }, []);

    // Data pro Plán — patří sem i přiřazení šablon: katalog šablon jede
    // v inspektoru nad Plánem a panel „Období“ z nich hlásí šablony bez jezdce.
    useEffect(() => {
        if (tab !== 'plan') return;
        fetchPrirazeni();
        fetchFixniPlaty();
        fetch('/api/konfigurace/pracovni-doba').then(r => r.ok ? r.json() : []).then(setPracovniDoba).catch(() => {});
        fetch('/api/nastaveni').then(r => r.ok ? r.json() : {}).then(d => { if (d.smeny_referencni_datum) setReferencniDatum(d.smeny_referencni_datum); }).catch(() => {});
    }, [tab]);

    // Číselníky pro editační modaly (role v pickeru, checklisty v řádcích aktivit).
    // Modal jde otevřít i z Přehledu, proto se váže na modal, ne na tab.
    useEffect(() => {
        if (!sablonModal && !smenaModal && !otevrenaModal) return;
        fetch('/api/role').then(r => r.json()).then(setVsechnyRole).catch(() => {});
        fetch('/api/checklisty/sablony').then(r => r.ok ? r.json() : []).then(setChecklistSablony).catch(e => console.error('Fetch checklist templates failed:', e));
    }, [sablonModal, smenaModal, otevrenaModal]);

    // Načíst KOLIDUJÍCÍHO manažera pro den — checkbox disablujeme jen pokud
    // existuje manažer, jehož čas se překrývá s touto směnou. Dva manažeři
    // za den (ranní + odpolední bez překryvu) jsou povoleni.
    useEffect(() => {
        if (!smenaModal || !smenaForm.datum) { setExistujiciManazer(null); return; }
        const params = new URLSearchParams({ datum: smenaForm.datum });
        if (editingSmena?.id) params.set('exclude_id', editingSmena.id);
        if (smenaForm.cas_od) params.set('cas_od', smenaForm.cas_od);
        if (smenaForm.cas_do) params.set('cas_do', smenaForm.cas_do);
        fetch(`/api/shifts/manazer-dne?${params}`)
            .then(r => r.ok ? r.json() : { manazer: null })
            .then(d => setExistujiciManazer(d?.manazer || null))
            .catch(() => setExistujiciManazer(null));
    }, [smenaModal, smenaForm.datum, smenaForm.cas_od, smenaForm.cas_do, editingSmena?.id]);

    // Detail směny i její formulář jsou oba inspektor a sedí na témž místě —
    // otevřít je naráz by znamenalo dva panely přes sebe. Formulář má přednost.
    // Hlídá se to tady, ne u šesti míst, odkud se formulář otevírá.
    useEffect(() => {
        if (smenaModal) setDetailSmena(null);
    }, [smenaModal]);

    useEffect(() => {
        refreshTab();
    }, [refreshTab]);

    // ── Handlers ──
    const handlePridatSmenu = async (e) => {
        e.preventDefault(); setError(null); setSaving(true);
        const isOtevrena = smenaForm.otevrena;
        let url, method, body;
        if (editingSmena) {
            url = `/api/shifts/${editingSmena.id}`;
            method = 'PUT';
            body = { kariera_id: smenaForm.kariera_id, cas_od: smenaForm.cas_od, cas_do: smenaForm.cas_do, hodin: smenaForm.hodin, min_uroven_kod: smenaForm.min_uroven_kod, role_ids: smenaForm.role_ids || [], poznamka: smenaForm.poznamka, zaskok: !!smenaForm._zaskok, aktivity: smenaForm.aktivity || [], pauzy: smenaForm.pauzy || [], faze: smenaForm.faze || [], manazer_smeny: !!smenaForm.manazer_smeny, pozaduje_vozidlo: !!smenaForm.pozaduje_vozidlo, pozaduje_pokladnu: !!smenaForm.pozaduje_pokladnu };
            if (!isOtevrena) body.zamestnanec_id = smenaForm.zamestnanec_id;
        } else if (isOtevrena) {
            url = '/api/shifts/otevrena';
            method = 'POST';
            body = { datum: smenaForm.datum, kariera_id: smenaForm.kariera_id, cas_od: smenaForm.cas_od, cas_do: smenaForm.cas_do, hodin: smenaForm.hodin, min_uroven_kod: smenaForm.min_uroven_kod, role_ids: smenaForm.role_ids || [], kapacita: 1, poznamka: smenaForm.poznamka, manazer_smeny: !!smenaForm.manazer_smeny, pozaduje_vozidlo: !!smenaForm.pozaduje_vozidlo, pozaduje_pokladnu: !!smenaForm.pozaduje_pokladnu };
        } else {
            url = '/api/shifts';
            method = 'POST';
            body = { datum: smenaForm.datum, kariera_id: smenaForm.kariera_id, zamestnanec_id: smenaForm.zamestnanec_id, cas_od: smenaForm.cas_od, cas_do: smenaForm.cas_do, hodin: smenaForm.hodin, min_uroven_kod: smenaForm.min_uroven_kod, role_ids: smenaForm.role_ids || [], poznamka: smenaForm.poznamka, draft: true, zaskok: !!smenaForm._zaskok, manazer_smeny: !!smenaForm.manazer_smeny, pozaduje_vozidlo: !!smenaForm.pozaduje_vozidlo, pozaduje_pokladnu: !!smenaForm.pozaduje_pokladnu };
        }
        let res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
        let data = await res.json();
        if (!res.ok && (data.error?.includes('kvalifikace') || data.error?.includes('Nedostatečná'))) {
            if (confirm(`${data.detail || data.error}\n\nChcete přesto pokračovat? (záskok)`)) {
                body._force = true;
                res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                data = await res.json();
            } else { setSaving(false); return; }
        }
        setSaving(false);
        if (!res.ok) { setError(data.error || data.message || 'Chyba'); return; }
        setSmenaModal(false); setEditingSmena(null); setSmenaForm(emptySmena);
        refreshTab();
    };

    const handleEditSmenu = (s) => {
        setEditingSmena(s);
        setSmenaForm({ datum: s.datum?.split('T')[0] ?? '', kariera_id: s.kariera_id ?? '', zamestnanec_id: s.zamestnanec_id ?? '', cas_od: s.cas_od?.slice(0, 5) ?? '', cas_do: s.cas_do?.slice(0, 5) ?? '', hodin: s.hodin ?? '', role_ids: (s.role || []).map(r => r.id), min_uroven_kod: s.min_uroven_kod || s.sablon?.min_uroven_kod || 'NOVICE', poznamka: s.poznamka ?? '', otevrena: s.otevrena || false, _zaskok: s.zaskok || false, aktivity: s.aktivity || s.sablon?.aktivity || [], pauzy: s.pauzy || s.sablon?.pauzy || [], faze: initFaze(s.faze || s.sablon?.faze), manazer_smeny: !!s.manazer_smeny, pozaduje_vozidlo: s.pozaduje_vozidlo ?? !!s.sablon?.pozaduje_vozidlo, pozaduje_pokladnu: s.pozaduje_pokladnu ?? !!s.sablon?.pozaduje_pokladnu });
        setSmenaModal(true); setError(null);
    };

    // Bere směnu i samé id — starší volání (SmenaChip) posílají id.
    // Draft se maže bez ptání: nikdo ho zatím neviděl. Publikovaná směna už
    // někomu stojí v plánu, takže se ptáme.
    const handleSmazatSmenu = async (smenaOrId) => {
        const sm = typeof smenaOrId === 'object' && smenaOrId ? smenaOrId
            // Chip posílá jen id — dohledat, ať víme, jestli je otevřená nebo převzatá
            : (smeny.find(x => x.id === smenaOrId) || otevrene.find(x => x.id === smenaOrId) || null);
        const id = sm ? sm.id : smenaOrId;
        // Otevřená směna, kterou si už někdo vzal: jejich směny zůstávají.
        const prevzali = sm?.prevzali || [];
        if (prevzali.length) {
            const jmena = prevzali.map(p => p.zamestnanec).join(', ');
            if (!confirm(`Smazat otevřenou směnu?\n\nUž si ji vzal(i): ${jmena}. Jejich směny zůstanou, zmizí jen volná místa.\n\nZruší se až při publikaci, do té doby ji jde vrátit.`)) return false;
        } else if (sm && sm.draft === false) {
            const kdo = sm.zamestnanec ? `${sm.zamestnanec.jmeno} ${sm.zamestnanec.prijmeni}` : 'nepřiřazenou směnu';
            if (!confirm(`Směnu už lidi vidí v plánu. Opravdu ji smazat ${sm.zamestnanec ? `\u2014 ${kdo}` : ''}?\n\nZruší se až při publikaci, do té doby ji jde vrátit.${sm.parent_otevrena_id ? '\n\nSměna je převzatá z otevřené — smaže se i to místo, k převzetí se nevrátí. Chceš-li ji nabídnout dál, použij „Uvolnit“.' : ''}`)) return false;
        }
        await fetch(`/api/shifts/${id}/kos`, { method: 'POST', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() } });
        if (detailSmena?.id === id) setDetailSmena(null);
        setVratitSmazani({ id, popis: sm ? popisSmeny(sm) : null, kdy: Date.now() });
        refreshTab();
    };

    // Popis do pruhu s vrácením — ať je poznat, co zrovna zmizelo.
    const popisSmeny = (sm) => {
        const d = sm.datum?.split?.('T')?.[0] || String(sm.datum).split('T')[0];
        const den = new Date(d + 'T12:00:00').toLocaleDateString('cs', { weekday: 'short', day: 'numeric', month: 'numeric' });
        const kod = sm.sablon?.kod || sablony.find(x => x.id === sm.sablon_id)?.kod || '';
        const kdo = sm.zamestnanec ? ` · ${sm.zamestnanec.jmeno} ${sm.zamestnanec.prijmeni}` : '';
        return `${kod ? kod + ' · ' : ''}${den}${kdo}`;
    };
    const handleObnovitSmenu = async (id) => {
        await fetch(`/api/shifts/${id}/obnovit`, { method: 'POST', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() } });
        refreshTab();
    };

    const exitSelectionMode = () => { setSelectedShiftIds([]); setMoveTargetModal(false); };
    const toggleShiftSelection = (id) => setSelectedShiftIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleBatchSmazat = async () => {
        if (selectedShiftIds.length === 0) return;
        if (!confirm(`Smazat ${selectedShiftIds.length} směn?\n\nZruší se až při publikaci — do té doby je jde vrátit.`)) return;
        await Promise.all(selectedShiftIds.map(id => fetch(`/api/shifts/${id}/kos`, { method: 'POST', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() } })));
        exitSelectionMode(); refreshTab();
    };

    // Uvolnit hromadně — táž akce jako v detailu, jen nad víc směnami. Postupně,
    // ne paralelně: každá směna je smazání + založení a souběžně si lezou do zelí.
    const handleBatchUvolnit = async () => {
        if (selectedShiftIds.length === 0) return;
        if (!confirm(`Uvolnit ${selectedShiftIds.length} směn?\n\nSměny zůstanou v rozpisu jako volné fleky bez člověka.`)) return;
        const vybrane = selectedShiftIds
            .map(id => smeny.find(x => x.id === id))
            .filter(x => x && !x.otevrena);
        for (const sm of vybrane) await uvolniSmenu(sm);
        exitSelectionMode(); refreshTab();
    };

    const handleBatchPresun = async (cilZamId) => {
        if (selectedShiftIds.length === 0 || !cilZamId) return;
        await Promise.all(selectedShiftIds.map(id => fetch(`/api/shifts/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify({ zamestnanec_id: cilZamId }) })));
        exitSelectionMode(); refreshTab();
    };

    const handlePridatOtevrenu = async (e) => {
        e.preventDefault(); setError(null); setSaving(true);
        const url = editingOtevrena ? `/api/shifts/${editingOtevrena.id}` : '/api/shifts/otevrena';
        const method = editingOtevrena ? 'PUT' : 'POST';
        const body = { ...otevrenaForm, role_ids: otevrenaForm.role_ids || [] };
        const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
        setSaving(false);
        if (!res.ok) { const data = await res.json(); setError(data.error || data.message || 'Chyba'); return; }
        setOtevrenaModal(false); setEditingOtevrena(null); setOtevrenaForm(emptyOtevrena);
        refreshTab();
    };

    const handleEditOtevrenu = (o) => {
        setEditingSmena(o);
        setSmenaForm({
            datum: (o.datum || '').split('T')[0] ?? '',
            kariera_id: o.kariera_id ?? o.sablon?.kariera_id ?? '',
            zamestnanec_id: '',
            cas_od: o.cas_od?.slice(0, 5) ?? '',
            cas_do: o.cas_do?.slice(0, 5) ?? '',
            hodin: o.hodin ?? '',
            pauzy: o.pauzy || o.sablon?.pauzy || [],
            aktivity: o.aktivity || o.sablon?.aktivity || [],
            faze: initFaze(o.faze || o.sablon?.faze),
            role_ids: (o.role || []).map(r => r.id),
            stav: o.stav ?? 'planovana',
            poznamka: o.poznamka ?? '',
            otevrena: true,
            min_uroven_kod: o.min_uroven_kod || o.sablon?.min_uroven_kod || 'NOVICE',
            manazer_smeny: !!o.manazer_smeny,
            pozaduje_vozidlo: o.pozaduje_vozidlo ?? !!o.sablon?.pozaduje_vozidlo,
            pozaduje_pokladnu: o.pozaduje_pokladnu ?? !!o.sablon?.pozaduje_pokladnu,
        });
        setSmenaModal(true); setError(null);
    };

    const handlePridatSablon = async (e) => {
        e.preventDefault(); setSaving(true);
        const res = await fetch('/api/shifts/sablony', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(sablonForm) });
        setSaving(false);
        if (res.ok) { setSablonModal(false); setSablonForm(emptySablon); fetchSablony(); }
    };

    const handleGenerovat = async (force = false) => {
        if (!genForm.od || !genForm.do) return;
        setGenLoading(true); if (!force) setGenVysledek(null);

        let url, body;
        if (genMode === 'prirazeni') {
            if (genSelZam.length === 0) { setGenLoading(false); return; }
            url = '/api/shifts/generuj-z-rozvrhu';
            body = { od: genForm.od, do: genForm.do, zamestnanec_ids: genSelZam, _force: force };
        } else {
            if (genPrirazeni.some(p => !p.sablon_id)) { setGenLoading(false); return; }
            url = '/api/shifts/generuj';
            body = {
                od: genForm.od,
                do: genForm.do,
                sablony: genPrirazeni.map(p => ({
                    sablon_id: parseInt(p.sablon_id),
                    zamestnanec_id: p.zamestnanec_id ? parseInt(p.zamestnanec_id) : null,
                })),
                _force: force,
            };
        }

        const res = await fetch(url, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify(body),
        });
        const vysledek = await res.json();
        setGenVysledek(vysledek);
        setGenLoading(false);

        // Pokud jsou chyby kvalifikace a nebylo force — nabídnout záskok
        if (!force && vysledek.chyby?.length > 0 && confirm(`${vysledek.chyby.length} směn nelze vytvořit kvůli kvalifikaci.\n\nChcete pokračovat jako záskok?`)) {
            handleGenerovat(true);
            return;
        }

        refreshTab();
    };

    const handlePrevzit = async (otevrenaId, zamestnanecId) => {
        setError(null);
        let res = await fetch(`/api/shifts/${otevrenaId}/prevzit`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify({ zamestnanec_id: zamestnanecId }) });
        if (!res.ok) {
            const data = await res.json();
            if ((data.error?.includes('kvalifikace') || data.error?.includes('Nedostatečná')) && upravujePlan) {
                if (confirm(`${data.detail || data.error}\n\nChcete přesto přiřadit? (záskok)`)) {
                    res = await fetch(`/api/shifts/${otevrenaId}/prevzit`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify({ zamestnanec_id: zamestnanecId, _force: true }) });
                    if (!res.ok) { const d2 = await res.json(); setError(d2.error || 'Chyba'); return; }
                } else return;
            } else { setError(data.error || 'Chyba'); return; }
        }
        refreshTab();
    };

    const handleVratit = (smenaOrId) => {
        const s = typeof smenaOrId === 'object' ? smenaOrId : { id: smenaOrId };
        setVratitSmenaAdmin(s);
    };

    const handleVratitSuccess = () => {
        refreshTab();
    };

    // ── Styles ──
    // Sdílená „glass" karta — stejná definice jako Finance/AOP (lib/uiTokens).
    const glassCard = buildCardStyle(isDark);

    const mutedColor = 'var(--text-tertiary)';
    const iStyle = inputSt(isDark);

    // Jeden přepínač pro celý Plán, nakreslený na stejném místě ve všech třech
    // zobrazeních — dřív to byly dvě záložky a dva různé přepínače.
    const POHLEDY = [
        { id: 'mrizka',   label: 'Mřížka',   jenPlanovac: true },
        { id: 'kalendar', label: 'Kalendář', jenPlanovac: false },
        { id: 'seznam',   label: 'Seznam',   jenPlanovac: false },
    ];
    const PrepinacPohledu = () => (
        <div style={{
            display: 'flex', gap: 3, padding: 3, borderRadius: 7,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
        }}>
            {POHLEDY.filter(p => !p.jenPlanovac || upravujePlan).map(p => (
                <button key={p.id} onClick={() => setPlanPohled(p.id)} style={{
                    padding: '5px 13px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600,
                    background: pohled === p.id ? `${SECTION_COLOR}20` : 'transparent',
                    color: pohled === p.id ? SECTION_COLOR : mutedColor,
                }}>{p.label}</button>
            ))}
        </div>
    );


    // ── Tab config ──
    // Záložky = podstránky modulu; vidí jen ty, ke kterým má přístup.
    const tabs = [
        { id: 'plan',          label: 'Plán',          icon: Calendar },
        { id: 'sablony',       label: 'Šablony',       icon: LayoutTemplate },
        { id: 'dochazka',      label: 'Docházka',      icon: ClipboardCheck },
        { id: 'nepritomnost',  label: 'Nepřítomnost',  icon: UserX },
    ].filter(t => muze(user, `smeny.${t.id}`));
    const swipeProps = useSwipeTabs(tabs.map(t => t.id), tab, setTab);
    // Otevřená záložka, ke které přístup není (třeba jen Nepřítomnost) → první dostupná
    useEffect(() => {
        if (tabs.length && !tabs.some(t => t.id === tab)) setTab(tabs[0].id);
    }, [tabs.map(t => t.id).join(','), tab]);

    // ── Editor state ──
    const [editorMode, setEditorMode] = useState('mesic');
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [cellDropdown, setCellDropdown] = useState(null);
    // Kolik směn se vešlo do buňky kalendáře. Kalendář má padnout na stránku,
    // takže výška řádku je daná a počet dlaždic se jí přizpůsobí — zbytek se
    // sbalí do „+N“, které přepne na ten týden.
    const kalendarMrizkaRef = React.useRef(null);
    const [radekKalendareVyska, setRadekKalendareVyska] = useState(0);

    // Poslední smazaná směna — pruh s „Vrátit“. Není to globální toast:
    // patří k místu, kde se mazalo, a má akci, kterou globální toast neumí.
    const [vratitSmazani, setVratitSmazani] = useState(null);

    // Hlavička Plánu podle normy (ListToolbar): hledání, filtr, pohledy.
    // Pohled = předem daná sada dat (právě jeden), filtr = vlastní zúžení té sady.
    const [pohledPlanu, setPohledPlanu] = useState('vse');

    // Měření horní hrany obsahu (viz `zmerObsah` výš) stojí až tady, protože
    // `editorMode` se deklaruje o pár řádků nad tímhle — pole závislostí se
    // vyhodnocuje při renderu a nahoře by spadlo na TDZ.
    useEffect(() => {
        zmerObsah();
        window.addEventListener('resize', zmerObsah);
        return () => window.removeEventListener('resize', zmerObsah);
    }, [zmerObsah, tab, pohled, editorMode, isMobile, isNarrow]);
    const [planHledani, setPlanHledani] = useState('');
    const PRAZDNE_FILTRY = { kariera: [], sablona: [], stav: [], pozaduje: [] };
    const [planFiltry, setPlanFiltry] = useState(PRAZDNE_FILTRY);
    const zmenFiltrPlanu = (id, hodnota) => setPlanFiltry(f => ({ ...f, [id]: hodnota }));
    const zrusFiltryPlanu = () => setPlanFiltry(PRAZDNE_FILTRY);
    const [dragData, setDragData] = useState(null);
    // Výšky řádků se synchronizují přes HTML table layout

    // Hromadný výběr směn — na desktopu Ctrl (na Macu Cmd) + klik, jako ve
    // správci souborů. Dřív se zapínal tlačítkem „Vybrat" a pak se musel zase
    // vypínat; teď výběr vzniká rovnou kliknutím a Esc ho zruší. Podržený Ctrl
    // ukáže lištu hromadných akcí dopředu, ať je vidět, že výběr běží.
    const [selectedShiftIds, setSelectedShiftIds] = useState([]);
    const [moveTargetModal, setMoveTargetModal] = useState(false);
    const [ctrlDrzi, setCtrlDrzi] = useState(false);
    const selectionMode = ctrlDrzi || selectedShiftIds.length > 0;
    useEffect(() => {
        if (isMobile || !jePlan) { setCtrlDrzi(false); return; }
        const dolu = (e) => {
            if (e.key === 'Control' || e.key === 'Meta') setCtrlDrzi(true);
            if (e.key === 'Escape') setSelectedShiftIds([]);
        };
        const nahoru = (e) => { if (e.key === 'Control' || e.key === 'Meta') setCtrlDrzi(false); };
        // Ctrl pustěný mimo okno (Ctrl+Tab do jiné karty) by jinak zůstal „držený".
        const pryc = () => setCtrlDrzi(false);
        window.addEventListener('keydown', dolu);
        window.addEventListener('keyup', nahoru);
        window.addEventListener('blur', pryc);
        return () => {
            window.removeEventListener('keydown', dolu);
            window.removeEventListener('keyup', nahoru);
            window.removeEventListener('blur', pryc);
        };
    }, [isMobile, jePlan]);
    const vybiraKlik = (e) => !isMobile && (e.ctrlKey || e.metaKey);
    // Mobil zná jen měsíc — týdenní přepínač tam v liště není.
    useEffect(() => { if (isMobile) setEditorMode('mesic'); }, [isMobile]);

    // Období je společné pro celý Plán — přepnu-li na týden, drtí ho mřížka,
    // kalendář i seznam stejně. Dřív uměla týden jen mřížka a přechod na
    // kalendář uživatele tiše vrátil na celý měsíc.
    useEffect(() => {
        if (!jePlan) return;
        setZobrazeni(editorMode);
    }, [jePlan, editorMode]);

    // Auto-scroll na dnešní sloupec v editoru (po otevření tabu, přepnutí módu, změně období)
    const editorScrollRef = React.useRef(null);
    useEffect(() => {
        if (!jePlan || pohled !== 'mrizka') return;
        const t = setTimeout(() => {
            const container = editorScrollRef.current;
            if (!container) return;
            const cell = container.querySelector('[data-today="1"]');
            if (!cell) return;
            const left = cell.offsetLeft - (container.clientWidth / 2) + (cell.clientWidth / 2);
            container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }, 60);
        return () => clearTimeout(t);
    }, [jePlan, pohled, editorMode, tydenOd, mesic]);

    // ── Mobil: měsíc se posouvá dotažením seznamu za okraj ──
    // Šipky a přepínač období z lišty zmizely. Kdo dojede na začátek měsíce
    // a táhne dál, dostane předchozí; na konci a dál nahoru další — jako
    // pull-to-refresh, jen místo obnovení přijde sousední měsíc. Stačí dotáhnout
    // nad práh a pustit. Průběh tahu se kreslí přímo do DOM: stav by při
    // každém pohybu prstu překreslil celý modul.
    const PRAH_TAHU = 72;
    const seznamDnuRef = useRef(null);
    const seznamObsahRef = useRef(null);
    const tahNahoreRef = useRef(null);
    const tahDoleRef = useRef(null);
    const tahRef = useRef(null);          // { y, nahore, dole, smer, delka } od začátku dotyku
    const cilPoTahuRef = useRef(null);    // { kam: 'zacatek' | 'konec', do: ms } — kam odrolovat nový měsíc
    const sousedniMesic = (smer) => {
        const [rok, mes] = mesic.split('-').map(Number);
        return new Date(rok, mes - 1 + smer, 1).toLocaleDateString('cs', { month: 'long', year: 'numeric' });
    };
    const ukazTah = (smer, delka) => {
        const obsah = seznamObsahRef.current;
        if (obsah) {
            obsah.style.transition = delka ? 'none' : 'transform .2s ease';
            obsah.style.transform = delka ? `translateY(${smer < 0 ? delka : -delka}px)` : '';
        }
        [[tahNahoreRef, -1], [tahDoleRef, 1]].forEach(([ref, s]) => {
            const el = ref.current;
            if (!el) return;
            const aktivni = s === smer && delka > 0;
            el.style.height = aktivni ? `${delka}px` : '0px';
            if (!aktivni) return;
            const hotovo = delka >= PRAH_TAHU;
            el.textContent = `${hotovo ? 'Pusť pro' : 'Táhni pro'} ${el.dataset.mesic}`;
            el.style.color = hotovo ? SECTION_COLOR : mutedColor;
        });
    };
    const tahZacatek = (e) => {
        const el = seznamDnuRef.current;
        if (!el) return;
        cilPoTahuRef.current = null; // uživatel sahá sám — nový měsíc už mu scroll nepřebírá
        tahRef.current = {
            y: e.touches[0].clientY,
            nahore: el.scrollTop <= 0,
            dole: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
            smer: 0, delka: 0,
        };
    };
    const tahPohyb = (e) => {
        const t = tahRef.current, el = seznamDnuRef.current;
        if (!t || !el) return;
        const dy = e.touches[0].clientY - t.y;
        // Okraj se hlídá i během tahu: kdo začal nahoře, odroloval dolů a pak
        // se vrací, nesmí tahat měsíc, dokud seznam znovu nestojí na začátku.
        if (dy > 0 && t.nahore && el.scrollTop <= 0) { t.smer = -1; t.delka = Math.min(dy * 0.5, 110); }
        else if (dy < 0 && t.dole && el.scrollTop + el.clientHeight >= el.scrollHeight - 1) { t.smer = 1; t.delka = Math.min(-dy * 0.5, 110); }
        else { t.smer = 0; t.delka = 0; }
        ukazTah(t.smer, t.delka);
    };
    const tahKonec = () => {
        const t = tahRef.current;
        tahRef.current = null;
        ukazTah(0, 0);
        if (!t || !t.smer || t.delka < PRAH_TAHU) return;
        // Do předchozího měsíce se přijíždí odspodu — navazuje na to, odkud
        // člověk táhl; do dalšího od začátku.
        cilPoTahuRef.current = { kam: t.smer < 0 ? 'konec' : 'zacatek', do: Date.now() + 3000 };
        posun(t.smer);
    };
    // Nový měsíc se dotahuje ze serveru, takže „na konec" se roluje znovu,
    // kdykoli dorazí data — ale jen chvíli po tahu a jen dokud se člověk nedotkne.
    useEffect(() => {
        const cil = cilPoTahuRef.current, el = seznamDnuRef.current;
        if (!cil || !el) return;
        if (Date.now() > cil.do) { cilPoTahuRef.current = null; return; }
        el.scrollTop = cil.kam === 'konec' ? el.scrollHeight : 0;
    }, [mesic, smeny, publishedSmeny, otevrene, nedostupnosti]);

    // ── Auto-scroll na dnešek v Přehledu ──
    // Seznam i kalendář začínají prvním dnem měsíce, takže na konci měsíce byl
    // dnešek klidně o pár obrazovek níž. Scroll se opakuje, dokud se dotahují data
    // (prázdný seznam má jiné výšky než plný), ale jen ~4 s od otevření tabu nebo
    // přepnutí měsíce — pozdější poll už uživateli se scrollem neposkočí.
    const prehledScrollDoRef = React.useRef(0);
    const prehledViewKlic = `${tab}|${mesic}|${pohled}|${isMobile || isNarrow ? 'list' : 'grid'}`;
    useEffect(() => {
        // Po tahu měsícem má přednost začátek/konec měsíce, ne dnešek.
        prehledScrollDoRef.current = (jePlan && pohled !== 'mrizka' && !cilPoTahuRef.current) ? Date.now() + 4000 : 0;
    }, [prehledViewKlic, tab]);

    useEffect(() => {
        if (!jePlan || pohled === 'mrizka') return;
        if (mesic !== todayStr().slice(0, 7)) return; // zobrazený měsíc dnešek neobsahuje
        if (Date.now() > prehledScrollDoRef.current) return;
        // Seznam dnů/řádků odroluj tak, aby dnešek začínal nahoře; v měsíčním
        // kalendáři stačí 'nearest' — mřížka je většinou celá vidět a 'start' by
        // zbytečně odscrolloval filtry a přepínač měsíce mimo obrazovku.
        const blok = (isMobile || isNarrow || pohled === 'seznam') ? 'start' : 'nearest';
        const t = setTimeout(() => {
            if (Date.now() > prehledScrollDoRef.current) return;
            document.querySelector('[data-prehled-dnes="1"]')?.scrollIntoView({ block: blok, behavior: 'auto' });
        }, 60);
        return () => clearTimeout(t);
    }, [prehledViewKlic, tab, mesic, pohledPlanu, publishedSmeny, otevrene, nedostupnosti, dnesTick]);

    // Výšky synchronizovány automaticky přes table layout

    // Zavřít dropdown klikem kamkoli na stránku (mimo dropdown)
    const dropdownRef = React.useRef(null);
    useEffect(() => {
        if (!cellDropdown) return;
        const handler = (e) => {
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setCellDropdown(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [cellDropdown]);

    useEffect(() => {
        if (!publikaceOpen) return;
        const handler = (e) => {
            if (publikaceRef.current && publikaceRef.current.contains(e.target)) return;
            setPublikaceOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [publikaceOpen]);

    // ── Hledání, filtr a pohled nad směnami ──
    // Jedno pravidlo pro všechna tři zobrazení: mřížka i kalendář se pak dívají
    // na stejnou sadu a přepnutí zobrazení nemění, co je vidět.
    const sablonaSmeny = (s) => s.sablon || sablony.find(x => x.id === s.sablon_id) || null;
    const hledanyText = stripDiacritics((planHledani || '').trim());

    const projdeHledanim = (s) => {
        if (!hledanyText) return true;
        const sab = sablonaSmeny(s);
        return stripDiacritics([
            s.zamestnanec?.jmeno, s.zamestnanec?.prijmeni,
            sab?.kod, sab?.nazev, s.poznamka,
        ].filter(Boolean).join(' ')).includes(hledanyText);
    };

    // Skupiny se kombinují (A ZÁROVEŇ), volby uvnitř skupiny se sčítají (NEBO).
    const projdeFiltrem = (s) => {
        const f = planFiltry;
        const sab = sablonaSmeny(s);
        if (f.kariera.length && !f.kariera.includes(String(s.kariera_id ?? sab?.kariera_id ?? ''))) return false;
        if (f.sablona.length && !f.sablona.includes(String(s.sablon_id ?? ''))) return false;
        if (f.stav.length && !f.stav.includes(s.stav)) return false;
        if (f.pozaduje.length) {
            const ma = {
                vozidlo:  (s.pozaduje_vozidlo ?? sab?.pozaduje_vozidlo) === true,
                pokladna: (s.pozaduje_pokladnu ?? sab?.pozaduje_pokladnu) === true,
                manazer:  s.manazer_smeny === true,
            };
            if (!f.pozaduje.some(k => ma[k])) return false;
        }
        return true;
    };

    const mojeZamId = user?.zamestnanec_id;
    const projdePohledem = (s) => {
        if (pohledPlanu === 'moje') return s.zamestnanec_id === mojeZamId;
        if (pohledPlanu === 'otevrene') return !!s.otevrena;
        if (pohledPlanu === 'rozpracovane') return !!(s.draft || s.draft_smazano);
        return true;
    };

    const projde = (s) => projdeFiltrem(s) && projdeHledanim(s) && projdePohledem(s);

    // Počty do pilulek se čtou ze zobrazované sady — v mřížce z rozpracovaných
    // směn, v kalendáři z publikovaných, protože to jsou jiná data.
    // Kalendář plánovače na mobilu jede z rozpracovaného plánu (viz
    // `kalendarSRozpracovanymi`); směny čekající na smazání v něm nejsou —
    // vrátit je jde z pruhu „Smazáno" a do publikace je počítá lišta.
    const kalendarovaSada = kalendarSRozpracovanymi ? smeny.filter(s => !s.draft_smazano) : publishedSmeny;
    const zakladPlanu = pohled === 'mrizka' ? smeny : kalendarovaSada;
    const poctyPlanu = {
        vse: zakladPlanu.filter(s => projdeFiltrem(s) && projdeHledanim(s)).length,
        moje: zakladPlanu.filter(s => s.zamestnanec_id === mojeZamId && projdeFiltrem(s) && projdeHledanim(s)).length,
        otevrene: otevrene.filter(o => o.zbyva > 0 && projdeFiltrem(o) && projdeHledanim(o)).length,
        rozpracovane: smeny.filter(s => (s.draft || s.draft_smazano) && projdeFiltrem(s) && projdeHledanim(s)).length,
    };

    const skupinyFiltruPlanu = [
        { id: 'kariera', label: 'Kariéra', labelKratky: 'Kariéra', typ: 'volby',
          volby: kariery.map(k => ({ value: String(k.id), label: k.nazev })) },
        { id: 'sablona', label: 'Šablona', labelKratky: 'Šablona', typ: 'volby',
          volby: sablony.map(x => ({ value: String(x.id), label: `${x.kod} · ${x.nazev}` })) },
        { id: 'stav', label: 'Stav směny', labelKratky: 'Stav', typ: 'volby',
          volby: Object.entries(stavMeta).map(([k, v]) => ({ value: k, label: v.label })) },
        { id: 'pozaduje', label: 'Vyžaduje', labelKratky: 'Vyžaduje', typ: 'volby',
          volby: [
              { value: 'vozidlo', label: 'Auto' },
              { value: 'pokladna', label: 'Pokladnu' },
              { value: 'manazer', label: 'Manažera směny' },
          ] },
    ];

    // Sady pro jednotlivá zobrazení. Bloky níž si je pod stejnými jmény zastíní,
    // takže filtrování nevyžaduje sáhnout do osmi set řádků mřížky.
    // Nefiltrované sady pro věci, které se filtrem schovat nesmí (publikace).
    const smenyVse = smeny;
    const otevreneVse = otevrene;
    const smenyPlan = smeny.filter(projde);
    const publishedPlan = kalendarovaSada.filter(projde);
    const otevrenePlan = otevrene.filter(o => projdeFiltrem(o) && projdeHledanim(o)
        && (pohledPlanu === 'vse' || pohledPlanu === 'otevrene'));
    const zamestnanciPlan = hledanyText
        ? zamestnanci.filter(z => stripDiacritics(`${z.jmeno} ${z.prijmeni}`).includes(hledanyText))
        : zamestnanci;

    // Dny zobrazeného období. Mřížka po nich kreslí sloupce, panel z nich
    // počítá fond pracovní doby — dřív si je počítala jen mřížka uvnitř sebe.
    const obdobiDny = (() => {
        if (editorMode === 'tyden') return dnyTydne;
        const [rok, mes] = mesic.split('-').map(Number);
        const pocetDnu = new Date(rok, mes, 0).getDate();
        return Array.from({ length: pocetDnu }, (_, i) => {
            const d = new Date(rok, mes - 1, i + 1);
            const datum = toLocalDateStr(d);
            const dow = d.getDay();
            return { datum, den: dnyLabels[dow === 0 ? 7 : dow], cislo: i + 1, jeVikend: dow === 0 || dow === 6, jeDnes: datum === todayStr(), jeSvatek: jeSvatek(datum) };
        });
    })();

    // ── Panel „Období“ ──
    // Souhrn toho, na co se zrovna dívám. Norma (`ModulLayout.panel`) váže panel
    // na vybranou položku; tenhle patří celému období, ne směně, takže stojí
    // vedle zobrazení jako vlastní sloupec. Respektuje filtr a hledání —
    // zužím-li se na Rozvoz, panel mluví o Rozvozu.
    // Plán vyplňuje stránku, ať kreslí mřížku, kalendář nebo seznam: kalendář
    // se vejde celý a nepřetéká, seznam a pravý sloupec rolují uvnitř sebe.
    // Záložky, které vyplňují stránku a rolují uvnitř sebe. `editorFullHeight`
    // řídí vnitřek Plánu, `plnaVyska` vnější obaly — ty musí držet výšku i pro
    // Docházku, jinak nemá tabulka o co se opřít a roluje se celá stránka.
    // Na mobilu Plán vyplní <main> stejně jako na desktopu: lišta stojí, roluje
    // jen seznam dnů. Ne `position: fixed` jako Zprávy — fixní obal by založil
    // vlastní vrstvu a inspektor i modály (nejsou v portálu) by se schovaly
    // pod plovoucí lištu. Rezervu pod lištou tu drží výplň <main>.
    const mobilPlan = isMobile && jePlan;
    // Docházka na mobilu taky: hlavička stojí, roluje jen seznam záznamů.
    const plnaVyska = (!isMobile && ['plan', 'dochazka'].includes(tab)) || mobilPlan || (isMobile && tab === 'dochazka');
    const editorFullHeight = jePlan;

    const zobrazitPanel = jePlan && !isMobile && !isNarrow;
    // Detail má kam stát — jen když je pravý sloupec vůbec vykreslený.
    // Aktivní šablona, kterou nikdo nejezdí, se při generování tiše přeskočí —
    // nejčastější důvod, proč „Generovat“ nic neudělá. Počítá se tady nahoře,
    // protože to hlásí panel „Období“ a ten se skládá dřív než blok Šablon.
    const platnaPrirazeni = prirazeni.filter(p => !p.platnost_do);
    const sablonyBezJezdce = sablony.filter(x => x.aktivni !== false
        && !platnaPrirazeni.some(p => p.sablon_id === x.id));

    const panelObdobi = (() => {
        if (!zobrazitPanel) return null;

        const den = (x) => x.datum?.split?.('T')?.[0] || String(x.datum).split('T')[0];
        const vObdobi = (x) => obdobiDny.some(d => d.datum === den(x));
        // Stejny zdroj jako zobrazeni vedle: mrizka pracuje s rozpracovanym
        // planem, kalendar a seznam s publikovanym. Jinak by panel scital neco
        // jineho, nez je videt.
        const zdrojPanelu = pohled === 'mrizka' ? smenyPlan : publishedPlan;
        const sm = zdrojPanelu.filter(x => !x.otevrena && !x.draft_smazano && vObdobi(x));
        const otev = otevrenePlan.filter(o => !o.draft_smazano && o.zbyva > 0 && vObdobi(o));

        const hodinOtevrene = otev.reduce((a, o) => a + Number(o.hodin || 0), 0);
        const hodinCelkem = sm.reduce((a, x) => a + Number(x.hodin || 0), 0) + hodinOtevrene;
        const lidi = new Set(sm.map(x => x.zamestnanec_id).filter(Boolean)).size;
        const pracDnu = obdobiDny.filter(d => !d.jeVikend && !d.jeSvatek).length || 1;
        const fte = hodinCelkem / (pracDnu * 8);

        // Fixní platy se do období rozpouštějí poměrem dnů, stejně jako to
        // dělala patička mřížky — jinak by týden ukazoval celý měsíční plat.
        const obdobiOd = obdobiDny[0]?.datum;
        const obdobiDo = obdobiDny[obdobiDny.length - 1]?.datum;
        const fixMesicni = fixniPlaty.filter(fp =>
            obdobiDo && fp.platnost_od <= obdobiDo
            && (fp.platnost_do === null || fp.platnost_do >= obdobiOd)
        ).reduce((a, fp) => a + Number(fp.castka_kc || 0) + Number(fp.variabilni_bonus_kc || 0), 0);
        const { od: mOd, do: mDo } = getObdobi();
        const dnuVMesici = Math.round((new Date(mDo + 'T12:00:00') - new Date(mOd + 'T12:00:00')) / 86400000) + 1;
        const fixNaklad = dnuVMesici > 0 ? fixMesicni * (obdobiDny.length / dnuVMesici) : fixMesicni;
        const varNaklad = sm.reduce((a, x) => a + Number(x.naklad_celkem || 0), 0)
            + otev.reduce((a, o) => a + Number(o.naklad_celkem || 0), 0);
        const planNaklad = varNaklad + fixNaklad;

        // Rozpad variabilní části na to, co dostane člověk, a co stát: na směně
        // je `sazba_kc_h` hodinová sazba a `naklad_celkem` už včetně odvodů.
        // U fixního platu je sazba nula, takže do variabilní části nespádne.
        const cistaZe = (x) => Number(x.sazba_kc_h || 0) * Number(x.hodin || 0);
        const varCista = sm.reduce((a, x) => a + cistaZe(x), 0) + otev.reduce((a, o) => a + cistaZe(o), 0);
        const varOdvody = Math.max(varNaklad - varCista, 0);

        const dily = [
            { id: 'fix',    label: 'Fixní platy', castka: fixNaklad, barva: '#10B981' },
            { id: 'cista',  label: 'Hodinové mzdy', castka: varCista, barva: SECTION_COLOR },
            { id: 'odvody', label: 'Odvody',        castka: varOdvody, barva: '#3B82F6' },
        ].filter(d => d.castka > 0);
        const dilySoucet = dily.reduce((a, d) => a + d.castka, 0);

        // Koláč přes `conic-gradient` — tři díly nepotřebují SVG ani knihovnu.
        const kolac = dilySoucet > 0 && (() => {
            let beh = 0;
            const stopy = dily.map(d => {
                const od = (beh / dilySoucet) * 360;
                beh += d.castka;
                const doo = (beh / dilySoucet) * 360;
                return `${d.barva} ${od}deg ${doo}deg`;
            }).join(', ');
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 4px' }}>
                    <div style={{ width: 62, height: 62, borderRadius: '50%', flexShrink: 0,
                        background: `conic-gradient(${stopy})`,
                        WebkitMask: 'radial-gradient(circle, transparent 38%, #000 39%)',
                        mask: 'radial-gradient(circle, transparent 38%, #000 39%)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 }}>
                        {dily.map(d => (
                            <div key={d.id} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 11 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: d.barva, flexShrink: 0,
                                    alignSelf: 'center' }} />
                                <span style={{ color: mutedColor, flex: 1, minWidth: 0, whiteSpace: 'nowrap',
                                    overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.label}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                                    {Math.round((d.castka / dilySoucet) * 100)} %
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        })();

        // Skutečnost z docházky. Plán i skutečnost se berou z téže služby, aby
        // rozdíl porovnával srovnatelné — plán z rozvrhu výš počítá jinak.
        const denni = (laborCost?.denni || []).filter(d => obdobiDny.some(x => x.datum === d.datum));
        const skutNaklad = denni.reduce((a, d) => a + Number(d.odvody_skut || 0), 0);
        const planDochazka = denni.reduce((a, d) => a + Number(d.odvody_plan || 0), 0);
        const maSkutecnost = skutNaklad > 0;
        // Poměr místo druhé částky: „Plán“ z docházky je jiné číslo než plán
        // z rozvrhu výš (nepočítá neobsazené ani fixy) a vedle sebe by sváděly
        // k odečtání, které nic neříká.
        const plneni = planDochazka > 0 ? Math.round((skutNaklad / planDochazka) * 100) : null;

        // Dny, kde někdo jede, ale nikdo nemá korunu manažera směny.
        const dnySeSmenou = new Set(sm.map(den));
        const dnySManazerem = new Set(sm.filter(x => x.manazer_smeny).map(den));
        const bezManazera = [...dnySeSmenou].filter(d => !dnySManazerem.has(d)).sort();

        const nepublikovane = smenyVse.filter(x => (x.draft || x.draft_smazano) && vObdobi(x)).length;
        const keSmazani = smenyVse.filter(x => x.draft_smazano && vObdobi(x));
        const denCesky = (d) => new Date(d + 'T12:00:00').toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
        const dnySklonovane = (p) => p === 1 ? 'den' : (p <= 4 ? 'dny' : 'dní');

        const radek = (popis, hodnota, barva, titulek) => (
            <div title={titulek} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
                padding: '7px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}` }}>
                <span style={{ fontSize: 12, color: mutedColor }}>{popis}</span>
                <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: barva || 'var(--text-primary)' }}>{hodnota}</span>
            </div>
        );
        const nadpis = (text) => (
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                color: mutedColor, margin: '15px 0 3px' }}>{text}</div>
        );
        // `onClick` dělá z upozornění cestu k nápravě, ne jen hlášku.
        const upozorneni = (barva, titulek, detail, onClick) => (
            <div onClick={onClick} role={onClick ? 'button' : undefined}
                title={onClick ? 'Otevřít' : undefined}
                style={{ padding: '9px 10px', borderRadius: 9, marginTop: 8, background: `${barva}14`,
                    border: `1px solid ${barva}38`, fontSize: 11.5, lineHeight: 1.45, color: barva,
                    ...(onClick ? { cursor: 'pointer' } : null) }}>
                <b style={{ display: 'block', marginBottom: 1 }}>{titulek}</b>{detail}
            </div>
        );

        // ── Graf: hodiny po dnech ──
        // Čísla výš říkají, kolik toho v období je; graf říká, **kde** to je.
        // Neobsazené hodiny sedí navrchu sloupce, takže díra v pokrytí je vidět
        // dřív, než se člověk dostane k řádku „Neobsazeno“.
        const jeKc = grafJednotka === 'kc';
        const hodnota = (x) => (jeKc ? Number(x.naklad_celkem || 0) : Number(x.hodin || 0));
        const poDnech = obdobiDny.map(d => {
            const prirazene = sm.filter(x => den(x) === d.datum).reduce((a, x) => a + hodnota(x), 0);
            const neobsazene = otev.filter(o => den(o) === d.datum).reduce((a, o) => a + hodnota(o), 0);
            return { ...d, prirazene, neobsazene, celkem: prirazene + neobsazene };
        });
        const fmtGraf = jeKc ? fmtKc : fmtH;
        const maxDen = Math.max(1, ...poDnech.map(d => d.celkem));
        const jsouData = poDnech.some(d => d.celkem > 0);

        const graf = (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 104, marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, gap: 8 }}>
                    {nadpis(jeKc ? 'Náklady po dnech' : 'Hodiny po dnech')}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: 10, color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                            max {fmtGraf(maxDen)}
                        </span>
                        <div style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 6,
                            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)' }}>
                            {[{ id: 'hodiny', label: 'h' }, { id: 'kc', label: 'Kč' }].map(j => (
                                <button key={j.id} onClick={() => setGrafJednotka(j.id)}
                                    title={j.id === 'kc' ? 'Náklady po dnech' : 'Hodiny po dnech'}
                                    style={{ padding: '1px 7px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                        fontSize: 9.5, fontWeight: 700,
                                        background: grafJednotka === j.id ? `${SECTION_COLOR}25` : 'transparent',
                                        color: grafJednotka === j.id ? SECTION_COLOR : mutedColor }}>{j.label}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, flex: 1, minHeight: 56,
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                    paddingBottom: 1 }}>
                    {poDnech.map(d => {
                        const vyskaP = d.celkem > 0 ? (d.prirazene / maxDen) * 100 : 0;
                        const vyskaN = d.celkem > 0 ? (d.neobsazene / maxDen) * 100 : 0;
                        const titulek = `${new Date(d.datum + 'T12:00:00').toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'numeric' })}`
                            + ` — ${fmtGraf(d.celkem) || (jeKc ? '0 Kč' : '0 h')}`
                            + (d.neobsazene > 0 ? ` (z toho ${fmtGraf(d.neobsazene)} neobsazeno)` : '');
                        return (
                            <div key={d.datum} title={titulek}
                                style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column',
                                    justifyContent: 'flex-end', gap: 1,
                                    background: d.jeDnes ? `${SECTION_COLOR}1a`
                                        : (d.jeVikend || d.jeSvatek) ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)')
                                        : 'transparent' }}>
                                {vyskaN > 0 && <div style={{ height: `${vyskaN}%`, background: '#EF4444', borderRadius: '2px 2px 0 0', minHeight: 2 }} />}
                                {vyskaP > 0 && <div style={{ height: `${vyskaP}%`, background: SECTION_COLOR, minHeight: 2,
                                    borderRadius: vyskaN > 0 ? 0 : '2px 2px 0 0' }} />}
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 9,
                    color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                    <span>{Number(poDnech[0]?.datum.slice(8))}.</span>
                    {poDnech.some(d => d.jeDnes) && <span style={{ color: SECTION_COLOR, fontWeight: 700 }}>dnes</span>}
                    <span>{Number(poDnech[poDnech.length - 1]?.datum.slice(8))}.</span>
                </div>
                {otev.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 10, color: mutedColor }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: '#EF4444', flexShrink: 0 }} />
                        neobsazené {jeKc ? 'náklady' : 'hodiny'}
                    </div>
                )}
            </div>
        );

        return (
            <div style={{ ...glassCard, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column',
                ...(editorFullHeight ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null) }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                    color: mutedColor, marginBottom: 6 }}>Období</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{getObdobiLabel()}</div>

                {radek('Hodin', fmtH(hodinCelkem) || '–', null,
                    hodinOtevrene > 0 ? `z toho nepřiřazeno ${fmtH(hodinOtevrene)}` : undefined)}
                {radek('Lidí ve směnách', lidi || '–')}
                {radek('FTE', fte > 0 ? fte.toFixed(1) : '–', null, `${pracDnu} prac. ${dnySklonovane(pracDnu)} × 8 h`)}

                {nadpis('Mzdové náklady')}
                {kolac}
                {dily.map(d => radek(d.label, fmtKc(d.castka) || '–', d.barva,
                    d.id === 'fix' ? 'Měsíční fixy rozpočítané na dny období'
                    : d.id === 'odvody' ? 'Rozdíl mezi nákladem směny a hodinovou sazbou'
                    : 'Hodinová sazba × odpracované hodiny'))}
                {/* Odvody chybí, když u úrovní není vyplněný náklad na hodinu — to
                    není nula, to je nevyplněný číselník, ať se po tom nepátrá. */}
                {varOdvody === 0 && varCista > 0 && (
                    <div style={{ fontSize: 10, color: mutedColor, lineHeight: 1.45, margin: '2px 0 6px' }}>
                        Odvody se nepočítají — úrovně kariér nemají vyplněný náklad na hodinu.
                    </div>
                )}
                {radek('Plán celkem', fmtKc(planNaklad) || '–', SECTION_COLOR)}
                {radek('Ø na hodinu', hodinCelkem > 0 ? fmtKc(varNaklad / hodinCelkem) : '–', null,
                    'Variabilní náklad dělený hodinami')}

                {maSkutecnost && <>
                    {nadpis('Odpracováno')}
                    {radek('Skutečnost', fmtKc(skutNaklad) || '0 Kč', null, 'Z docházky, včetně odvodů')}
                    {plneni !== null && radek('Plnění plánu', `${plneni} %`,
                        plneni > 100 ? '#EF4444' : '#10B981',
                        `Proti plánu ze stejného výpočtu (${fmtKc(planDochazka) || '0 Kč'}) — plán z rozvrhu výš počítá i neobsazené směny a fixní platy`)}
                </>}

                {(otev.length > 0 || bezManazera.length > 0 || keSmazani.length > 0 || nepublikovane > 0
                    || sablonyBezJezdce.length > 0) && <>
                    {nadpis('K dořešení')}
                    {otev.length > 0 && upozorneni('#EF4444', `Neobsazeno · ${otev.length}`,
                        [...new Set(otev.map(den))].sort().slice(0, 4).map(denCesky).join(', ')
                        + (new Set(otev.map(den)).size > 4 ? ' …' : ''))}
                    {bezManazera.length > 0 && upozorneni('#FF8A00',
                        `Bez manažera · ${bezManazera.length} ${dnySklonovane(bezManazera.length)}`,
                        bezManazera.slice(0, 4).map(denCesky).join(', ') + (bezManazera.length > 4 ? ' …' : ''))}
                    {keSmazani.length > 0 && upozorneni('#FF8A00', `Ke smazání · ${keSmazani.length}`,
                        [...new Set(keSmazani.map(den))].sort().slice(0, 4).map(denCesky).join(', ')
                        + (new Set(keSmazani.map(den)).size > 4 ? ' …' : ''))}
                    {nepublikovane > 0 && upozorneni('#F59E0B', `Nepublikováno · ${nepublikovane}`,
                        'lidi tyhle změny zatím nevidí')}
                    {/* Nejčastější důvod, proč „Generovat“ nic neudělá. Klik otevře
                        katalog rovnou na těch šablonách, kterým chybí jezdec. */}
                    {sablonyBezJezdce.length > 0 && upozorneni('#3B82F6',
                        `Šablony bez jezdce · ${sablonyBezJezdce.length}`,
                        `${sablonyBezJezdce.slice(0, 3).map(x => x.kod).join(', ')}${sablonyBezJezdce.length > 3 ? '…' : ''} — generování je přeskočí`,
                        () => { setSablonyPohled('bez-jezdce'); setVybranaSablona(null); setSablonyPanel(true); })}
                </>}

                {jsouData && graf}

                <div style={{ marginTop: 12, paddingTop: 10, fontSize: 11, color: mutedColor, lineHeight: 1.5,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, flexShrink: 0 }}>
                    Rozpad mezd po lidech je v záložce <b style={{ color: 'var(--text-primary)' }}>Náklady</b>.
                </div>
            </div>
        );
    })();

    // Naplánované publikace patří k nastavení, ne k dennímu plánování —
    // proto sedí v hlavičce Šablon, ne nad mřížkou.
    const nastrojePublikaci = (
        <>
        {planovanePublikace.length > 0 && (() => {
            const cekajici = planovanePublikace.filter(p => p.stav === 'ceka');
            const historie = planovanePublikace.filter(p => p.stav !== 'ceka');
            const stavMap = {
                ceka:       { label: 'Čeká',        color: '#F59E0B' },
                provedeno:  { label: 'Publikováno', color: '#10B981' },
                selhalo:    { label: 'Selhalo',     color: '#EF4444' },
                zruseno:    { label: 'Stornováno',  color: mutedColor },
            };
            const renderRow = (p) => {
                const dt = new Date(p.publikovat_v);
                const dtLabel = dt.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                const meta = stavMap[p.stav] || { label: p.stav, color: mutedColor };
                return (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                                {String(p.od).split('T')[0]} – {String(p.do).split('T')[0]}
                            </span>
                            <span style={{ fontSize: 11, color: mutedColor }}>→ {dtLabel}</span>
                            {p.publikovano_smen != null && (
                                <span style={{ fontSize: 10, color: mutedColor }}>{p.publikovano_smen} směn</span>
                            )}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: meta.color, padding: '2px 8px', borderRadius: 7, background: `${meta.color}18`, flexShrink: 0 }}>
                            {meta.label}
                        </span>
                        {p.stav === 'ceka' && (
                            <button
                                onClick={async () => {
                                    if (!confirm('Stornovat naplánovanou publikaci?')) return;
                                    await fetch(`/api/shifts/planovane-publikace/${p.id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
                                    fetchPlanovanePublikace();
                                }}
                                title="Storno"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D85A30', padding: 4, display: 'flex', flexShrink: 0 }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                );
            };
            const hasCekajici = cekajici.length > 0;
            return (
                <div ref={publikaceRef} style={{ position: 'relative' }}>
                    <button onClick={() => setPublikaceOpen(v => !v)}
                        title="Naplánované publikace a historie"
                        style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${hasCekajici ? '#F59E0B40' : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`, cursor: 'pointer', fontSize: 12, fontWeight: 600, background: hasCekajici ? '#F59E0B12' : 'transparent', color: hasCekajici ? '#F59E0B' : mutedColor, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Clock size={13} />
                        <span>{hasCekajici ? cekajici.length : historie.length}</span>
                    </button>
                    {publikaceOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50, ...glassCard, borderRadius: 9, padding: 10, minWidth: 360, maxWidth: 480, maxHeight: 420, overflowY: 'auto', boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.5)' : '0 8px 28px rgba(0,0,0,0.15)' }}>
                            {hasCekajici && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <Clock size={13} style={{ color: '#F59E0B' }} />
                                        <span style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                            Čekající publikace · {cekajici.length}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {cekajici.map(renderRow)}
                                    </div>
                                </>
                            )}
                            {historie.length > 0 && (
                                <>
                                    <button
                                        onClick={() => setPlanovanePublikaceExpand(v => !v)}
                                        style={{
                                            marginTop: hasCekajici ? 8 : 0,
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            fontSize: 10, fontWeight: 500, color: mutedColor,
                                            textTransform: 'uppercase', letterSpacing: 0.3,
                                            display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0',
                                        }}
                                    >
                                        {planovanePublikaceExpand ? '▼' : '▶'} Historie · {historie.length}
                                    </button>
                                    {planovanePublikaceExpand && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                                            {historie.map(renderRow)}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
        })()}
        </>
    );

    // Nástroje mřížky v hlavičce: koš (cíl pro drag&drop), Publikovat
    // a naplánované publikace. Počítají z nefiltrovaných sad — rozpracované
    // směny existují bez ohledu na to, co je zrovna vidět.
    // Zapnutý filtr nesmí schovat Publikovat — rozpracované směny existují
    // bez ohledu na to, co je zrovna vidět. Proto nefiltrované `smeny`.
    const nepublikovanychVObdobi = upravujePlan
        ? smeny.filter(s => (s.draft || s.draft_smazano) && obdobiDny.some(d => d.datum === s.datum?.split('T')[0])).length
          + otevrene.filter(o => o.draft && obdobiDny.some(d => d.datum === (o.datum?.split?.('T')?.[0] || String(o.datum).split('T')[0]))).length
        : 0;
    const otevriPublikaci = () => {
        const { od, do: doo } = getObdobi();
        setPublikovatForm({ od, do: doo, mode: 'teď', naplanovat_na: '' });
        setPublikovatModal(true);
    };

    const nastrojeMrizky = pohled === 'mrizka' && upravujePlan ? (
        <>
            {/* Koš jako cíl přetažení zrušen — mazalo se tím jen myší a jen v mřížce,
                a bublina se seznamem reagovala jen na najetí. Smazat je teď akce
                u směny; co se při publikaci zruší, hlásí panel „Období“. */}
            {nepublikovanychVObdobi > 0 && (
                <button onClick={otevriPublikaci}
                    style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid #10B98140`, cursor: 'pointer', fontSize: 12, fontWeight: 600, background: '#10B98118', color: '#10B981', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle2 size={14} /> Publikovat
                </button>
            )}
        </>
    ) : null;

    // ── Ovládání období ──
    // Týden/měsíc a posun obdobím patří celému Plánu, ne jednomu zobrazení —
    // dřív měla každá ze tří obrazovek vlastní pruh a mřížka navíc jediná uměla
    // týden. Teď sedí v hlavičce vedle přepínače zobrazení a platí na všechna.
    // Období je uzavřené, když v něm nějaké směny jsou a všechny jsou dokončené.
    const obdobiUzavrene = (() => {
        const vObdobi = smeny.filter(x => !x.draft_smazano
            && obdobiDny.some(d => d.datum === (x.datum?.split?.('T')?.[0] || String(x.datum).split('T')[0])));
        return vObdobi.length > 0 && vObdobi.every(x => x.stav === 'dokoncena');
    })();

    const ovladaniObdobi = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2, background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
                borderRadius: 7, padding: 2, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                {['tyden', 'mesic'].map(m => (
                    <button key={m} onClick={() => setEditorMode(m)} style={{
                        padding: isMobile ? '4px 9px' : '4px 11px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 500,
                        background: editorMode === m ? `${SECTION_COLOR}20` : 'transparent',
                        color: editorMode === m ? SECTION_COLOR : mutedColor, transition: 'all 0.15s',
                    }}>{m === 'tyden' ? 'Týden' : 'Měsíc'}</button>
                ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => posun(-1)} title="Předchozí období" aria-label="Předchozí období"
                    style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, borderRadius: 6,
                        padding: '4px 6px', cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', background: 'transparent' }}>
                    <ChevronLeft size={14} />
                </button>
                {/* Na mobilu užší popisek, ať se ovládání vejde na jeden řádek. */}
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: isMobile ? 92 : 118,
                    textAlign: 'center', textTransform: 'capitalize', fontVariantNumeric: 'tabular-nums' }}>{getObdobiLabel()}</span>
                {/* Dřív měla „Uzavřena“ každá dlaždice zvlášť. Je to vlastnost období,
                    ne jednotlivé směny — takže se říká jednou, tady. */}
                {obdobiUzavrene && (
                    <span title="Všechny směny období jsou dokončené"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99,
                            background: 'rgba(16,185,129,.14)', color: '#10B981', fontSize: 10.5, fontWeight: 700,
                            cursor: 'help', flexShrink: 0 }}>
                        <CheckCircle2 size={10} /> Uzavřeno
                    </span>
                )}
                <button onClick={() => posun(1)} title="Další období" aria-label="Další období"
                    style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, borderRadius: 6,
                        padding: '4px 6px', cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', background: 'transparent' }}>
                    <ChevronRight size={14} />
                </button>
                <button onClick={jdiNaDnes} style={{ padding: isMobile ? '4px 8px' : '4px 10px', borderRadius: 6, border: `1px solid ${SECTION_COLOR}40`,
                    background: `${SECTION_COLOR}10`, color: SECTION_COLOR, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Dnes</button>
            </div>
        </div>
    );

    // ── Detail šablony ──
    // Dřív se šablona otevírala rovnou do modálu s dvanácti poli a „kdo ji jezdí“
    // bylo úplně jinde — tabulka přiřazení pod seznamem, kde se šablona hledala
    // podruhé. Teď je to jedno místo: co šablona je a kdo podle ní jezdí.
    // Jede v inspektoru, ne ve sloupci — jsou v tom pole (platnost přiřazení,
    // přidání jezdce) a formuláře do pomocného sloupce nepatří.
    const detailSablony = (sab, prirazeniSablony, { popisekSab, radekSouhrnu, dnyLabel, pridejPrirazeni }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: sab.barva || '#6B7280', flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{sab.nazev}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: mutedColor }}>{sab.kod}</div>
                </div>
            </div>

            <div>
                {radekSouhrnu('Čas', `${sab.cas_od?.slice(0, 5)}–${sab.cas_do?.slice(0, 5)}`)}
                {radekSouhrnu('Hodin', fmtH(sab.hodin) || '–')}
                {radekSouhrnu('Kariéra · seniorita',
                    `${sab.kariera?.nazev || '–'} · ${(sab.min_uroven_kod || '–').toLowerCase()}`)}
                {radekSouhrnu('Dny', dnyLabel(sab))}
                {radekSouhrnu('Cyklus', (sab.delka_cyklu || 1) > 1 ? `${sab.delka_cyklu} týdny` : 'každý týden')}
                {radekSouhrnu('Přestávky', (sab.pauzy || []).length ? `${sab.pauzy.length}×` : 'žádné')}
                {radekSouhrnu('Stav', sab.aktivni === false
                    ? <span style={{ color: mutedColor }}>neaktivní</span>
                    : <span style={{ color: '#10B981' }}>aktivní</span>)}
            </div>

            {(sab.manazer_smeny || sab.pozaduje_vozidlo || sab.pozaduje_pokladnu) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {sab.manazer_smeny && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 99, background: 'rgba(255,138,0,.14)', color: '#FF8A00' }}>
                            <Crown size={10} />Manažer směny
                        </span>
                    )}
                    {sab.pozaduje_vozidlo && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 99, background: 'rgba(59,130,246,.14)', color: '#3B82F6' }}>
                            <Truck size={10} />Auto
                        </span>
                    )}
                    {sab.pozaduje_pokladnu && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 99, background: 'rgba(16,185,129,.14)', color: '#10B981' }}>
                            <Banknote size={10} />Pokladna
                        </span>
                    )}
                </div>
            )}

            {/* Katalog otevře i ten, kdo Plán jen čte — editaci mu neukážeme. */}
            {upravujePlan && (
                <button onClick={() => { setSablonForm({ ...sab, cas_od: sab.cas_od?.slice(0, 5) ?? '', cas_do: sab.cas_do?.slice(0, 5) ?? '', pauzy: sab.pauzy || [], faze: initFaze(sab.faze) }); setSablonModal(sab.id); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', background: 'transparent', color: SECTION_COLOR,
                        border: `1px solid ${SECTION_COLOR}40` }}>
                    Upravit šablonu →
                </button>
            )}

            {/* „Kdo ji jezdí“ — dřív samostatná tabulka přiřazení pod seznamem. */}
            <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, paddingTop: 11 }}>
                <span style={{ ...popisekSab, marginBottom: 7 }}>Kdo ji jezdí · {prirazeniSablony.length}</span>

                {prirazeniSablony.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: mutedColor, lineHeight: 1.5, marginBottom: 9 }}>
                        Nikdo zatím. Bez přiřazení generátor podle téhle šablony nic nevytvoří.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
                        {prirazeniSablony.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px',
                                borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-raised)' }}>
                                <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-primary)',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {p.zamestnanec?.jmeno} {p.zamestnanec?.prijmeni}
                                </span>
                                {!upravujePlan && (
                                    <span style={{ fontSize: 11, color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                                        od {p.platnost_od ? new Date(String(p.platnost_od).slice(0, 10)).toLocaleDateString('cs-CZ') : '–'}
                                    </span>
                                )}
                                {upravujePlan && <input type="date"
                                    value={p.platnost_od ? String(p.platnost_od).slice(0, 10) : ''}
                                    onChange={async e => {
                                        await fetch(`/api/shifts/sablony/prirazeni/${p.id}`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                                            body: JSON.stringify({ platnost_od: e.target.value }),
                                        });
                                        fetchPrirazeni();
                                    }}
                                    title="Platnost od"
                                    style={{ ...iStyle, width: 116, padding: '3px 6px', fontSize: 11 }} />}
                                {upravujePlan && <button title="Ukončit přiřazení"
                                    onClick={async () => {
                                        if (!confirm('Odebrat přiřazení?')) return;
                                        await fetch(`/api/shifts/sablony/prirazeni/${p.id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
                                        fetchPrirazeni();
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', display: 'flex', padding: 2, flexShrink: 0 }}>
                                    <Trash2 size={13} />
                                </button>}
                            </div>
                        ))}
                    </div>
                )}

                {upravujePlan && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Dropdown
                        value={novePrirazeni.zamestnanec_id === '' ? '' : String(novePrirazeni.zamestnanec_id)}
                        onChange={(v) => setNovePrirazeni(p => ({ ...p, zamestnanec_id: v }))}
                        options={zamestnanci
                            .filter(z => z.stav !== 'neaktivni' && !prirazeniSablony.some(p => p.zamestnanec_id === z.id))
                            .map(z => ({ value: String(z.id), label: `${z.jmeno} ${z.prijmeni}`,
                                popis: (z.kariery || []).map(k => k.kod).join(', ') || undefined }))}
                        placeholder="Přidat zaměstnance…" isDark={isDark} accent={SECTION_COLOR} height={32}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                        <input type="date" value={novePrirazeni.platnost_od}
                            onChange={e => setNovePrirazeni(p => ({ ...p, platnost_od: e.target.value }))}
                            title="Platnost od"
                            style={{ ...iStyle, flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 11.5 }} />
                        <button onClick={pridejPrirazeni}
                            disabled={!novePrirazeni.zamestnanec_id || !novePrirazeni.platnost_od}
                            style={{ padding: '5px 13px', borderRadius: 7, border: 'none', fontSize: 11.5, fontWeight: 600,
                                cursor: novePrirazeni.zamestnanec_id ? 'pointer' : 'not-allowed',
                                background: SECTION_COLOR, color: '#14091f',
                                opacity: novePrirazeni.zamestnanec_id ? 1 : 0.45 }}>
                            Přidat
                        </button>
                    </div>
                </div>}
            </div>
        </div>
    );

    // ── Šablony ──
    // Nejsou vlastní stránka. Při plánování se šablona vybírá rovnou v buňce
    // mřížky — ta nabídne jen šablony pro danou kariéru, filtrované podle
    // seniority člověka a bez těch, co by se kryly s jeho existující směnou.
    // Žádná paleta tohle nepřekoná, takže katalog není nástroj plánování:
    // chodí se sem spravovat definice a přiřazení, a to je setup. Proto
    // inspektor nad Plánem místo čtvrté záložky.
    const hledanySab = stripDiacritics(sablonyHledani);
    const hledaneSab = sablony.filter(sab => !hledanySab || stripDiacritics(
        `${sab.kod || ''} ${sab.nazev || ''} ${sab.kariera?.nazev || ''}`).includes(hledanySab));
    const poctySab = {
        aktivni: hledaneSab.filter(x => x.aktivni !== false).length,
        bezJezdce: hledaneSab.filter(x => sablonyBezJezdce.some(s => s.id === x.id)).length,
        neaktivni: hledaneSab.filter(x => x.aktivni === false).length,
        vse: hledaneSab.length,
    };
    const seznamSab = (sablonyPohled === 'vse' ? hledaneSab
        : sablonyPohled === 'bez-jezdce' ? hledaneSab.filter(x => sablonyBezJezdce.some(s => s.id === x.id))
        : sablonyPohled === 'neaktivni' ? hledaneSab.filter(x => x.aktivni === false)
        : hledaneSab.filter(x => x.aktivni !== false))
        .slice()
        .sort((a, b) => String(a.kod || '').localeCompare(String(b.kod || ''), 'cs'));

    const vybranaSab = vybranaSablona ? sablony.find(x => x.id === vybranaSablona) : null;
    // Platná přiřazení téhle šablony — „kdo ji jezdí“.
    const prirazeniSablony = vybranaSab ? platnaPrirazeni.filter(p => p.sablon_id === vybranaSab.id) : [];

    const dnyZkratky = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
    const dnyLabel = (sab) => {
        const dny = sab.dny_v_tydnu || sab.dny || [];
        if (!Array.isArray(dny) || dny.length === 0) return '–';
        if (dny.length === 7) return 'každý den';
        return dny.map(d => dnyZkratky[(Number(d) - 1 + 7) % 7]).join(' ');
    };

    const popisekSab = { fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase',
        letterSpacing: 0.3, marginBottom: 3, display: 'block' };
    const radekSouhrnu = (popis, hodnota) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
            padding: '7px 0', fontSize: 12,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}` }}>
            <span style={{ color: mutedColor }}>{popis}</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right' }}>{hodnota}</span>
        </div>
    );

    // Referenční datum cyklu patří k „Generovat“ — od něj se počítají vícetýdenní
    // cykly, takže se na něj kouká právě ve chvíli generování a nikdy jindy.
    const refDatumCyklu = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: mutedColor }}
            title="Od tohohle data se počítají vícetýdenní cykly šablon">
            <CalendarDays size={13} />
            <span>Ref. datum cyklu</span>
            <input type="date" value={referencniDatum}
                onChange={async e => {
                    const val = e.target.value;
                    setReferencniDatum(val);
                    await fetch('/api/nastaveni/smeny_referencni_datum', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                        body: JSON.stringify({ hodnota: val }),
                    });
                }}
                style={{ ...iStyle, width: 132, padding: '3px 6px', fontSize: 11 }} />
        </div>
    );

    const pridejPrirazeni = async () => {
        if (!vybranaSab || !novePrirazeni.zamestnanec_id || !novePrirazeni.platnost_od) return;
        await fetch('/api/shifts/sablony/prirazeni', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({
                zamestnanec_id: Number(novePrirazeni.zamestnanec_id),
                sablon_id: vybranaSab.id,
                platnost_od: novePrirazeni.platnost_od,
            }),
        });
        setNovePrirazeni({ zamestnanec_id: '', platnost_od: todayStr() });
        fetchPrirazeni();
    };

    // Katalog v inspektoru: hledání, pohledy, řádky. Sloupec se statistikami tu
    // není — pravý sloupec patří Plánu, takže čísla stojí nad seznamem.
    const seznamSablonObsah = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <input type="text" value={sablonyHledani} onChange={e => setSablonyHledani(e.target.value)}
                placeholder="Hledat kód, název, kariéru…" style={{ ...iStyle, width: '100%' }} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                    ['aktivni', CheckCircle2, 'Aktivní', poctySab.aktivni],
                    ['bez-jezdce', UserX, 'Bez jezdce', poctySab.bezJezdce],
                    ['neaktivni', X, 'Neaktivní', poctySab.neaktivni],
                    ['vse', LayoutTemplate, 'Vše', poctySab.vse],
                ].map(([klic, Ikona, label, pocet]) => (
                    <PohledPill key={klic} Icon={Ikona} label={label} pocet={pocet}
                        aktivni={sablonyPohled === klic} onClick={() => setSablonyPohled(klic)}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni />
                ))}
            </div>

            {sablonyPohled === 'bez-jezdce' && poctySab.bezJezdce > 0 && (
                <div style={{ padding: '8px 10px', borderRadius: 9, background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.28)', fontSize: 11.5, lineHeight: 1.45, color: '#F59E0B' }}>
                    Tyhle šablony nikdo nejezdí, takže je <b>Generovat přeskočí</b>. Otevři šablonu
                    a přiřaď k ní člověka.
                </div>
            )}

            {seznamSab.length === 0 ? (
                <div style={{ padding: '28px 8px', textAlign: 'center' }}>
                    <LayoutTemplate size={30} style={{ color: mutedColor, margin: '0 auto 8px', opacity: 0.4 }} />
                    <p style={{ color: mutedColor, fontSize: 12.5, margin: 0 }}>
                        {sablony.length === 0 ? 'Žádné šablony' : 'Nic neodpovídá výběru.'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {seznamSab.map(sab => {
                        const jezdci = platnaPrirazeni.filter(p => p.sablon_id === sab.id).length;
                        return (
                            <button key={sab.id} type="button" onClick={() => setVybranaSablona(sab.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                                    padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
                                    background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-elevated)',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                    borderLeft: `3px solid ${sab.barva || '#6B7280'}`,
                                    opacity: sab.aktivni === false ? 0.55 : 1,
                                }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700,
                                    color: sab.barva || mutedColor, minWidth: 50, flexShrink: 0 }}>{sab.kod}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{sab.nazev}</span>
                                        {sab.manazer_smeny && <Crown size={11} strokeWidth={2.5} style={{ color: '#FF8A00' }} />}
                                        {sab.pozaduje_vozidlo && <Truck size={11} style={{ color: '#3B82F6' }} />}
                                        {sab.pozaduje_pokladnu && <Banknote size={11} style={{ color: '#10B981' }} />}
                                        {(sab.delka_cyklu || 1) > 1 && (
                                            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
                                                background: `${SECTION_COLOR}20`, color: SECTION_COLOR }}>{sab.delka_cyklu}T</span>
                                        )}
                                    </span>
                                    <span style={{ display: 'block', fontSize: 10.5, color: mutedColor, marginTop: 1,
                                        fontVariantNumeric: 'tabular-nums' }}>
                                        {sab.cas_od?.slice(0, 5)}–{sab.cas_do?.slice(0, 5)} · {fmtH(sab.hodin)} · {dnyLabel(sab)}
                                    </span>
                                </span>
                                <span title={jezdci ? `${jezdci} jezdí tuhle šablonu` : 'Nikdo ji nejezdí — generování ji přeskočí'}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                                        fontSize: 11, fontWeight: 600, color: jezdci ? 'var(--text-secondary)' : '#F59E0B' }}>
                                    <Users size={11} />{jezdci}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {upravujePlan && (
                <button type="button" onClick={() => { setSablonForm(emptySablon); setSablonModal(true); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
                        padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                        background: 'transparent', color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}40` }}>
                    <Plus size={14} /> Nová šablona
                </button>
            )}
        </div>
    );

    const inspektorSablon = (sablonyPanel || vybranaSab) ? (
        <SidePanel open
            onClose={() => { setSablonyPanel(false); setVybranaSablona(null); }}
            title={vybranaSab ? 'Detail šablony' : 'Šablony'}
            isDark={isDark} isMobile={isMobile} width={430} modal={false}
            accent={SECTION_COLOR} topOffset={inspektorTop}>
            <div style={{ padding: 16 }}>
                {vybranaSab ? (<>
                    {sablonyPanel && (
                        <button type="button" onClick={() => setVybranaSablona(null)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 10,
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                fontSize: 11.5, fontWeight: 600, color: mutedColor }}>
                            <ChevronLeft size={13} /> Všechny šablony
                        </button>
                    )}
                    {detailSablony(vybranaSab, prirazeniSablony, { popisekSab, radekSouhrnu, dnyLabel, pridejPrirazeni })}
                </>) : seznamSablonObsah}
            </div>
        </SidePanel>
    ) : null;

    const hlavickaPlanu = (
        <div style={{ marginBottom: 14 }}>
            <ListToolbar
                siroky={!isMobile} kompaktni={isMobile} sklo
                isDark={isDark} mutedColor={mutedColor} inputStyle={iStyle} barva={SECTION_COLOR}
                hledani={{
                    value: planHledani, onChange: setPlanHledani,
                    placeholder: 'Hledat jméno, kód šablony, poznámku…',
                    placeholderKratky: 'Hledat…',
                }}
                /* Mobil: jen hledání, filtr, pohledy a „+ Směna". Šablony, generování
                   a období patří k plánování, a to se na telefonu nedělá; měsíc
                   se tam posouvá dotažením seznamu za jeho začátek nebo konec. */
                akce={isMobile ? (upravujePlan ? [{
                    id: 'nova-smena', label: 'Směna', Icon: Plus, primarni: true,
                    title: 'Nová směna',
                    onClick: () => { setSmenaForm(emptySmena); setEditingSmena(null); setSmenaModal(true); setError(null); },
                }] : []) : [
                    {
                        id: 'sablony', label: 'Šablony', Icon: LayoutTemplate,
                        title: 'Katalog šablon a kdo je jezdí',
                        onClick: () => { setVybranaSablona(null); setSablonyPanel(true); },
                    },
                    ...(upravujePlan ? [
                    ...(pohled === 'mrizka' ? [{
                        id: 'generovat', label: 'Generovat', Icon: RefreshCw,
                        title: 'Vygenerovat směny ze šablon',
                        onClick: () => { setGenVysledek(null); setGenModal(true); },
                    }] : []),
                    {
                        id: 'nova-smena', label: 'Směna', Icon: Plus, primarni: true,
                        title: 'Nová směna',
                        onClick: () => { setSmenaForm(emptySmena); setEditingSmena(null); setSmenaModal(true); setError(null); },
                    },
                    ] : []),
                ]}
                nastroje={isMobile ? null : <>{nastrojeMrizky}{nastrojePublikaci}</>}
                filtr={(
                    <FiltrTlacitko skupiny={skupinyFiltruPlanu} hodnoty={planFiltry}
                        onChange={zmenFiltrPlanu} onReset={zrusFiltryPlanu}
                        pocetVysledku={pohled === 'mrizka' ? smenyPlan.length : publishedPlan.length}
                        siroky isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
                pohled={(
                    <>
                        <PohledPill Icon={Calendar} label="Vše" pocet={poctyPlanu.vse}
                            aktivni={pohledPlanu === 'vse'} onClick={() => setPohledPlanu('vse')}
                            title="Všechny směny zobrazeného období"
                            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        {mojeZamId && (
                            <PohledPill Icon={Users} label="Moje" pocet={poctyPlanu.moje}
                                aktivni={pohledPlanu === 'moje'} onClick={() => setPohledPlanu('moje')}
                                title="Jen směny, které mám já"
                                isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        )}
                        <PohledPill Icon={Unlock} label="Otevřené" pocet={poctyPlanu.otevrene}
                            aktivni={pohledPlanu === 'otevrene'} upozorneni={poctyPlanu.otevrene > 0}
                            onClick={() => setPohledPlanu('otevrene')}
                            title="Neobsazené směny, které si někdo může vzít"
                            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        {upravujePlan && (
                            <PohledPill Icon={Edit2} label="Rozpracované" pocet={poctyPlanu.rozpracovane}
                                aktivni={pohledPlanu === 'rozpracovane'} onClick={() => setPohledPlanu('rozpracovane')}
                                title="Nepublikované změny — lidi je zatím nevidí"
                                isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        )}
                    </>
                )}
                pohledVpravo={isMobile ? null : <>{ovladaniObdobi}<PrepinacPohledu /></>}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={skupinyFiltruPlanu} hodnoty={planFiltry}
                        onChange={zmenFiltrPlanu} onReset={zrusFiltryPlanu}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />
        </div>
    );

    const viewOptions = [
        { id: 'seznam', label: 'Seznam', Icon: List },
        { id: 'den',    label: 'Den',    Icon: CalendarDays },
        { id: 'tyden',  label: 'Týden',  Icon: Calendar },
        { id: 'mesic',  label: 'Měsíc',  Icon: LayoutGrid },
    ];

    // ── Day column for week view ──
    const DenSloupec = ({ den }) => {
        const denSmeny = smeny.filter(s => s.datum?.split('T')[0] === den.datum && !s.otevrena);
        const denOtevrene = otevrene.filter(o => o.datum?.split('T')[0] === den.datum && o.zbyva > 0);
        const isEmpty = denSmeny.length === 0 && denOtevrene.length === 0;
        return (
            <div style={{
                ...glassCard, borderRadius: 9, padding: 10, minHeight: 160,
                ...(den.jeDnes ? { borderColor: SECTION_COLOR + '60' } : {}),
                ...(den.jeVikend ? { opacity: 0.8 } : {}),
            }}>
                <div style={{ textAlign: 'center', marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}` }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: den.jeDnes ? SECTION_COLOR : mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>{den.den}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: den.jeDnes ? SECTION_COLOR : 'var(--text-primary)', lineHeight: 1.2 }}>{den.cislo}</div>
                </div>
                {isEmpty
                    ? <div style={{ fontSize: 11, color: mutedColor, textAlign: 'center', opacity: 0.5, marginTop: 12 }}>—</div>
                    : <>
                        {denSmeny.map(s => <SmenaChip key={s.id} s={s} isDark={isDark} size="normal" onEdit={handleEditSmenu} onDelete={handleSmazatSmenu} />)}
                        {denOtevrene.map(o => <SmenaChip key={o.id} s={{ ...o, otevrena: true, zamestnanec_id: null }} isOtevrena isDark={isDark} size="normal" onEdit={handleEditOtevrenu} onDelete={handleSmazatSmenu} />)}
                    </>
                }
            </div>
        );
    };

    // ── Month day cell ──
    const MesicDen = ({ den }) => {
        if (!den) return <div style={{ borderRadius: 7, minHeight: 80 }} />;
        const denSmeny = smeny.filter(s => s.datum?.split('T')[0] === den.datum && !s.otevrena);
        const denOtevrene = otevrene.filter(o => o.datum?.split('T')[0] === den.datum && o.zbyva > 0);
        const all = [...denSmeny, ...denOtevrene.map(o => ({ ...o, otevrena: true, zamestnanec_id: null }))];
        return (
            <div
                onClick={() => { setVybranyDen(den.datum); setZobrazeni('den'); }}
                style={{
                    ...glassCard, borderRadius: 7, padding: 6, minHeight: 80, cursor: 'pointer',
                    ...(den.jeDnes ? { borderColor: SECTION_COLOR + '60' } : {}),
                    transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: den.jeDnes ? SECTION_COLOR : den.jeVikend ? mutedColor : 'var(--text-primary)' }}>{den.cislo}</span>
                    {all.length > 0 && <span style={{ fontSize: 9, color: mutedColor, opacity: 0.6 }}>{all.length}×</span>}
                </div>
                {all.slice(0, 3).map((s, i) => <SmenaChip key={`${s.otevrena ? 'o' : 's'}-${s.id}`} s={s} isOtevrena={s.otevrena} isDark={isDark} size="compact" />)}
                {all.length > 3 && <div style={{ fontSize: 9, color: mutedColor, textAlign: 'center' }}>+{all.length - 3}</div>}
            </div>
        );
    };

    // ── Timeline helpers ──
    const HOUR_HEIGHT = 56;
    const parseTime = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h + m / 60; };

    const getTimelineRange = (datum, denSmeny, denOtevrene) => {
        const pd = getPracDobaProDen(datum);
        const pdOd = parseTime(pd.cas_od || '08:00');
        const pdDo = parseTime(pd.cas_do || '22:00');
        let minH = pdOd, maxH = pdDo;
        [...denSmeny, ...denOtevrene].forEach(s => {
            const od = parseTime(s.cas_od);
            const doo = parseTime(s.cas_do);
            if (od < minH) minH = od;
            if (doo > maxH) maxH = doo;
        });
        return { startH: Math.floor(Math.max(minH - 1, 0)), endH: Math.ceil(Math.min(maxH + 1, 24)), pdOd, pdDo };
    };

    // ── Seznam view ──
    const renderSeznam = () => {
        const allSmeny = [
            ...smeny.filter(s => !s.otevrena).map(s => ({ ...s, _typ: 'smena' })),
            ...otevrene.filter(o => o.zbyva > 0).map(o => ({ ...o, _typ: 'otevrena', otevrena: true, zamestnanec_id: null })),
        ].sort((a, b) => (a.datum || '').localeCompare(b.datum || '') || (a.cas_od || '').localeCompare(b.cas_od || ''));

        if (allSmeny.length === 0) {
            return (
                <div style={{ ...glassCard, borderRadius: 12, padding: 48, textAlign: 'center' }}>
                    <List size={40} style={{ color: mutedColor, margin: '0 auto 12px', opacity: 0.4 }} />
                    <p style={{ color: mutedColor, fontSize: 14 }}>Žádné směny v tomto období</p>
                </div>
            );
        }

        if (isMobile) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {allSmeny.map(s => {
                        const d = new Date((s.datum || '').split('T')[0] + 'T12:00:00');
                        const denLabel = dnyLabels[d.getDay() === 0 ? 7 : d.getDay()] || '';
                        return (
                            <div key={s.id + s._typ} onClick={() => s._typ === 'smena' ? handleEditSmenu(s) : handleEditOtevrenu(s)}
                                style={{ ...glassCard, borderRadius: 9, padding: '10px 14px', cursor: 'pointer', borderLeft: `3px solid ${s.sablon?.barva || '#6B7280'}`, position: 'relative' }}>
                                {s.zaskok && <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: s.sablon?.barva || '#6B7280', color: '#000', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} title="Záskok">Z</span>}
                                {s.manazer_smeny && <span style={{ position: 'absolute', top: -4, left: -4, width: 18, height: 18, borderRadius: '50%', background: '#FF8A00', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} title="Manažer směny"><Crown size={10} strokeWidth={2.5} /></span>}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {s.otevrena ? `🔓 ${s.obsazeno || 0}/${s.kapacita || '?'}` : `${s.zamestnanec?.jmeno || ''} ${s.zamestnanec?.prijmeni || ''}`}
                                    </span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: s.sablon?.barva || mutedColor }}>{s.sablon?.nazev}</span>
                                </div>
                                <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                                    {denLabel} {d.getDate()}.{d.getMonth() + 1}. · {(s.cas_od || '').slice(0, 5)} – {(s.cas_do || '').slice(0, 5)} · {s.hodin}h
                                    {s.stav && <span style={{ marginLeft: 8, color: stavMeta[s.stav]?.color || mutedColor }}>{stavMeta[s.stav]?.label}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return (
            <div style={{ ...glassCard, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` }}>
                                {['Datum', 'Den', 'Skupina', 'Zaměstnanec', 'Od', 'Do', 'Hod.', 'Stav'].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {allSmeny.map(s => {
                                const d = new Date((s.datum || '').split('T')[0] + 'T12:00:00');
                                const denLabel = dnyLabels[d.getDay() === 0 ? 7 : d.getDay()] || '';
                                const stav = stavMeta[s.stav] || {};
                                return (
                                    <tr key={s.id + s._typ}
                                        onClick={() => s._typ === 'smena' ? handleEditSmenu(s) : handleEditOtevrenu(s)}
                                        style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`, cursor: 'pointer' }}
                                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: 500 }}>{d.getDate()}.{d.getMonth() + 1}.</td>
                                        <td style={{ padding: '8px 12px', color: mutedColor }}>{denLabel}</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.sablon?.barva || '#6B7280' }} />
                                                <span style={{ color: 'var(--text-primary)' }}>{s.sablon?.nazev}</span>
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: 500 }}>
                                            {s.otevrena ? <span style={{ opacity: 0.6 }}>🔓 Otevřená ({s.obsazeno}/{s.kapacita})</span> : `${s.zamestnanec?.jmeno || ''} ${s.zamestnanec?.prijmeni || ''}`}
                                            {s.zaskok && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#F59E0B', color: '#000', fontSize: 9, fontWeight: 800, alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Záskok">Z</span>}
                                            {s.manazer_smeny && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#FF8A00', color: '#000', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Manažer směny"><Crown size={9} strokeWidth={2.5} /></span>}
                                            {smenaPozadujeAuto(s) && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#3B82F6', color: '#fff', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Vyžaduje auto"><Truck size={9} /></span>}
                                            {smenaPozadujePokladnu(s) && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#10B981', color: '#fff', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Vyžaduje pokladnu"><Banknote size={9} /></span>}
                                        </td>
                                        <td style={{ padding: '8px 12px', color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>{(s.cas_od || '').slice(0, 5)}</td>
                                        <td style={{ padding: '8px 12px', color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>{(s.cas_do || '').slice(0, 5)}</td>
                                        <td style={{ padding: '8px 12px', color: mutedColor }}>{s.hodin}h</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            {s.stav && <span style={{ fontSize: 11, fontWeight: 600, color: stav.color, background: stav.color + '18', padding: '2px 8px', borderRadius: 99 }}>{stav.label}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ── Timeline Day view ──
    const renderTimelineDay = () => {
        const denSmeny = smeny.filter(s => s.datum?.split('T')[0] === vybranyDen && !s.otevrena);
        const denOtevrene = otevrene.filter(o => o.datum?.split('T')[0] === vybranyDen && o.zbyva > 0);
        const activeKariery = kariery.filter(k => denSmeny.some(s => s.kariera_id === k.id) || denOtevrene.some(o => o.sablon?.kariera_id === k.id));

        if (activeKariery.length === 0 && denSmeny.length === 0 && denOtevrene.length === 0) {
            return (
                <div style={{ ...glassCard, borderRadius: 12, padding: 48, textAlign: 'center' }}>
                    <CalendarDays size={40} style={{ color: mutedColor, margin: '0 auto 12px', opacity: 0.4 }} />
                    <p style={{ color: mutedColor, fontSize: 14 }}>Žádné směny na tento den</p>
                </div>
            );
        }

        const { startH, endH, pdOd, pdDo } = getTimelineRange(vybranyDen, denSmeny, denOtevrene);
        const totalH = endH - startH;
        const hours = Array.from({ length: totalH + 1 }, (_, i) => startH + i);
        const today = new Date().toISOString().split('T')[0];
        const nowH = vybranyDen === today ? new Date().getHours() + new Date().getMinutes() / 60 : null;

        return (
            <div style={{ ...glassCard, borderRadius: 10, overflow: 'hidden' }}>
                {/* Header se skupinami */}
                <div style={{ display: 'grid', gridTemplateColumns: `50px repeat(${activeKariery.length || 1}, 1fr)`, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` }}>
                    <div style={{ padding: '8px 4px', fontSize: 10, color: mutedColor, textAlign: 'center' }}>Čas</div>
                    {activeKariery.map(g => (
                        <div key={g.id} style={{ padding: '8px 6px', fontSize: 11, fontWeight: 600, color: g.barva || mutedColor, textAlign: 'center', borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
                            {g.ikona || ''} {g.nazev}
                        </div>
                    ))}
                </div>

                {/* Timeline */}
                <div style={{ display: 'grid', gridTemplateColumns: `50px repeat(${activeKariery.length || 1}, 1fr)`, position: 'relative' }}>
                    {/* Hour labels */}
                    <div style={{ position: 'relative' }}>
                        {hours.map(h => (
                            <div key={h} style={{ height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 2, fontSize: 10, fontWeight: 500, color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                                {String(h).padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>

                    {/* Group columns */}
                    {activeKariery.map(g => {
                        const gSmeny = denSmeny.filter(s => s.kariera_id === g.id);
                        const gOtevrene = denOtevrene.filter(o => o.sablon?.kariera_id === g.id);
                        return (
                            <div key={g.id} style={{ position: 'relative', borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, minHeight: totalH * HOUR_HEIGHT }}>
                                {/* Hour grid lines */}
                                {hours.map(h => (
                                    <div key={h} style={{ position: 'absolute', top: (h - startH) * HOUR_HEIGHT, left: 0, right: 0, height: 1, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} />
                                ))}

                                {/* Working hours highlight */}
                                <div style={{
                                    position: 'absolute',
                                    top: Math.max(pdOd - startH, 0) * HOUR_HEIGHT,
                                    height: (Math.min(pdDo, endH) - Math.max(pdOd, startH)) * HOUR_HEIGHT,
                                    left: 0, right: 0,
                                    background: isDark ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.04)',
                                }} />

                                {/* Shift blocks */}
                                {[...gSmeny, ...gOtevrene.map(o => ({ ...o, otevrena: true }))].map(s => {
                                    const od = parseTime(s.cas_od);
                                    const doo = parseTime(s.cas_do);
                                    const top = (od - startH) * HOUR_HEIGHT;
                                    const height = Math.max((doo - od) * HOUR_HEIGHT, 24);
                                    return (
                                        <div key={`${s.otevrena ? 'o' : 's'}-${s.id}`} onClick={() => s.otevrena ? handleEditOtevrenu(s) : handleEditSmenu(s)}
                                            style={{
                                                position: 'absolute', top, left: 3, right: 3, height,
                                                background: (s.sablon?.barva || (g.barva || '#6B7280')) + (isDark ? 'CC' : '22'),
                                                border: `1px solid ${s.sablon?.barva || (g.barva || '#6B7280')}${s.otevrena ? '60' : 'AA'}`,
                                                borderLeft: `3px solid ${s.sablon?.barva || (g.barva || '#6B7280')}`,
                                                borderRadius: 5, padding: '3px 6px', cursor: 'pointer',
                                                overflow: 'hidden', fontSize: 11,
                                                borderStyle: s.otevrena ? 'dashed' : 'solid',
                                            }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {s.otevrena ? `🔓 ${s.obsazeno || 0}/${s.kapacita}` : `${s.zamestnanec?.jmeno || ''} ${s.zamestnanec?.prijmeni || ''}`}
                                            </div>
                                            {height > 32 && <div style={{ fontSize: 10, color: mutedColor, marginTop: 1 }}>{(s.cas_od || '').slice(0, 5)} – {(s.cas_do || '').slice(0, 5)}</div>}
                                        </div>
                                    );
                                })}

                                {/* Now line */}
                                {nowH !== null && nowH >= startH && nowH <= endH && (
                                    <div style={{ position: 'absolute', top: (nowH - startH) * HOUR_HEIGHT, left: 0, right: 0, height: 2, background: '#EF4444', zIndex: 5 }}>
                                        <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ── Timeline Week view ──
    const renderTimelineWeek = () => {
        // Najít globální range přes celý týden
        let globalStart = 24, globalEnd = 0;
        dnyTydne.forEach(den => {
            const ds = smeny.filter(s => s.datum?.split('T')[0] === den.datum && !s.otevrena);
            const doo = otevrene.filter(o => o.datum?.split('T')[0] === den.datum && o.zbyva > 0);
            const pd = getPracDobaProDen(den.datum);
            const pdOd = parseTime(pd.cas_od || '08:00');
            const pdDo = parseTime(pd.cas_do || '22:00');
            let minH = pdOd, maxH = pdDo;
            [...ds, ...doo].forEach(s => {
                const od = parseTime(s.cas_od); const doo2 = parseTime(s.cas_do);
                if (od < minH) minH = od;
                if (doo2 > maxH) maxH = doo2;
            });
            if (minH < globalStart) globalStart = minH;
            if (maxH > globalEnd) globalEnd = maxH;
        });
        globalStart = Math.floor(Math.max(globalStart - 1, 0));
        globalEnd = Math.ceil(Math.min(globalEnd + 1, 24));
        const totalH = globalEnd - globalStart;
        const hours = Array.from({ length: totalH + 1 }, (_, i) => globalStart + i);
        const today = new Date().toISOString().split('T')[0];
        const nowH = new Date().getHours() + new Date().getMinutes() / 60;

        const borderStyle = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
        const headerBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)';
        const stickyBg = isDark ? 'rgba(15,15,25,0.98)' : 'rgba(255,255,255,0.98)';

        return (
            <div style={{ ...glassCard, borderRadius: 10, overflow: 'hidden', display: 'flex' }}>
                {/* Sticky sloupec s časy */}
                <div style={{ width: 50, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: stickyBg }}>
                    {/* Header placeholder */}
                    <div style={{ height: 44, borderBottom: `1px solid ${headerBorder}`, borderRight: `1px solid ${borderStyle}` }} />
                    {/* Hour labels */}
                    {hours.map(h => (
                        <div key={h} style={{ height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 2, fontSize: 10, fontWeight: 500, color: mutedColor, fontVariantNumeric: 'tabular-nums', borderRight: `1px solid ${borderStyle}` }}>
                            {String(h).padStart(2, '0')}:00
                        </div>
                    ))}
                </div>

                {/* Scrollovatelná oblast se dny */}
                <div style={{ flex: 1, overflowX: isMobile ? 'auto' : 'visible' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, minmax(${isMobile ? '120px' : '0'}, 1fr))`, minWidth: isMobile ? 840 : undefined }}>
                        {/* Day headers */}
                        {dnyTydne.map(den => (
                            <div key={den.datum}
                                onClick={() => { setVybranyDen(den.datum); setZobrazeni('den'); }}
                                style={{
                                    height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                    borderBottom: `1px solid ${headerBorder}`,
                                    borderLeft: `1px solid ${borderStyle}`,
                                    background: den.jeDnes ? `${SECTION_COLOR}10` : 'transparent',
                                }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: den.jeDnes ? SECTION_COLOR : mutedColor, textTransform: 'uppercase' }}>{den.den}</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: den.jeDnes ? SECTION_COLOR : 'var(--text-primary)', lineHeight: 1.2 }}>{den.cislo}</div>
                            </div>
                        ))}

                        {/* Day timeline columns */}
                        {dnyTydne.map(den => {
                            const ds = smeny.filter(s => s.datum?.split('T')[0] === den.datum && !s.otevrena);
                            const doo = otevrene.filter(o => o.datum?.split('T')[0] === den.datum && o.zbyva > 0);
                            const pd = getPracDobaProDen(den.datum);
                            const pdOd = parseTime(pd.cas_od || '08:00');
                            const pdDo = parseTime(pd.cas_do || '22:00');

                            return (
                                <div key={den.datum} style={{
                                    position: 'relative', minHeight: totalH * HOUR_HEIGHT,
                                    borderLeft: `1px solid ${borderStyle}`,
                                    opacity: den.jeVikend ? 0.7 : 1,
                                }}>
                                    {hours.map(h => (
                                        <div key={h} style={{ position: 'absolute', top: (h - globalStart) * HOUR_HEIGHT, left: 0, right: 0, height: 1, background: borderStyle }} />
                                    ))}
                                    <div style={{
                                        position: 'absolute',
                                        top: Math.max(pdOd - globalStart, 0) * HOUR_HEIGHT,
                                        height: (Math.min(pdDo, globalEnd) - Math.max(pdOd, globalStart)) * HOUR_HEIGHT,
                                        left: 0, right: 0,
                                        background: 'rgba(245,158,11,0.04)',
                                    }} />
                                    {[...ds, ...doo.map(o => ({ ...o, otevrena: true }))].map(s => {
                                        const od = parseTime(s.cas_od);
                                        const doo2 = parseTime(s.cas_do);
                                        const top = (od - globalStart) * HOUR_HEIGHT;
                                        const height = Math.max((doo2 - od) * HOUR_HEIGHT, 20);
                                        return (
                                            <div key={`${s.otevrena ? 'o' : 's'}-${s.id}`} onClick={() => s.otevrena ? handleEditOtevrenu(s) : handleEditSmenu(s)}
                                                style={{
                                                    position: 'absolute', top, left: 2, right: 2, height,
                                                    background: (s.sablon?.barva || '#6B7280') + (isDark ? 'CC' : '22'),
                                                    border: `1px solid ${s.sablon?.barva || '#6B7280'}${s.otevrena ? '60' : 'AA'}`,
                                                    borderLeft: `3px solid ${s.sablon?.barva || '#6B7280'}`,
                                                    borderRadius: 4, padding: '2px 4px', cursor: 'pointer',
                                                    overflow: 'hidden', fontSize: 10,
                                                    borderStyle: s.otevrena ? 'dashed' : 'solid',
                                                }}>
                                                <div style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                                                    {s.otevrena ? '🔓' : (s.zamestnanec?.jmeno || '').split(' ')[0]}
                                                </div>
                                                {height > 28 && <div style={{ fontSize: 9, color: mutedColor }}>{(s.cas_od || '').slice(0, 5)}</div>}
                                            </div>
                                        );
                                    })}
                                    {den.jeDnes && nowH >= globalStart && nowH <= globalEnd && (
                                        <div style={{ position: 'absolute', top: (nowH - globalStart) * HOUR_HEIGHT, left: 0, right: 0, height: 2, background: '#EF4444', zIndex: 5 }}>
                                            <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // ── Den view (legacy grouped) ──
    const renderDen = () => {
        const denSmeny = smeny.filter(s => s.datum?.split('T')[0] === vybranyDen && !s.otevrena);
        const denOtevrene = otevrene.filter(o => o.datum?.split('T')[0] === vybranyDen && o.zbyva > 0);
        if (denSmeny.length === 0 && denOtevrene.length === 0) {
            return (
                <div style={{ ...glassCard, borderRadius: 12, padding: 48, textAlign: 'center' }}>
                    <CalendarDays size={40} style={{ color: mutedColor, margin: '0 auto 12px', opacity: 0.4 }} />
                    <p style={{ color: mutedColor, fontSize: 14 }}>Žádné směny na tento den</p>
                </div>
            );
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {kariery.map(g => {
                    const gSmeny = denSmeny.filter(s => s.kariera_id === g.id);
                    const gOtevrene = denOtevrene.filter(o => o.sablon?.kariera_id === g.id);
                    if (gSmeny.length === 0 && gOtevrene.length === 0) return null;
                    return (
                        <div key={g.id} style={{ ...glassCard, borderRadius: 10, padding: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: mutedColor, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{g.ikona || ''} {g.nazev} · {g.kariera?.nazev}</div>
                            {gSmeny.map(s => <SmenaChip key={s.id} s={s} isDark={isDark} size="full" onEdit={handleEditSmenu} onDelete={handleSmazatSmenu} />)}
                            {gOtevrene.map(o => <SmenaChip key={o.id} s={{ ...o, otevrena: true, zamestnanec_id: null }} isOtevrena isDark={isDark} size="full" onEdit={handleEditOtevrenu} onDelete={handleSmazatSmenu} />)}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── Detail směny ──
    // Jede v inspektoru (`Modal`), ne v pravém sloupci: formulář ve sloupci
    // 300 px nemá kam růst a pod 1200 px se sloupec nekreslí, takže klik na
    // směnu na mobilu nedělal nic. Kdo smí upravovat, dostane pole; kdo jen
    // čte, vidí týž obsah jako text. `uzky` zůstává pro případ, že by detail
    // někdo zase vecpal do úzkého místa — dnes se volá vždy s `false`.
    const detailSmenyObsah = (s, uzky) => (
        upravujePlan && !s.otevrena ? detailSmenyEditace(s, uzky) : detailSmenyCteno(s, uzky)
    );

    // Pole, která plánovač mění při rozvrhování měsíce. Co se mění jednou za čas
    // (kariéra, role, fáze, pauzy) zůstává v modálu za „Upravit vše“ — do sloupce
    // se to nevejde, aniž by se z něj stal schodůček.
    const detailSmenyEditace = (s, uzky) => {
        const f = detailForm || { zamestnanec_id: '', cas_od: '', cas_do: '', poznamka: '' };
        const sb = s.sablon || (s.sablon_id ? sablony.find(x => x.id === s.sablon_id) : null);
        const bv = sb?.barva || '#6B7280';
        const datumStr = s.datum?.split?.('T')?.[0] || String(s.datum).split('T')[0];
        const datumLabel = new Date(datumStr + 'T12:00:00')
            .toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'long' });
        const hodinZPoli = vypocetHodin(f.cas_od, f.cas_do, s.pauzy);
        const karieraId = s.kariera_id ?? sb?.kariera_id;

        const popisek = { fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase',
            letterSpacing: 0.3, marginBottom: 3, display: 'block' };
        const pole = { width: '100%', background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
            borderRadius: 7, padding: '7px 9px', fontSize: 12.5, color: 'var(--text-primary)', outline: 'none' };

        // Čas se ukládá vždy i s hodinami — backend si je z časů nepočítá.
        const ulozCasy = () => {
            const od = f.cas_od, doo = f.cas_do;
            if (!od || !doo) return;
            if (od === (s.cas_od || '').slice(0, 5) && doo === (s.cas_do || '').slice(0, 5)) return;
            ulozDetail({ cas_od: od, cas_do: doo, hodin: vypocetHodin(od, doo, s.pauzy) });
        };

        const prepinac = (klic, label, popis) => (
            <label key={klic} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '5px 0' }}>
                <input type="checkbox" checked={!!s[klic]} disabled={saving}
                    onChange={e => ulozDetail({ [klic]: e.target.checked })}
                    style={{ width: 14, height: 14, marginTop: 1, accentColor: SECTION_COLOR, cursor: 'pointer' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>{label}</span>
                    {popis && <span style={{ display: 'block', fontSize: 10, color: mutedColor, lineHeight: 1.35 }}>{popis}</span>}
                </span>
            </label>
        );

        const souhrn = (label, hodnota) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
                fontSize: 11.5, color: mutedColor, padding: '6px 0',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}` }}>
                <span>{label}</span>
                <b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{hodnota}</b>
            </div>
        );

        const akce = (Ikona, label, onClick, barva) => (
            <button onClick={onClick} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                    padding: '8px 10px', borderRadius: 8, fontSize: 12, cursor: saving ? 'default' : 'pointer',
                    background: barva ? `${barva}14` : (isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-raised)'),
                    border: `1px solid ${barva ? `${barva}38` : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`,
                    color: barva || 'var(--text-secondary)', opacity: saving ? 0.6 : 1 }}>
                <Ikona size={14} style={{ flexShrink: 0 }} />{label}
            </button>
        );

        const pocetFazi = (s.faze || []).filter(x => x?.id).length;
        const pocetPauz = (s.pauzy || []).length;

        // Smazaná směna se needituje — buď se vrátí zpátky do rozpisu, nebo ji
        // při nejbližší publikaci zrušíme nadobro. Jiná možnost není.
        if (s.draft_smazano) return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 11, height: 11, borderRadius: 3, background: bv, flexShrink: 0, opacity: 0.6 }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: mutedColor, textDecoration: 'line-through' }}>
                            {sb?.nazev || 'Směna'}
                        </span>
                    </div>
                    <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                        {sb?.kod ? `${sb.kod} · ` : ''}{datumLabel}
                    </div>
                </div>
                <div style={{ padding: '10px 11px', borderRadius: 9, background: 'rgba(239,68,68,0.09)',
                    border: '1px solid rgba(239,68,68,0.26)', fontSize: 11.5, lineHeight: 1.5, color: '#ff9a9a' }}>
                    <b style={{ display: 'block', marginBottom: 2 }}>Označeno ke smazání</b>
                    {s.draft === false
                        ? 'Lidi ji v plánu zatím pořád vidí. Zmizí jim až při publikaci — a dozvědí se o tom.'
                        : 'Nikdy nebyla publikovaná, takže ji nikdo neviděl. Při publikaci zmizí potichu.'}
                </div>
                {akce(RotateCcw, 'Vrátit do rozpisu', () => { handleObnovitSmenu(s.id); setDetailSmena(null); }, '#10B981')}
            </div>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 11, height: 11, borderRadius: 3, background: bv, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                            {sb?.nazev || 'Směna'}
                        </span>
                        {s.zaskok && <span title="Záskok" style={{ width: 17, height: 17, borderRadius: '50%', background: bv, color: '#000', fontSize: 9, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>Z</span>}
                    </div>
                    <div style={{ fontSize: 11, color: mutedColor, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                        {sb?.kod ? `${sb.kod} · ` : ''}{datumLabel}
                    </div>
                </div>

                <div>
                    <span style={popisek}>Kdo</span>
                    <Dropdown
                        value={f.zamestnanec_id === '' ? '' : String(f.zamestnanec_id)}
                        onChange={(v) => {
                            setDetailForm(p => ({ ...p, zamestnanec_id: v }));
                            if (v) ulozDetail({ zamestnanec_id: Number(v) });
                        }}
                        options={zamestnanci
                            .filter(z => z.stav !== 'neaktivni')
                            .map(z => ({ value: String(z.id), label: `${z.jmeno} ${z.prijmeni}`,
                                popis: (z.kariery || []).map(k => k.kod).join(', ') || undefined }))}
                        placeholder="Vybrat zaměstnance"
                        isDark={isDark} accent={SECTION_COLOR} height={34}
                    />
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={popisek}>Od</span>
                        <input type="time" value={f.cas_od} disabled={saving}
                            onChange={e => setDetailForm(p => ({ ...p, cas_od: e.target.value }))}
                            onBlur={ulozCasy} style={pole} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={popisek}>Do</span>
                        <input type="time" value={f.cas_do} disabled={saving}
                            onChange={e => setDetailForm(p => ({ ...p, cas_do: e.target.value }))}
                            onBlur={ulozCasy} style={pole} />
                    </div>
                    <div style={{ paddingBottom: 8, fontSize: 12.5, fontWeight: 700, color: SECTION_COLOR,
                        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {fmtH(hodinZPoli) || fmtH(s.hodin)}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {prepinac('manazer_smeny', 'Manažer směny', 'Potvrzuje výplaty záloh PINem, jeden na den')}
                    {prepinac('pozaduje_vozidlo', 'Vyžaduje auto')}
                    {prepinac('pozaduje_pokladnu', 'Vyžaduje pokladnu')}
                </div>

                <div>
                    <span style={popisek}>Poznámka</span>
                    <input type="text" value={f.poznamka} disabled={saving} placeholder="—"
                        onChange={e => setDetailForm(p => ({ ...p, poznamka: e.target.value }))}
                        onBlur={() => { if (f.poznamka !== (s.poznamka || '')) ulozDetail({ poznamka: f.poznamka }); }}
                        style={pole} />
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={popisek}>Kariéra</span>
                        <Dropdown
                            value={karieraId ? String(karieraId) : ''}
                            onChange={(v) => { if (v && Number(v) !== karieraId) ulozDetail({ kariera_id: Number(v) }); }}
                            options={kariery.map(k => ({ value: String(k.id), label: k.nazev }))}
                            placeholder="–" isDark={isDark} accent={SECTION_COLOR} height={32}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={popisek}>Min. seniorita</span>
                        <Dropdown
                            value={s.min_uroven_kod || sb?.min_uroven_kod || 'NOVICE'}
                            onChange={(v) => { if (v !== (s.min_uroven_kod || sb?.min_uroven_kod)) ulozDetail({ min_uroven_kod: v }); }}
                            options={['NOVICE', 'JUNIOR', 'MEDIOR', 'SENIOR', 'LEADER']
                                .map(u => ({ value: u, label: u.charAt(0) + u.slice(1).toLowerCase() }))}
                            isDark={isDark} accent={SECTION_COLOR} height={32}
                        />
                    </div>
                </div>

                {prepinac('zaskok', 'Záskok', 'Bere směnu bez požadované kvalifikace')}

                <div>
                    {souhrn('Fáze směny', pocetFazi || '–')}
                    {souhrn('Přestávky', pocetPauz ? `${pocetPauz}×` : 'žádné')}
                    {souhrn('Stav', stavMeta[s.stav]?.label || s.stav || '–')}
                    {/* U fixního platu částka mate — mřížka na stejném řádku píše
                        „fixní“ a dvě různá čísla na jedné obrazovce nikomu nepomohou. */}
                    {souhrn('Náklad', fixniPlaty.some(fp => fp.zamestnanec_id === s.zamestnanec_id)
                        ? <span style={{ color: '#10B981', fontStyle: 'italic', fontWeight: 500 }}>fixní plat</span>
                        : (fmtKc(s.naklad_celkem) || '–'))}
                </div>

                <button onClick={() => { setDetailSmena(null); handleEditSmenu(s); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', background: 'transparent', color: SECTION_COLOR,
                        border: `1px solid ${SECTION_COLOR}40`, marginTop: 'auto', flexShrink: 0 }}>
                    Role, pauzy a délky fází →
                </button>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    {akce(Plus, 'Duplikovat na jiný den', () => handleDuplikovat(s))}
                    {akce(Unlock, 'Uvolnit — sundat z člověka', () => handleUvolnit(s), '#F59E0B')}
                    {akce(Trash2, 'Smazat směnu', () => handleSmazatSmenu(s), '#EF4444')}
                </div>
            </div>
        );
    };

    const detailSmenyCteno = (s, uzky) => {
        const sb = s.sablon || (s.sablon_id ? sablony.find(x => x.id === s.sablon_id) : null);
        const bv = sb?.barva || '#6B7280';
        const datumD = s.datum ? new Date((s.datum?.split?.('T')?.[0] || String(s.datum).split('T')[0]) + 'T12:00:00') : null;
        const datumLabel = datumD ? datumD.toLocaleDateString('cs', uzky
            ? { weekday: 'short', day: 'numeric', month: 'numeric' }
            : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
        const myZamId = user?.zamestnanec_id;
        const startTs = new Date(`${(s.datum || '').split('T')[0]}T${(s.cas_od || '00:00').slice(0, 5)}`).getTime();
        const hodinDoStartu = (startTs - Date.now()) / 3600000;
        const lzeVratit = s.zamestnanec_id === myZamId && hodinDoStartu >= 48;
        const tlacitko = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            ...(uzky ? { width: '100%' } : null) };
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: uzky ? 12 : 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: bv, flexShrink: 0 }} />
                    <span style={{ fontSize: uzky ? 14 : 16, fontWeight: 700, color: 'var(--text-primary)' }}>{sb?.nazev || 'Směna'}</span>
                    {s.zaskok && <span style={{ width: 18, height: 18, borderRadius: '50%', background: bv, color: '#000', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Z</span>}
                    <ManazerKorunka s={s} size={14} />
                    <AutoIkona s={s} size={14} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                        ['Datum', datumLabel],
                        ['Kód', sb?.kod || '–'],
                        ['Čas', `${(s.cas_od || '').slice(0, 5)} – ${(s.cas_do || '').slice(0, 5)}`],
                        ['Hodin', fmtH(s.hodin)],
                        ['Kariéra', sb?.kariera?.nazev || s.kariera?.nazev || '–'],
                        ['Stav', stavMeta[s.stav]?.label || s.stav || '–'],
                    ].map(([label, val]) => (
                        <div key={label}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: uzky ? 13 : 14, fontWeight: 500, color: 'var(--text-primary)' }}>{val}</div>
                        </div>
                    ))}
                </div>
                {s.zamestnanec && (
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>Kdo</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{s.zamestnanec.jmeno} {s.zamestnanec.prijmeni}</div>
                    </div>
                )}
                {s.poznamka && (
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>Poznámka</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{s.poznamka}</div>
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    ...(uzky ? { flexDirection: 'column', alignItems: 'stretch' } : null) }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', ...(uzky ? { flexDirection: 'column' } : null) }}>
                        {s.zamestnanec_id === myZamId && hodinDoStartu >= 12 && (
                            <button
                                onClick={() => { setDetailSmena(null); setProhoditSmena(s); }}
                                style={{ ...tlacitko, background: '#6366F118', color: '#6366F1', border: '1px solid #6366F140' }}
                            >
                                <ArrowLeftRight size={14} />
                                Prohodit
                            </button>
                        )}
                        {lzeVratit ? (
                            <button
                                onClick={() => { setDetailSmena(null); handleVratit(s); }}
                                style={{ ...tlacitko, background: 'rgba(216,90,48,.12)', color: '#D85A30', border: '1px solid rgba(216,90,48,.3)' }}
                            >
                                <RotateCcw size={14} />
                                Nemůžu přijít
                            </button>
                        ) : s.zamestnanec_id === myZamId && hodinDoStartu > 0 ? (
                            <span style={{ fontSize: 11, color: mutedColor, fontStyle: 'italic' }}>
                                Do začátku &lt; 48 h — kontaktuj nadřízeného
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    // ResizeObserver, ne `window.resize` — výška se mění i při přepnutí
    // týden/měsíc nebo otevření panelu, kdy okno zůstává stejné.
    useEffect(() => {
        const el = kalendarMrizkaRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(([z]) => {
            const radku = Math.max(1, Number(el.dataset.radku) || 1);
            setRadekKalendareVyska(Math.round(z.contentRect.height / radku));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [jePlan, pohled, editorMode, mesic, tydenOd, isNarrow, isMobile]);

    // Vybraná směna → rozepsaná pole. Klíčem je id, ne celý objekt — po
    // uložení se `detailSmena` přepisuje a formulář by se při psaní resetoval.
    useEffect(() => {
        if (!detailSmena) { setDetailForm(null); return; }
        setDetailForm({
            zamestnanec_id: detailSmena.zamestnanec_id ?? '',
            cas_od: (detailSmena.cas_od || '').slice(0, 5),
            cas_do: (detailSmena.cas_do || '').slice(0, 5),
            poznamka: detailSmena.poznamka || '',
        });
    }, [detailSmena?.id]);

    // Nabídka vrácení zmizí sama po osmi vteřinách — dél než stačí člověk
    // zjistit, že sáhl vedle, a méně, než aby překážela.
    useEffect(() => {
        if (!vratitSmazani) return;
        const t = setTimeout(() => setVratitSmazani(null), 8000);
        return () => clearTimeout(t);
    }, [vratitSmazani]);


    return (
        <div style={plnaVyska ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}>
            {/* ── Header — na mobilu se zobrazuje v TopBaru, tady skryjeme ── */}
            {!isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexDirection: 'row', gap: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 8, background: `${SECTION_COLOR}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Calendar size={19} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.1 }}>Směny</h2>
                            <div style={{ fontSize: 12, color: mutedColor, marginTop: 2 }}>Plánování a přehled směn</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {/* ⚙ Nastavení směn (norma §1h) — role dřív ležely ve společném Nastavení */}
                        {muze(user, 'smeny.plan', 'upravuje') && (
                            <NastaveniTlacitko onClick={() => setNastaveniSmen(true)} barva={SECTION_COLOR} isDark={isDark} popis="Nastavení směn" />
                        )}
                        {/* Přidat směnu přesunuto do editoru */}
                        {/* Tlačítko nová otevřená odstraněno — vytváří se v editoru */}
                        {/* Tlačítka přesunuta dovnitř tabů */}
                    </div>
                </div>
            )}

            {/* Dark mode select/option fix */}
            {isDark && <style>{`
                select option { background: #1a1a2e; color: #e2e8f0; }
                select option:checked { background: #2a2a4e; }
            `}</style>}

            {/* Záložky z katalogu — na mobilu je přepíná střed lišty. */}
            <div style={{ marginBottom: isMobile ? 0 : 16 }}>
                <TabBar modul="smeny" user={user} aktivni={tab} onZmena={setTab}
                    barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>
            {nastaveniSmen && (
                <NastaveniModulu nadpis="Nastavení směn" barva={SECTION_COLOR} isDark={isDark}
                    onClose={() => { setNastaveniSmen(false); fetch('/api/role').then(r => r.json()).then(setVsechnyRole).catch(() => {}); }}
                    sekce={[{ id: 'role', nazev: 'Role', popis: 'barva a ikona v plánu, kariéra', obsah: () => <RoleSprava isDark={isDark} /> }]} />
            )}

            <div {...swipeProps} style={plnaVyska ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
            {/* ── Error banner ── */}
            {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: '#EF4444' }}>{error}</span>
                    </div>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 2, display: 'flex' }}><X size={15} /></button>
                </div>
            )}

            {/* ── EDITOR tab ── */}
            {jePlan && hlavickaPlanu}

            {jePlan && vratitSmazani && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12,
                    padding: '9px 12px', borderRadius: 9, background: 'rgba(239,68,68,0.09)',
                    border: '1px solid rgba(239,68,68,0.26)' }}>
                    <Trash2 size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>
                        Smazáno{vratitSmazani.popis ? ` — ${vratitSmazani.popis}` : ''}
                        <span style={{ color: mutedColor }}> · zruší se až při publikaci</span>
                    </span>
                    <button onClick={() => { handleObnovitSmenu(vratitSmazani.id); setVratitSmazani(null); }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                            fontSize: 12.5, fontWeight: 700, color: SECTION_COLOR,
                            borderBottom: `1px solid ${SECTION_COLOR}70` }}>
                        Vrátit
                    </button>
                    <button onClick={() => setVratitSmazani(null)} title="Zavřít" aria-label="Zavřít"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: mutedColor, display: 'flex', padding: 2 }}>
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Mobil nemá v liště Publikovat — úpravy z kalendáře jsou ale drafty,
                takže se tu nabídne, jakmile nějaká čeká. */}
            {mobilPlan && nepublikovanychVObdobi > 0 && (
                <button onClick={otevriPublikaci}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginBottom: 10, flexShrink: 0,
                        padding: '9px 12px', borderRadius: 9, border: '1px solid #10B98140', background: '#10B98114',
                        color: 'var(--text-primary)', fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}>
                    <CheckCircle2 size={15} style={{ color: '#10B981', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Nepublikované změny · {nepublikovanychVObdobi}</span>
                    <span style={{ fontWeight: 700, color: '#10B981' }}>Publikovat</span>
                </button>
            )}

            {/* Zobrazení a panel „Období“ stojí vedle sebe; na užším okně panel zmizí
                a čísla se vrátí do dlaždic pod mřížkou. */}
            {jePlan && (
            <div ref={obsahRef} style={{ display: 'flex', gap: 14, alignItems: 'stretch',
                ...(editorFullHeight ? { flex: 1, minHeight: 0 } : null) }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                ...(editorFullHeight ? { minHeight: 0 } : null) }}>

            {jePlan && pohled === 'mrizka' && (() => {
                // Hledání, filtr a pohled se propisují tím, že si blok zastíní
                // jména, se kterými počítá — mřížka níž pak pracuje s vybranou
                // sadou, aniž by o filtrování musela vědět.
                const smeny = smenyPlan;
                const otevrene = otevrenePlan;
                const zamestnanci = zamestnanciPlan;

                // Dny období počítá komponenta — potřebuje je i panel „Období".
                const editorDny = obdobiDny;

                // Zaměstnanci per kariéra (ne per scheduling group)
                const karieryMap = {};
                zamestnanci.filter(z => z.stav !== 'neaktivni').forEach(z => {
                    (z.kariery || []).forEach(k => {
                        if (!karieryMap[k.id]) karieryMap[k.id] = { id: k.id, kod: k.kod, nazev: k.nazev, lidi: [] };
                        const uroven = (z.urovne || []).find(u => u.kariera_id === k.id);
                        karieryMap[k.id].lidi.push({ ...z, _urovenKod: uroven?.uroven?.kod || '?', _urovenNazev: uroven?.uroven?.nazev || '' });
                    });
                });
                const zamPerKariera = Object.values(karieryMap).filter(k => k.lidi.length > 0);
                // Barva kariéry = barva první scheduling group s touto kariérou
                // Barvu vezmeme z první šablony dané kariéry
                zamPerKariera.forEach(k => { const sab = sablony.find(s => s.kariera_id === k.id && s.barva); k.barva = sab?.barva || '#6B7280'; });

                // Smazaná směna zůstává na svém místě přeškrtnutá, dokud ji publikace
                // nezruší — při rozvrhování je podstatnější, že tam byla, než uklizená
                // mřížka. Součty hodin a nákladů ji dál nepočítají.
                const getSmenyBunka = (zamId, datum, karieraId) => smeny.filter(s => s.zamestnanec_id === zamId && s.datum?.split('T')[0] === datum && !s.otevrena && (!karieraId || s.kariera_id == karieraId));
                const getOtevreneBunka = (datum) => otevrene.filter(o => o.datum?.split('T')[0] === datum);
                const jeNedostupny = (zamId, datum) => nedostupnosti.some(n => n.zamestnanec_id === zamId && n.od <= datum && n.do >= datum);
                const getNedostupnostTyp = (zamId, datum) => nedostupnosti.find(n => n.zamestnanec_id === zamId && n.od <= datum && n.do >= datum)?.typ;
                const hodinZam = (zamId, karieraId) => smeny.filter(s => s.zamestnanec_id === zamId && !s.otevrena && !s.draft_smazano && (!karieraId || s.kariera_id == karieraId) && editorDny.some(d => d.datum === s.datum?.split('T')[0])).reduce((sum, s) => sum + Number(s.hodin || 0), 0);
                const nakladZam = (zamId, karieraId) => smeny.filter(s => s.zamestnanec_id === zamId && !s.otevrena && !s.draft_smazano && (!karieraId || s.kariera_id == karieraId) && editorDny.some(d => d.datum === s.datum?.split('T')[0])).reduce((sum, s) => sum + Number(s.naklad_celkem || 0), 0);
                // fmtKc přesunuto na top-level
                const editorOd = editorDny[0]?.datum;
                const editorDo = editorDny[editorDny.length - 1]?.datum;
                // True pokud zaměstnanec má aktivní fixní plat překrývající editor období
                // (existence záznamu = je v režimu fixu, i když částka může být 0 Kč).
                const hasFixniPlat = (zamId, karieraId = null) => {
                    if (!editorOd || !editorDo) return false;
                    return fixniPlaty.some(fp =>
                        fp.zamestnanec_id === zamId
                        && (karieraId == null || fp.kariera_id === karieraId)
                        && fp.platnost_od <= editorDo
                        && (fp.platnost_do === null || fp.platnost_do >= editorOd)
                    );
                };

                const handleQuickAdd = (datum, zamestnanecId, karieraId) => {
                    setSmenaForm({ ...emptySmena, datum, zamestnanec_id: zamestnanecId, kariera_id: karieraId });
                    setEditingSmena(null); setSmenaModal(true); setError(null);
                    setCellDropdown(null);
                };

                const handleCellClick = (e, datum, zamId, groupId) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCellDropdown(prev => prev?.datum === datum && prev?.zamId === zamId ? null : { datum, zamId, groupId, rect });
                };

                const handleSelectSablon = async (sablon, datum, zamId, groupId) => {
                    setCellDropdown(null);
                    const body = { datum, kariera_id: sablon.kariera_id, sablon_id: sablon.id, zamestnanec_id: zamId, cas_od: sablon.cas_od?.slice(0,5), cas_do: sablon.cas_do?.slice(0,5), hodin: sablon.hodin, draft: true };
                    let res = await fetch('/api/shifts', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                    if (!res.ok) {
                        const d = await res.json();
                        if (d.error?.includes('kvalifikace') || d.error?.includes('Nedostatečná')) {
                            if (confirm(`${d.detail || d.error}\n\nChcete přesto přiřadit? (záskok)`)) {
                                body._force = true;
                                res = await fetch('/api/shifts', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                            } else return;
                        } else { setError(d.error || d.message || 'Chyba'); return; }
                    }
                    refreshTab();
                };

                // Drag & drop
                const handleDragStart = (e, smena) => {
                    setDragData({ smenaId: smena.id, fromDatum: smena.datum?.split('T')[0], zamId: smena.zamestnanec_id, groupId: smena.kariera_id });
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', smena.id);
                };

                const handleDragOver = (e, karieraId) => {
                    e.preventDefault();
                    // Povolit drop jen v rámci kariéry nebo do koše
                    if (karieraId && dragData && String(dragData.groupId) !== String(karieraId)) {
                        e.dataTransfer.dropEffect = 'none';
                        return;
                    }
                    e.dataTransfer.dropEffect = 'move';
                    e.currentTarget.dataset.origBg = e.currentTarget.dataset.origBg || e.currentTarget.style.background;
                    e.currentTarget.style.background = `${SECTION_COLOR}15`;
                };
                const handleDragLeave = (e) => { e.currentTarget.style.background = e.currentTarget.dataset.origBg || ''; };

                const handleDrop = async (e, datum, zamId, karieraId) => {
                    e.preventDefault(); e.currentTarget.style.background = e.currentTarget.dataset.origBg || '';
                    if (!dragData) return;
                    // Kontrola kariéry — drop jen v rámci stejné kariéry (zamId==='otevrene' je OK — stejná kariéra)
                    if (karieraId && String(dragData.groupId) !== String(karieraId)) { setDragData(null); return; }
                    const srcSmena = smeny.find(s => s.id === dragData.smenaId) || otevrene.find(o => o.id === dragData.smenaId);
                    if (!srcSmena) { setDragData(null); return; }
                    const srcIsOtevrena = srcSmena.otevrena || !srcSmena.zamestnanec_id;

                    if (zamId === 'otevrene') {
                        if (srcIsOtevrena) {
                            // Přesun otevřené na jiný den
                            await fetch(`/api/shifts/${dragData.smenaId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify({ datum }) });
                        } else {
                            // Přiřazená → otevřená (skutečný delete + nová otevřená) — zachovat manažera a poznámku
                            await fetch(`/api/shifts/${dragData.smenaId}`, { method: 'DELETE', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() } });
                            await fetch('/api/shifts/otevrena', {
                                method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
                                body: JSON.stringify({
                                    datum,
                                    kariera_id: srcSmena.kariera_id,
                                    sablon_id: srcSmena.sablon_id,
                                    cas_od: srcSmena.cas_od?.slice(0,5),
                                    cas_do: srcSmena.cas_do?.slice(0,5),
                                    hodin: srcSmena.hodin,
                                    kapacita: 1,
                                    poznamka: srcSmena.poznamka,
                                    manazer_smeny: !!srcSmena.manazer_smeny,
                                }),
                            });
                        }
                        setDragData(null);
                        refreshTab();
                        return;
                    }

                    if (srcIsOtevrena) {
                        // Otevřená → přiřadit zaměstnanci (skutečný delete + nová přiřazená)
                        await fetch(`/api/shifts/${dragData.smenaId}`, { method: 'DELETE', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() } });
                        const body = { datum, kariera_id: srcSmena.kariera_id, sablon_id: srcSmena.sablon_id, zamestnanec_id: zamId, cas_od: srcSmena.cas_od?.slice(0,5), cas_do: srcSmena.cas_do?.slice(0,5), hodin: srcSmena.hodin, min_uroven_kod: srcSmena.min_uroven_kod || srcSmena.sablon?.min_uroven_kod, draft: true };
                        const res = await fetch('/api/shifts', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                        if (!res.ok) {
                            const d = await res.json();
                            if (d.error?.includes('kvalifikace') || d.error?.includes('Nedostatečná')) {
                                if (confirm(`${d.detail || d.error}\n\nChcete přesto přiřadit? (záskok)`)) {
                                    body._force = true;
                                    await fetch('/api/shifts', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                                } else {
                                    // Vrátit otevřenou zpět
                                    await fetch('/api/shifts/otevrena', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify({ datum: srcSmena.datum?.split('T')[0] || datum, kariera_id: srcSmena.kariera_id, sablon_id: srcSmena.sablon_id, cas_od: srcSmena.cas_od?.slice(0,5), cas_do: srcSmena.cas_do?.slice(0,5), hodin: srcSmena.hodin, kapacita: 1 }) });
                                }
                            } else {
                                setError(d.error || d.message || 'Chyba při přiřazení');
                            }
                        }
                        setDragData(null);
                        refreshTab();
                        return;
                    }

                    // Přiřazená → jiný den/zaměstnanec
                    const body = { datum };
                    if (zamId !== dragData.zamId) body.zamestnanec_id = zamId;
                    const res = await fetch(`/api/shifts/${dragData.smenaId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                    if (!res.ok) {
                        const d = await res.json();
                        if (d.error?.includes('kvalifikace') || d.error?.includes('Nedostatečná')) {
                            if (confirm(`${d.detail || d.error}\n\nChcete přesto přesunout? (záskok)`)) {
                                body._force = true;
                                await fetch(`/api/shifts/${dragData.smenaId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                            }
                        } else {
                            setError(d.error || d.message || 'Chyba při přesunu');
                        }
                    }
                    setDragData(null);
                    refreshTab();
                };

                const urovenRank = { NOVICE: 1, JUNIOR: 2, MEDIOR: 3, SENIOR: 4, LEADER: 5 };

                // Šablony pro kariéru, volitelně filtrované dle úrovně zaměstnance
                const getSablonyProKarieru = (karieraId, zamId, datum) => {
                    let result = sablony.filter(s => s.kariera_id == karieraId && s.aktivni !== false);
                    // Filtr dle dne v týdnu
                    if (datum) {
                        const dow = new Date(datum + 'T12:00:00').getDay();
                        const denCislo = dow === 0 ? 7 : dow; // 1=Po ... 7=Ne
                        result = result.filter(s => !s.dny_v_tydnu || s.dny_v_tydnu.length === 0 || s.dny_v_tydnu.includes(denCislo));
                    }
                    // Filtr dle min. úrovně zaměstnance
                    if (zamId) {
                        const zam = zamestnanci.find(z => z.id === zamId);
                        const zamUroven = (zam?.urovne || []).find(u => u.kariera_id == karieraId);
                        const zamRank = urovenRank[zamUroven?.uroven?.kod] || 0;
                        result = result.filter(s => zamRank >= (urovenRank[s.min_uroven_kod] || 0));
                    }
                    return result;
                };

                const toggleGroup = (gId) => setCollapsedGroups(prev => { const n = new Set(prev); n.has(gId) ? n.delete(gId) : n.add(gId); return n; });

                const isCompact = editorMode === 'mesic';
                const colW = isCompact ? 'minmax(75px, 1fr)' : 'minmax(90px, 1fr)';
                const stickyBg = isDark ? 'rgba(15,15,25,0.99)' : 'rgba(255,255,255,0.99)';
                const borderC = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
                const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';

                return (
                    <div style={editorFullHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
                        {/* Ovládání období i nástroje mřížky jsou v hlavičce Plánu. */}
                        {/* Grid — table layout pro synchronizaci výšek */}
                        <div ref={editorScrollRef} onClick={() => setCellDropdown(null)} style={{ ...glassCard, borderRadius: 10, overflow: 'auto', ...(editorFullHeight ? { flex: 1, minHeight: 0 } : {}) }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: isCompact ? (editorDny.length * 78 + 250) : undefined, tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: isCompact ? 150 : 200 }} />
                                {editorDny.map(d => <col key={d.datum} style={{ width: isCompact ? 78 : undefined }} />)}
                                <col style={{ width: 85 }} />
                            </colgroup>
                            {/* Záhlaví s daty drží při rolování nahoře (`top: 0`) — bez toho
                                se při dvaceti zaměstnancích nedalo poznat, ke kterému dni sloupec
                                patří. Podklad musí být neprostupný, jinak jím prolézají řádky,
                                a spodní linku kreslí `box-shadow`: při `border-collapse` patří
                                rámeček tabulce a odroloval by pryč. */}
                            <thead>
                                <tr>
                                    <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 7, background: stickyBg, padding: '0 10px', textAlign: 'left', borderRight: `1px solid ${borderC}`, boxShadow: `inset 0 -1px 0 ${borderC}` }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>Zaměstnanec</span>
                                    </th>
                                    {editorDny.map(den => {
                                        const tonKrytu = den.jeDnes ? `${SECTION_COLOR}28`
                                            : (den.jeVikend || den.jeSvatek) ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')
                                            : null;
                                        return (
                                        <th key={den.datum} data-today={den.jeDnes ? '1' : undefined} style={{
                                            position: 'sticky', top: 0, zIndex: 5,
                                            padding: '6px 2px', textAlign: 'center',
                                            background: stickyBg,
                                            // Odstín dne leží přes neprostupný podklad, ne místo něj.
                                            ...(tonKrytu ? { backgroundImage: `linear-gradient(${tonKrytu}, ${tonKrytu})` } : {}),
                                            boxShadow: den.jeDnes
                                                ? `inset 1px 0 0 ${SECTION_COLOR}60, inset -1px 0 0 ${SECTION_COLOR}60, inset 0 -1px 0 ${borderC}, inset 0 2px 0 ${SECTION_COLOR}`
                                                : `inset 0 -1px 0 ${borderC}`,
                                        }}>
                                            <div style={{ fontSize: 9, fontWeight: 700, color: den.jeDnes ? SECTION_COLOR : mutedColor, textTransform: 'uppercase' }}>{den.jeDnes ? 'Dnes' : den.den}{den.jeSvatek ? ' 🔴' : ''}</div>
                                            <div style={{ fontSize: isCompact ? 11 : 14, fontWeight: 700, color: den.jeDnes ? SECTION_COLOR : (den.jeSvatek || den.jeVikend) ? mutedColor : 'var(--text-primary)', lineHeight: 1 }}>{den.cislo}</div>
                                        </th>
                                        );
                                    })}
                                    <th style={{ position: 'sticky', right: 0, top: 0, zIndex: 6, background: stickyBg, textAlign: 'center', borderLeft: `1px solid ${borderC}`, boxShadow: `inset 0 -1px 0 ${borderC}`, minWidth: 80, padding: '4px 6px' }}>
                                        <div style={{ fontSize: 9, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3 }}>Σ hod / Kč</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                            {zamPerKariera.map(g => {
                                const getKarOtevProDen = (datum) => otevrene.filter(o => !o.draft_smazano && o.zbyva > 0 && (o.sablon?.kariera_id == g.id || o.kariera_id == g.id) && (o.datum?.split?.('T')?.[0] || String(o.datum).split('T')[0]) === datum);
                                const edOtevreneKar = editorDny.flatMap(d => getKarOtevProDen(d.datum));
                                const karOtevH = edOtevreneKar.reduce((s, o) => s + Number(o.hodin || 0), 0);
                                const karPrirazeneH = smeny.filter(s => s.kariera_id == g.id && !s.otevrena && !s.draft_smazano && editorDny.some(d => d.datum === s.datum?.split('T')[0])).reduce((sum, s) => sum + Number(s.hodin || 0), 0);
                                const totalGroupH = karPrirazeneH + karOtevH;
                                return (
                                <React.Fragment key={`tg-${g.id}`}>
                                    {/* Kariéra header row */}
                                    <tr>
                                        <td onClick={() => toggleGroup(g.id)} style={{ position: 'sticky', left: 0, zIndex: 4, background: stickyBg, padding: '0 10px', cursor: 'pointer', borderBottom: `1px solid ${borderC}`, borderRight: `1px solid ${borderC}` }}>
                                            <div style={{ minHeight: isCompact ? 28 : 34, display: 'flex', alignItems: 'center', background: `${SECTION_COLOR}08`, borderRadius: 3, padding: '0 6px', margin: '0 -6px' }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: SECTION_COLOR }}>{collapsedGroups.has(g.id) ? '▸' : '▾'} {g.nazev}</span>
                                                <span style={{ fontSize: 10, color: mutedColor, marginLeft: 6 }}>({g.lidi.length})</span>
                                            </div>
                                        </td>
                                        {editorDny.map(den => {
                                            const denPrirazene = smeny.filter(s => s.kariera_id == g.id && s.datum?.split('T')[0] === den.datum && !s.otevrena).reduce((sum, s) => sum + Number(s.hodin || 0), 0);
                                            const denOtevrene = getKarOtevProDen(den.datum).reduce((sum, o) => sum + Number(o.hodin || 0), 0);
                                            const denH = denPrirazene + denOtevrene;
                                            return <td key={den.datum} style={{
                                                textAlign: 'center', borderBottom: `1px solid ${borderC}`,
                                                background: den.jeDnes ? `${SECTION_COLOR}18` : `${SECTION_COLOR}08`,
                                                ...(den.jeDnes ? { boxShadow: `inset 1px 0 0 ${SECTION_COLOR}60, inset -1px 0 0 ${SECTION_COLOR}60` } : {}),
                                            }}>
                                                {denH > 0 && <span style={{ fontSize: 10, fontWeight: 500, color: SECTION_COLOR }}>{fmtH(denH)}</span>}
                                            </td>;
                                        })}
                                        {(() => {
                                            const totalNaklad = smeny.filter(s => s.kariera_id == g.id && !s.otevrena && editorDny.some(d => d.datum === s.datum?.split('T')[0])).reduce((sum, s) => sum + Number(s.naklad_celkem || 0), 0);
                                            return (
                                                <td style={{ position: 'sticky', right: 0, zIndex: 3, background: stickyBg, textAlign: 'right', borderBottom: `1px solid ${borderC}`, borderLeft: `1px solid ${borderC}`, padding: '2px 8px' }}>
                                                    {totalGroupH > 0 && <div style={{ fontSize: 10, fontWeight: 500, color: SECTION_COLOR }}>{fmtH(totalGroupH)}</div>}
                                                    {totalNaklad > 0 && <div style={{ fontSize: 9, color: mutedColor }}>{fmtKc(totalNaklad)}</div>}
                                                </td>
                                            );
                                        })()}
                                    </tr>

                                    {/* Nepřiřazeno row */}
                                    {!collapsedGroups.has(g.id) && (
                                        <tr>
                                            <td style={{ position: 'sticky', left: 0, zIndex: 4, background: stickyBg, padding: '0 10px', borderBottom: `1px solid ${borderC}`, borderRight: `1px solid ${borderC}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 3, padding: '4px 6px', margin: '0 -6px' }}>
                                                    <Unlock size={isCompact ? 10 : 12} style={{ color: mutedColor }} />
                                                    <span style={{ fontSize: isCompact ? 10 : 12, fontWeight: 500, color: mutedColor, fontStyle: 'italic' }}>Nepřiřazeno</span>
                                                </div>
                                            </td>
                                            {editorDny.map(den => {
                                                const denOtev = otevrene.filter(o => o.zbyva > 0 && (o.sablon?.kariera_id == g.id || o.kariera_id == g.id) && o.datum?.split('T')[0] === den.datum);
                                                const npBg = den.jeDnes ? `${SECTION_COLOR}10` : (den.jeVikend || den.jeSvatek) ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent';
                                                return (
                                                    <td key={den.datum}
                                                        onClick={(e) => handleCellClick(e, den.datum, 'otevrene', g.id)}
                                                        onDragOver={(e) => handleDragOver(e, g.id)} onDragLeave={handleDragLeave}
                                                        onDrop={(e) => handleDrop(e, den.datum, 'otevrene', g.id)}
                                                        style={{
                                                            padding: '4px 3px', borderBottom: `1px solid ${borderC}`, background: npBg, cursor: 'pointer', verticalAlign: 'top',
                                                            ...(den.jeDnes ? { boxShadow: `inset 1px 0 0 ${SECTION_COLOR}60, inset -1px 0 0 ${SECTION_COLOR}60` } : {}),
                                                        }}>
                                                        {denOtev.map(o => {
                                                            const sab = o.sablon || (o.sablon_id ? sablony.find(sb => sb.id === o.sablon_id) : null);
                                                            return (
                                                                <div key={o.id} draggable onDragStart={(e) => handleDragStart(e, { ...o, otevrena: true })}
                                                                    onClick={(e) => { e.stopPropagation(); handleEditOtevrenu(o); }}
                                                                    style={{ fontSize: isCompact ? 11 : 12, padding: '5px 7px', borderRadius: 5, background: (sab?.barva || '#6B7280') + '18', borderLeft: `3px dashed ${sab?.barva || '#6B7280'}`, cursor: 'grab', marginBottom: 2, textAlign: 'center', opacity: o.draft ? 0.75 : 1, lineHeight: 1.3 }}>
                                                                    {isCompact ? (<>
                                                                        {(sab?.kod || o.draft) && <div style={{ fontWeight: 700, color: mutedColor, lineHeight: 1.2 }}>{o.draft ? '★ ' : ''}{sab?.kod || ''}</div>}
                                                                        <div style={{ lineHeight: 1.2, fontSize: 10, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{(o.cas_od||'').slice(0,5)}–{(o.cas_do||'').slice(0,5)}</div>
                                                                    </>) : (<>
                                                                        {o.draft && <span style={{ color: SECTION_COLOR, marginRight: 2 }}>★</span>}
                                                                        {sab?.kod && <span style={{ color: mutedColor, fontWeight: 600, marginRight: 3 }}>{sab.kod}</span>}
                                                                        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{(o.cas_od||'').slice(0,5)}–{(o.cas_do||'').slice(0,5)}</span>
                                                                    </>)}
                                                                    {(() => {
                                                                        const akt = o.aktivity || [];
                                                                        const has = { ukol: akt.some(a => a.typ === 'ukol'), checklist: akt.some(a => a.typ === 'checklist'), pauza: akt.some(a => a.typ === 'pauza') };
                                                                        const auto = smenaPozadujeAuto(o);
                                                                        if (!o.manazer_smeny && !auto && !has.checklist && !has.ukol && !has.pauza) return null;
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 3, color: mutedColor }}>
                                                                                {o.manazer_smeny && <Crown size={11} style={{ color: '#FF8A00' }} aria-label="Manažer směny" />}
                                                                                <AutoIkona s={o} size={11} />
                                                                                {has.checklist && <ClipboardCheck size={11} style={{ color: '#10B981' }} aria-label="Checklist" />}
                                                                                {has.ukol && <ListChecks size={11} style={{ color: '#F59E0B' }} aria-label="Úkol" />}
                                                                                {has.pauza && <Coffee size={11} style={{ color: mutedColor }} aria-label="Pauza" />}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            );
                                                        })}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ position: 'sticky', right: 0, zIndex: 3, background: stickyBg, textAlign: 'right', borderBottom: `1px solid ${borderC}`, borderLeft: `1px solid ${borderC}`, padding: '2px 8px' }}>
                                                {karOtevH > 0 && <div style={{ fontSize: 10, fontWeight: 500, color: mutedColor, fontStyle: 'italic' }}>{fmtH(karOtevH)}</div>}
                                                {(() => { const n = edOtevreneKar.reduce((s, o) => s + Number(o.naklad_celkem || 0), 0); return n > 0 ? <div style={{ fontSize: 9, color: mutedColor, fontStyle: 'italic' }}>{fmtKc(n)}</div> : null; })()}
                                            </td>
                                        </tr>
                                    )}

                                    {/* Employee rows */}
                                    {!collapsedGroups.has(g.id) && g.lidi.map(zam => (
                                        <tr key={zam.id}>
                                            <td style={{ position: 'sticky', left: 0, zIndex: 4, background: stickyBg, padding: '0 10px', borderBottom: `1px solid ${borderC}`, borderRight: `1px solid ${borderC}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                                                    {(zam.avatar_url_resolved || zam.avatar_url) ? (
                                                        <img src={zam.avatar_url_resolved || zam.avatar_url} alt="" style={{ width: isCompact ? 20 : 24, height: isCompact ? 20 : 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                                    ) : (
                                                        <div style={{ width: isCompact ? 20 : 24, height: isCompact ? 20 : 24, borderRadius: '50%', background: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: isCompact ? 8 : 9, fontWeight: 700, flexShrink: 0 }}>
                                                            {(zam.jmeno || '?')[0]}{(zam.prijmeni || '?')[0]}
                                                        </div>
                                                    )}
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: isCompact ? 10 : 12, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{zam.jmeno} {zam.prijmeni}</div>
                                                        <div style={{ fontSize: 9, color: mutedColor, marginTop: -1, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <span>{zam._urovenKod}</span>
                                                            {hasFixniPlat(zam.id, g.id) && <span title="Fixní plat" style={{ fontSize: 8, fontWeight: 700, color: '#10B981', background: 'rgba(16,185,129,0.12)', padding: '1px 5px', borderRadius: 3, letterSpacing: 0.4 }}>FIX</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {editorDny.map(den => {
                                                const bunkaSmeny = getSmenyBunka(zam.id, den.datum, g.id);
                                                const nedost = jeNedostupny(zam.id, den.datum);
                                                const nedostTyp = nedost ? getNedostupnostTyp(zam.id, den.datum) : null;
                                                const nedostBg = nedost ? ({
                                                    dovolena: isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.08)',
                                                    nemoc:    isDark ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.08)',
                                                    osobni:   isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.08)',
                                                    jine:     isDark ? 'rgba(107,114,128,0.10)' : 'rgba(107,114,128,0.08)',
                                                }[nedostTyp] || (isDark ? 'rgba(107,114,128,0.10)' : 'rgba(107,114,128,0.08)')) : null;
                                                const nedostStripe = nedost ? ({
                                                    dovolena: 'rgba(59,130,246,0.16)',
                                                    nemoc:    'rgba(239,68,68,0.16)',
                                                    osobni:   'rgba(245,158,11,0.16)',
                                                    jine:     'rgba(107,114,128,0.16)',
                                                }[nedostTyp] || 'rgba(107,114,128,0.16)') : null;
                                                const wkBg = nedostBg || (den.jeDnes ? `${SECTION_COLOR}10` : (den.jeVikend || den.jeSvatek) ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent');
                                                const nedostTitle = nedost ? ({'dovolena':'Dovolená','nemoc':'Nemoc','osobni':'Osobní volno','jine':'Nedostupný'}[nedostTyp] || 'Nedostupný') : undefined;
                                                return (
                                                    <td key={den.datum}
                                                        onClick={(e) => handleCellClick(e, den.datum, zam.id, g.id)}
                                                        onDragOver={(e) => handleDragOver(e, g.id)} onDragLeave={handleDragLeave}
                                                        onDrop={(e) => handleDrop(e, den.datum, zam.id, g.id)}
                                                        title={nedostTitle}
                                                        style={{
                                                            padding: '4px 3px', borderBottom: `1px solid ${borderC}`, background: wkBg,
                                                            cursor: 'pointer', verticalAlign: 'top',
                                                            ...(nedost ? { backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 6px, ${nedostStripe} 6px, ${nedostStripe} 11px)` } : {}),
                                                            ...(den.jeDnes ? { boxShadow: `inset 1px 0 0 ${SECTION_COLOR}60, inset -1px 0 0 ${SECTION_COLOR}60` } : {}),
                                                        }}>
                                                        {bunkaSmeny.map(s => {
                                                            const stav = stavMeta[s.stav] || {};
                                                            const sab = s.sablon_id ? sablony.find(sb => sb.id === s.sablon_id) : null;
                                                            return (
                                                                <div key={s.id} draggable={!ctrlDrzi && !s.draft_smazano} onDragStart={(e) => !ctrlDrzi && !s.draft_smazano && handleDragStart(e, s)}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (vybiraKlik(e)) { if (!s.draft_smazano) toggleShiftSelection(s.id); return; }
                                                                        if (selectedShiftIds.length) setSelectedShiftIds([]);
                                                                        setDetailSmena(s);
                                                                    }}
                                                                    title={`${s.sablon?.nazev || ''} · ${s.hodin}h${s.poznamka ? ` · ${s.poznamka}` : ''}${(s.aktivity?.length ? ` · ${s.aktivity.length} aktivit` : '')}`}
                                                                    style={{ fontSize: isCompact ? 11 : 12, padding: '5px 7px', borderRadius: 5,
                                                                        background: s.draft_smazano ? 'rgba(239,68,68,0.08)' : selectedShiftIds.includes(s.id) ? `${SECTION_COLOR}35` : (sab?.barva || '#6B7280') + '18',
                                                                        borderLeft: `3px ${s.draft_smazano ? 'dotted #EF4444' : `${s.draft ? 'dashed' : 'solid'} ${sab?.barva || '#6B7280'}`}`,
                                                                        outline: (selectedShiftIds.includes(s.id) || detailSmena?.id === s.id) ? `2px solid ${SECTION_COLOR}` : 'none',
                                                                        textDecoration: s.draft_smazano ? 'line-through' : 'none',
                                                                        cursor: ctrlDrzi ? 'copy' : (s.draft_smazano ? 'pointer' : 'grab'),
                                                                        marginBottom: 2, textAlign: 'center', opacity: s.draft_smazano ? 0.62 : (s.draft ? 0.75 : 1), position: 'relative', lineHeight: 1.3 }}>
                                                                    {isCompact ? (<>
                                                                        {(sab?.kod || s.draft) && <div style={{ fontWeight: 700, color: mutedColor, lineHeight: 1.2 }}>{s.draft ? '★ ' : ''}{sab?.kod || ''}</div>}
                                                                        <div style={{ lineHeight: 1.2, fontSize: 10, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{(s.cas_od||'').slice(0,5)}–{(s.cas_do||'').slice(0,5)}</div>
                                                                    </>) : (<>
                                                                        {s.draft && <span style={{ color: SECTION_COLOR, marginRight: 2 }}>★</span>}
                                                                        {sab?.kod && <span style={{ color: mutedColor, fontWeight: 600, marginRight: 3 }}>{sab.kod}</span>}
                                                                        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{(s.cas_od||'').slice(0,5)}–{(s.cas_do||'').slice(0,5)}</span>
                                                                    </>)}
                                                                    {(() => {
                                                                        const akt = s.aktivity || [];
                                                                        const has = { ukol: akt.some(a => a.typ === 'ukol'), checklist: akt.some(a => a.typ === 'checklist'), pauza: akt.some(a => a.typ === 'pauza') };
                                                                        const auto = smenaPozadujeAuto(s);
                                                                        if (!s.zaskok && !s.manazer_smeny && !auto && !has.checklist && !has.ukol && !has.pauza) return null;
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 3, color: mutedColor }}>
                                                                                <ManazerKorunka s={s} size={11} />
                                                                                <AutoIkona s={s} size={11} />
                                                                                {s.zaskok && <ArrowLeftRight size={11} style={{ color: '#F59E0B' }} aria-label="Záskok" />}
                                                                                {has.checklist && <ClipboardCheck size={11} style={{ color: '#10B981' }} aria-label="Checklist" />}
                                                                                {has.ukol && <ListChecks size={11} style={{ color: '#F59E0B' }} aria-label="Úkol" />}
                                                                                {has.pauza && <Coffee size={11} style={{ color: mutedColor }} aria-label="Pauza" />}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            );
                                                        })}
                                                    </td>
                                                );
                                            })}
                                            {(() => {
                                                const zamH = hodinZam(zam.id, g.id);
                                                const zamN = nakladZam(zam.id, g.id);
                                                const isFixni = hasFixniPlat(zam.id, g.id);
                                                return (
                                                    <td style={{ position: 'sticky', right: 0, zIndex: 3, background: stickyBg, textAlign: 'right', borderBottom: `1px solid ${borderC}`, borderLeft: `1px solid ${borderC}`, padding: '2px 8px' }}>
                                                        {zamH > 0 && <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-primary)' }}>{fmtH(zamH)}</div>}
                                                        {zamH > 0 && (isFixni
                                                            ? <div style={{ fontSize: 9, color: '#10B981', fontStyle: 'italic' }}>fixní</div>
                                                            : zamN > 0 && <div style={{ fontSize: 9, color: mutedColor }}>{fmtKc(zamN)}</div>
                                                        )}
                                                    </td>
                                                );
                                            })()}
                                        </tr>
                                    ))}
                                </React.Fragment>
                                );
                            })}
                            </tbody>
                            </table>
                        </div>

                        {/* Summary dlaždice — jen když není panel „Období“ (úzké okno). */}
                        {!zobrazitPanel && (() => {
                            const edSmeny = smeny.filter(s => !s.otevrena && !s.draft_smazano && editorDny.some(d => d.datum === s.datum?.split('T')[0]));
                            const totalH = edSmeny.reduce((s, x) => s + Number(x.hodin || 0), 0);
                            const fixniZamIds = [...new Set(edSmeny.filter(s => Number(s.sazba_kc_h) === 0).map(s => s.zamestnanec_id).filter(Boolean))];
                            // Stejná definice jako panel „Období“: kdo má v období směnu,
                            // ne kolik řádků má rozpis — jinak stejná obrazovka po změně
                            // šířky hlásí dvě různá čísla.
                            const hcIds = new Set(edSmeny.map(x => x.zamestnanec_id).filter(Boolean));
                            const hc = hcIds.size;
                            const pocetDnu = editorDny.filter(d => !d.jeVikend && !d.jeSvatek).length || 1;
                            const edOtevrene = otevrene.filter(o => !o.draft_smazano && o.zbyva > 0 && editorDny.some(d => d.datum === (o.datum?.split?.('T')?.[0] || String(o.datum).split('T')[0])));
                            const otevH = edOtevrene.reduce((s, o) => s + Number(o.hodin || 0), 0);
                            const otevNaklad = edOtevrene.reduce((s, o) => s + Number(o.naklad_celkem || 0), 0);
                            const celkemH = totalH + otevH;
                            const fte = celkemH / (pocetDnu * 8);

                            const tileStyle = { ...glassCard, borderRadius: 9, padding: '12px 16px' };
                            const labelSt = { fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 };
                            const valueSt = { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 };
                            const subSt = { fontSize: 10, color: mutedColor, marginTop: 3 };

                            return (
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 2fr', gap: 10, marginTop: 14 }}>
                                    <div style={tileStyle}>
                                        <div style={labelSt}>Celkem</div>
                                        <div style={valueSt}>{fmtH(celkemH) || '–'}</div>
                                        {otevH > 0 && <div style={subSt}>z toho nepřiřazeno {fmtH(otevH)}</div>}
                                    </div>
                                    <div style={tileStyle}>
                                        <div style={labelSt}>Lidí</div>
                                        <div style={valueSt}>{hc || '–'}</div>
                                        <div style={subSt}>{fixniZamIds.length} fixní / {hc - fixniZamIds.length} variab.</div>
                                    </div>
                                    <div style={tileStyle}>
                                        <div style={labelSt}>FTE</div>
                                        <div style={valueSt}>{fte > 0 ? fte.toFixed(1) : '–'}</div>
                                        <div style={subSt}>{pocetDnu} prac. dnů × 8h</div>
                                    </div>
                                    {(() => {
                                        // Suma aktivních fixních platů + variabilních bonusů překrývajících editor období
                                        const fixMesicni = fixniPlaty.filter(fp =>
                                            fp.platnost_od <= editorDo
                                            && (fp.platnost_do === null || fp.platnost_do >= editorOd)
                                        ).reduce((s, fp) => s + Number(fp.castka_kc || 0) + Number(fp.variabilni_bonus_kc || 0), 0);
                                        const { od: eOd, do: eDo } = getObdobi();
                                        const eDniMesic = Math.round((new Date(eDo + 'T12:00:00') - new Date(eOd + 'T12:00:00')) / 86400000) + 1;
                                        const edDnu = editorDny.length;
                                        const fixNaklad = eDniMesic > 0 ? fixMesicni * (edDnu / eDniMesic) : fixMesicni;
                                        const varNakladPrirazene = edSmeny.filter(s => !s.draft_smazano).reduce((a, s) => a + Number(s.naklad_celkem || 0), 0);
                                        const varNakladLC = varNakladPrirazene + otevNaklad;
                                        const celkemNaklad = varNakladLC + fixNaklad;
                                        const varH = edSmeny.filter(s => Number(s.sazba_kc_h) > 0).reduce((s, x) => s + Number(x.hodin || 0), 0) + otevH;
                                        return (
                                        <div style={tileStyle}>
                                            <div style={labelSt}>Náklady</div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                                                <span style={valueSt}>{fmtKc(varNakladLC) || '–'}</span>
                                                <span style={{ fontSize: 11, color: mutedColor }}>var.</span>
                                                {fixNaklad > 0 && <>
                                                    <span style={{ fontSize: 16, color: mutedColor }}>+</span>
                                                    <span style={valueSt}>{fmtKc(fixNaklad)}</span>
                                                    <span style={{ fontSize: 11, color: '#10B981' }}>fix.</span>
                                                    <span style={{ fontSize: 16, color: mutedColor }}>=</span>
                                                    <span style={{ ...valueSt, color: SECTION_COLOR }}>{fmtKc(celkemNaklad)}</span>
                                                </>}
                                            </div>
                                            <div style={subSt}>
                                                {varH > 0 && `Ø ${Math.round(varNakladLC / varH)} Kč/h variabilní`}
                                            </div>
                                        </div>
                                        );
                                    })()}
                                </div>
                            );
                        })()}

                        {/* Cell dropdown — fixed positioned, outside grid */}
                        {cellDropdown && (() => {
                            const isOtevrene = cellDropdown.zamId === 'otevrene';
                            // Šablony jen pro kariéru dané sekce, filtrované dle úrovně zaměstnance
                            const rawSablony = isOtevrene
                                ? (cellDropdown.groupId ? getSablonyProKarieru(cellDropdown.groupId, null, cellDropdown.datum) : sablony.filter(s => s.aktivni !== false))
                                : getSablonyProKarieru(cellDropdown.groupId, cellDropdown.zamId, cellDropdown.datum);

                            // Filtrovat šablony dle překryvů s existujícími směnami
                            // Overlap check přes VŠECHNY směny zaměstnance (ne jen z jedné kariéry)
                            const existujici = !isOtevrene ? smeny.filter(s => s.zamestnanec_id === cellDropdown.zamId && s.datum?.split('T')[0] === cellDropdown.datum && !s.otevrena) : [];
                            const casOverlap = (od1, do1, od2, do2) => {
                                const a1 = parseTime(od1), a2 = parseTime(do1), b1 = parseTime(od2), b2 = parseTime(do2);
                                return a1 < b2 && b1 < a2;
                            };
                            const ddSablony = rawSablony.filter(sab => {
                                return !existujici.some(s => casOverlap(sab.cas_od, sab.cas_do, s.cas_od, s.cas_do));
                            });
                            const r = cellDropdown.rect;
                            // Pozice: pokud by dropdown přetekl dole, zobrazit nahoru
                            const spaceBelow = window.innerHeight - r.bottom;
                            const goUp = spaceBelow < 200;
                            return (
                                <div ref={dropdownRef} onClick={e => e.stopPropagation()} style={{
                                    position: 'fixed',
                                    top: goUp ? undefined : r.bottom + 2,
                                    bottom: goUp ? (window.innerHeight - r.top + 2) : undefined,
                                    left: r.left, zIndex: 9999, minWidth: 190,
                                    background: isDark ? 'rgba(20,20,30,0.98)' : 'white',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                                    borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,0.3)', overflow: 'hidden',
                                    maxHeight: 260, overflowY: 'auto',
                                }}>
                                    {ddSablony.length > 0 && <div style={{ padding: '5px 10px', fontSize: 9, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>Šablony</div>}
                                    {ddSablony.map(sab => (
                                        <button key={sab.id}
                                            onClick={async () => {
                                                setCellDropdown(null);
                                                if (isOtevrene) {
                                                    // Vytvořit otevřenou směnu ze šablony rovnou
                                                    const body = { datum: cellDropdown.datum, kariera_id: sab.kariera_id, sablon_id: sab.id, cas_od: sab.cas_od?.slice(0,5), cas_do: sab.cas_do?.slice(0,5), hodin: sab.hodin, kapacita: 1 };
                                                    const res = await fetch('/api/shifts/otevrena', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                                                    if (res.ok) { refreshTab(); } else { const d = await res.json(); setError(d.error || d.message || 'Chyba'); }
                                                } else {
                                                    handleSelectSablon(sab, cellDropdown.datum, cellDropdown.zamId, sab.kariera_id);
                                                }
                                            }}
                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-primary)', background: 'transparent' }}
                                            onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <span style={{ fontWeight: 600, color: mutedColor, marginRight: 4 }}>{sab.kod}</span>
                                            {(sab.cas_od||'').slice(0,5)}-{(sab.cas_do||'').slice(0,5)}
                                            <span style={{ color: mutedColor, marginLeft: 4 }}>{sab.hodin}h</span>
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => {
                                            setCellDropdown(null);
                                            setSmenaForm({ ...emptySmena, datum: cellDropdown.datum, kariera_id: cellDropdown.groupId, otevrena: isOtevrene, zamestnanec_id: isOtevrene ? '' : cellDropdown.zamId });
                                            setEditingSmena(null); setSmenaModal(true); setError(null);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', cursor: 'pointer', fontSize: 11, color: SECTION_COLOR, background: 'transparent', borderTop: `1px solid ${borderC}`, fontWeight: 600 }}
                                        onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <Plus size={12} /> Vlastní směna
                                    </button>
                                </div>
                            );
                        })()}

                    </div>
                );
            })()}

            {/* ── PŘEHLED tab ── */}
            {jePlan && pohled !== 'mrizka' && (() => {
                const publishedSmeny = publishedPlan;
                const otevrene = otevrenePlan;

                // Přehled: použít samostatný fetch publikovaných (nezávislý na editoru)
                const pubSmeny = publishedSmeny.filter(s => !s.otevrena);
                const myZamId = user?.zamestnanec_id;
                const myZam = myZamId ? zamestnanci.find(z => z.id === myZamId) : null;
                const myInicialy = myZam ? `${myZam.jmeno?.charAt(0) || ''}${myZam.prijmeni?.charAt(0) || ''}` : '?';
                const hasSubordinates = user?.has_subordinates;
                const canEditS = (s) => upravujePlan; // Editovat smí, kdo Přehled upravuje
                const canViewS = (s) => upravujePlan || s.zamestnanec_id === myZamId; // Uživatel vidí detail své směny
                // Vrácení směny: vlastní směna + >48h do začátku (admin vidí, ale backend má vlastní kontrolu)
                const hodinyDoStartu = (s) => {
                    const datum = (typeof s.datum === 'string' ? s.datum : '').split('T')[0];
                    const cas = (s.cas_od || '00:00').slice(0, 5);
                    const start = new Date(`${datum}T${cas}`);
                    return (start.getTime() - Date.now()) / 3600000;
                };
                const canVratit = (s) => s.zamestnanec_id === myZamId && hodinyDoStartu(s) >= 48;
                const canProhodit = (s) => s.zamestnanec_id === myZamId && hodinyDoStartu(s) >= 12;
                const isOtevreneFiltr = pohledPlanu === 'otevrene';
                const publishedOtevrene = otevrene.filter(o => !o.draft_smazano && (kalendarSRozpracovanymi || !o.draft) && o.zbyva > 0);
                const filtrovane = isOtevreneFiltr
                    ? publishedOtevrene.map(o => ({ ...o, otevrena: true, zamestnanec_id: null }))
                    : pubSmeny.filter(s => {
                        if (pohledPlanu === 'moje') return s.zamestnanec_id === myZamId;
                        return true;
                    });
                const grouped = {};
                filtrovane.forEach(s => { const d = s.datum?.split('T')[0]; if (!grouped[d]) grouped[d] = []; grouped[d].push(s); });

                // Nepřítomnosti se propisují do všech filtrů (Moje, Všichni, Otevřené).
                // V Moje vidím i kolegy, abych viděl kdo nemůže vzít/přehodit směnu.
                const myNedostupnosti = nedostupnosti;
                // Helper pro tooltip s plnými jmény + typem
                const nedostTooltip = (list) => list.map(n => {
                    const jm = n.zamestnanec ? `${n.zamestnanec.jmeno} ${n.zamestnanec.prijmeni}` : '?';
                    const tc = TYP_CONFIG[n.typ] || TYP_CONFIG.jine;
                    return `${jm} — ${tc.label}${n.poznamka ? ` (${n.poznamka})` : ''}`;
                }).join('\n');

                // Kvalifikace aktuálního uživatele pro otevřenou směnu
                const urovenRankMap = { NOVICE: 1, JUNIOR: 2, MEDIOR: 3, SENIOR: 4, LEADER: 5 };
                const jeKvalifikovanyPro = (karId, minKod) => {
                    if (!karId || !minKod || !myZam) return false;
                    const myUroven = (myZam.urovne || []).find(u => u.kariera_id == karId);
                    const myRank = urovenRankMap[myUroven?.uroven?.kod] || 0;
                    const minRank = urovenRankMap[minKod] || 0;
                    return myRank >= minRank;
                };
                const urovenLabel = (kod) => kod ? kod.charAt(0) + kod.slice(1).toLowerCase() : '?';
                const getNedostupnostiProDen = (datum) => myNedostupnosti.filter(n => n.od <= datum && n.do >= datum);
                // Přidat nepřítomnosti do grouped pro mobile
                myNedostupnosti.forEach(n => {
                    const start = new Date(n.od + 'T12:00:00');
                    const end = new Date(n.do + 'T12:00:00');
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const dat = d.toISOString().split('T')[0];
                        if (!grouped[dat]) grouped[dat] = [];
                    }
                });
                // Kalendář i seznam kreslí dny období (`obdobiDny`), ať je období
                // měsíc nebo týden. Buňky nesou celé datum — týden přes přelom
                // měsíce by z čísla dne datum poskládat neuměl.
                const dnyObdobi2 = obdobiDny;
                const prvniDow2 = new Date(dnyObdobi2[0].datum + 'T12:00:00').getDay();
                const calOffset2 = prvniDow2 === 0 ? 6 : prvniDow2 - 1;
                const calCells2 = [];
                for (let i = 0; i < calOffset2; i++) calCells2.push(null);
                dnyObdobi2.forEach(d => calCells2.push(d.datum));
                const radkuKalendare = Math.ceil(calCells2.length / 7);
                // 37 px si vezme záhlaví dne a odsazení buňky, 41 px stojí jedna
                // dvouřádková dlaždice i s mezerou. Než se řádek změří, kreslí se vše.
                const maxDlazdic = radekKalendareVyska > 0
                    ? Math.max(1, Math.floor((radekKalendareVyska - 37) / 41))
                    : 99;
                // Sdílená dlaždice směny — shodná v kalendáři (compact) i v mobilním seznamu (full).
                const renderSmenaDlazdice = (s, varianta) => {
                    const sb = s.sablon || (s.sablon_id ? sablony.find(x => x.id === s.sablon_id) : null);
                    const bv = sb?.barva || '#6B7280';
                    const isCompact = varianta === 'compact';
                    const isOtevrena = !!s.otevrena;
                    const clickable = canEditS(s) || canViewS(s) || (isOtevrena && upravujePlan);
                    const d = isCompact ? {
                        padding: '4px 6px', borderRadius: 5, nameFs: 10, timeFs: 10, personFs: 9,
                        zaskok: { top: -3, right: -3, size: 14, fs: 8 },
                        crownInline: 12, stavIcon: 22,
                        akce: { right: 4, gap: 4, pad: 4, icon: 14, btnRadius: 6 },
                        btnFs: 10, btnPad: '4px 6px',
                    } : {
                        padding: '10px 12px', borderRadius: 7, nameFs: 12, timeFs: 12, personFs: 11,
                        zaskok: { top: -4, right: -4, size: 16, fs: 9 },
                        crownInline: 16, stavIcon: 34,
                        akce: { right: 10, gap: 8, pad: 10, icon: 22, btnRadius: 10 },
                        btnFs: 12, btnPad: '8px 12px',
                    };

                    // Otevřená směna — spočítat kvalifikaci, popisek, stav převzetí
                    const karId = isOtevrena ? (s.kariera_id || sb?.kariera_id) : null;
                    const karNazev = karId ? (kariery.find(k => k.id === karId)?.nazev || '?') : '?';
                    const urKod = isOtevrena ? (s.min_uroven_kod || sb?.min_uroven_kod) : null;
                    const urNazev = urKod ? urovenLabel(urKod) : '?';
                    const uzPrevzal = isOtevrena && s.prevzali?.some(p => p.zamestnanec_id === myZamId);
                    const kvalifikaceOk = isOtevrena ? jeKvalifikovanyPro(karId, urKod) : true;

                    return (
                        <div key={s.id}
                            onClick={(e) => {
                                if (vybiraKlik(e)) { if (!isOtevrena && upravujePlan) toggleShiftSelection(s.id); return; }
                                if (selectedShiftIds.length) setSelectedShiftIds([]);
                                if (isOtevrena) { if (upravujePlan) handleEditOtevrenu(s); return; }
                                // Klik ukazuje směnu ve sloupci; formulář se otevře až
                                // z „Upravit vše“ — kvůli půl hodině není proč ho otvírat.
                                if (canEditS(s) || canViewS(s)) setDetailSmena(s);
                            }}
                            style={{ ...glassCard, padding: d.padding, borderRadius: d.borderRadius,
                                borderLeft: `3px ${isOtevrena ? 'dashed' : 'solid'} ${bv}`,
                                outline: (selectedShiftIds.includes(s.id) || detailSmena?.id === s.id) ? `2px solid ${SECTION_COLOR}` : 'none',
                                background: selectedShiftIds.includes(s.id) ? `${SECTION_COLOR}20` : undefined,
                                cursor: clickable ? 'pointer' : 'default', position: 'relative' }}>
                            {s.zaskok && <span style={{ position: 'absolute', top: d.zaskok.top, right: d.zaskok.right, width: d.zaskok.size, height: d.zaskok.size, borderRadius: '50%', background: bv, color: '#000', fontSize: d.zaskok.fs, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} title="Záskok">Z</span>}
                            {!isCompact && (sb?.nazev || s.manazer_smeny || smenaPozadujeAuto(s) || s.draft) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, lineHeight: 1.2 }}>
                                    {sb?.nazev && <span style={{ fontSize: d.nameFs, fontWeight: 600, color: 'var(--text-primary)' }}>{sb.nazev}</span>}
                                    <ManazerKorunka s={s} size={d.crownInline} />
                                    <AutoIkona s={s} size={d.crownInline} />
                                    {/* Jen v kalendáři plánovače na mobilu — publikované sady drafty nemají. */}
                                    {s.draft && (
                                        <span title="Změna zatím není publikovaná — lidi ji nevidí"
                                            style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                                                background: 'rgba(245,158,11,.14)', color: '#F59E0B', whiteSpace: 'nowrap' }}>
                                            Nepublikováno
                                        </span>
                                    )}
                                </div>
                            )}
                            <div title={isCompact ? sb?.nazev : undefined}
                                style={{ fontSize: d.timeFs, fontWeight: 500, color: 'var(--text-primary)',
                                    display: 'flex', alignItems: 'center', gap: 4, lineHeight: 1.3,
                                    whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {sb?.kod && <span style={{ fontWeight: 700, color: bv }}>{sb.kod}</span>}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {(s.cas_od || '').slice(0, 5)}-{(s.cas_do || '').slice(0, 5)}
                                </span>
                                {isCompact && <ManazerKorunka s={s} size={d.crownInline} />}
                                {isCompact && <AutoIkona s={s} size={d.crownInline} />}
                            </div>
                            {isOtevrena ? (
                                <div style={{ fontSize: d.personFs, fontWeight: 700, color: SECTION_COLOR, marginTop: 2, lineHeight: 1.25 }}>
                                    K dispozici pro {karNazev} ({urNazev})
                                </div>
                            ) : (
                                /* V pohledu „Moje“ je na každé dlaždici moje vlastní jméno —
                                   řádek, který nic neříká a bere výšku. */
                                (pohledPlanu === 'moje' && s.zamestnanec_id === myZamId) ? null : (
                                <div style={{ fontSize: d.personFs, color: mutedColor, marginTop: isCompact ? 0 : 2, lineHeight: 1.3,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.zamestnanec ? `${s.zamestnanec.jmeno} ${s.zamestnanec.prijmeni}` : ''}</div>
                                )
                            )}
                            {isOtevrena ? (
                                <div onClick={e => e.stopPropagation()} style={{ marginTop: isCompact ? 4 : 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {upravujePlan ? (<>
                                        <select id={`prehled-prevzit-${s.id}`}
                                            style={{ flex: '1 1 110px', fontSize: d.btnFs, padding: d.btnPad, borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                            <option value="">Zaměstnanec…</option>
                                            {zamestnanci.filter(z => z.stav !== 'neaktivni' && !s.prevzali?.some(p => p.zamestnanec_id === z.id)).map(z => <option key={z.id} value={z.id}>{z.jmeno} {z.prijmeni}</option>)}
                                        </select>
                                        <button onClick={e => { e.stopPropagation(); const sel = document.getElementById(`prehled-prevzit-${s.id}`); if (sel.value) handlePrevzit(s.id, parseInt(sel.value)); }}
                                            style={{ fontSize: d.btnFs, fontWeight: 600, padding: d.btnPad, borderRadius: 5, background: SECTION_COLOR, color: '#000', border: 'none', cursor: 'pointer' }}>
                                            Přiřadit
                                        </button>
                                    </>) : uzPrevzal ? (
                                        <span style={{ fontSize: d.btnFs, color: mutedColor, fontStyle: 'italic' }}>Již převzato</span>
                                    ) : myZamId ? (
                                        <button
                                            onClick={e => { e.stopPropagation(); if (kvalifikaceOk) handlePrevzit(s.id, myZamId); }}
                                            disabled={!kvalifikaceOk}
                                            title={kvalifikaceOk ? 'Převzít směnu' : `Nedostatečná kvalifikace — vyžadováno ${karNazev} na úrovni ${urNazev}`}
                                            style={{ flex: 1, fontSize: d.btnFs, fontWeight: 600, padding: d.btnPad, borderRadius: 5, background: kvalifikaceOk ? SECTION_COLOR : 'transparent', color: kvalifikaceOk ? '#000' : mutedColor, border: kvalifikaceOk ? 'none' : `1px dashed ${isDark ? 'rgba(255,255,255,0.18)' : 'var(--border-default)'}`, cursor: kvalifikaceOk ? 'pointer' : 'not-allowed', opacity: kvalifikaceOk ? 1 : 0.55 }}>
                                            Převzít
                                        </button>
                                    ) : null}
                                </div>
                            ) : s.stav === 'probihajici' ? (
                                <div style={{ position: 'absolute', top: '50%', right: d.akce.right, transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 4, padding: isCompact ? '1px 5px' : '3px 8px', borderRadius: 99, background: `${stavMeta.probihajici.color}20`, border: `1px solid ${stavMeta.probihajici.color}55`, color: stavMeta.probihajici.color, fontSize: isCompact ? 9 : 10, fontWeight: 600, whiteSpace: 'nowrap' }} title="Směna probíhá">
                                    <Activity size={isCompact ? 9 : 11} strokeWidth={2.5} /> Probíhající
                                </div>
                            /* „Uzavřena“ se z dlaždice vytratila schválně: v proběhlém období
                               je dokončená každá směna a stokrát totéž slovo nic neříká.
                               Že je období hotové, hlásí hlavička u názvu období. */
                            ) : (
                                <div style={{ position: 'absolute', top: '50%', right: d.akce.right, transform: 'translateY(-50%)', display: 'flex', gap: d.akce.gap }}>
                                    {canProhodit(s) && (
                                        <button
                                            onClick={e => { e.stopPropagation(); setProhoditSmena(s); }}
                                            title="Prohodit / předat směnu kolegovi"
                                            style={{ background: '#6366F118', border: '1px solid #6366F155', cursor: 'pointer', padding: d.akce.pad, color: '#6366F1', display: 'flex', borderRadius: d.akce.btnRadius }}
                                        >
                                            <ArrowLeftRight size={d.akce.icon} />
                                        </button>
                                    )}
                                    {canVratit(s) && (
                                        <button
                                            onClick={e => { e.stopPropagation(); handleVratit(s); }}
                                            title="Vrátit směnu (nemůžu přijít)"
                                            style={{ background: '#D85A3018', border: '1px solid #D85A3055', cursor: 'pointer', padding: d.akce.pad, color: '#D85A30', display: 'flex', borderRadius: d.akce.btnRadius }}
                                        >
                                            <RotateCcw size={d.akce.icon} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                };
                return (<>
                    {/* Ovládání období je v hlavičce Plánu, společné pro všechna zobrazení. */}
                    {/* Seznam / Kalendář */}
                    {(isMobile || isNarrow) ? (
                        /* Mobile/narrow desktop: seznam dnů období */
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column',
                            ...(editorFullHeight ? { flex: 1, minHeight: 0 } : null) }}>
                        {/* Ukazatele tahu — výšku a text jim nastavuje `ukazTah`. */}
                        {isMobile && [-1, 1].map(smer => (
                            <div key={smer} ref={smer < 0 ? tahNahoreRef : tahDoleRef} aria-hidden="true"
                                data-mesic={sousedniMesic(smer)}
                                style={{ position: 'absolute', left: 0, right: 0, zIndex: 1, height: 0, overflow: 'hidden',
                                    ...(smer < 0 ? { top: 0 } : { bottom: 0 }),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12.5, fontWeight: 600, color: mutedColor, pointerEvents: 'none' }} />
                        ))}
                        <div ref={isMobile ? seznamDnuRef : undefined}
                            onTouchStart={isMobile ? tahZacatek : undefined}
                            onTouchMove={isMobile ? tahPohyb : undefined}
                            onTouchEnd={isMobile ? tahKonec : undefined}
                            onTouchCancel={isMobile ? tahKonec : undefined}
                            style={{ ...(editorFullHeight ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null),
                                // Bez vlastního pružení — okraj seznamu patří tahu měsícem.
                                ...(isMobile ? { overscrollBehaviorY: 'none' } : null) }}>
                        <div ref={isMobile ? seznamObsahRef : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {/* Název měsíce: z lišty zmizel přepínač období, takže orientaci
                                dává nadpis na začátku seznamu. */}
                            {isMobile && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                                    <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{getObdobiLabel()}</span>
                                    {obdobiUzavrene && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99,
                                            background: 'rgba(16,185,129,.14)', color: '#10B981', fontSize: 10.5, fontWeight: 700 }}>
                                            <CheckCircle2 size={10} /> Uzavřeno
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* Den, ve kterém se neděje nic, dostával nadpis stejně — třicet
                                prázdných nadpisů pod sebou. Dnešek zůstává vždy, i prázdný:
                                je to kotva pro auto-scroll a odpověď na „jedu dneska?“. */}
                            {dnyObdobi2.filter(({ datum }) => {
                                if (datum === todayStr()) return true;
                                if ((grouped[datum] || []).some(s => s.id)) return true;
                                return getNedostupnostiProDen(datum).length > 0;
                            }).map(({ datum }) => {
                                const dd = new Date(datum + 'T12:00:00');
                                const denLabel = dd.toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'long' });
                                const isWk = dd.getDay() === 0 || dd.getDay() === 6;
                                const jeDnes = datum === todayStr();
                                const denNedost = getNedostupnostiProDen(datum);
                                const denSmeny = (grouped[datum] || []).filter(s => s.id);
                                return (
                                    <div key={datum} data-prehled-dnes={jeDnes ? '1' : undefined} style={jeDnes ? { scrollMarginTop: 12 } : undefined}>
                                        {(() => {
                                            const jaNepr = denNedost.some(n => n.zamestnanec_id === myZamId);
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', borderBottom: `1px solid ${jeDnes ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)')}`, marginBottom: 6 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: jeDnes ? SECTION_COLOR : isWk ? mutedColor : 'var(--text-primary)', textTransform: 'capitalize' }}>{denLabel}</span>
                                                    {jeDnes && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: SECTION_COLOR, color: '#000', textTransform: 'uppercase', letterSpacing: 0.4 }}>Dnes</span>}
                                                    {jaNepr && <X size={12} style={{ color: '#EF4444', strokeWidth: 3 }} />}
                                                </div>
                                            );
                                        })()}
                                        {/* Nepřítomnosti — sloučená karta */}
                                        {denNedost.length > 0 && (() => {
                                            const jaNepr = denNedost.some(n => n.zamestnanec_id === myZamId);
                                            const ostatni = denNedost.filter(n => n.zamestnanec_id !== myZamId);
                                            const inicialy = ostatni.map(n => n.zamestnanec ? `${n.zamestnanec.jmeno?.charAt(0)}${n.zamestnanec.prijmeni?.charAt(0)}` : '?');
                                            const label = [jaNepr ? myInicialy : null, ...inicialy].filter(Boolean).join(', ');
                                            const fullNames = denNedost.map(n => {
                                                const jm = n.zamestnanec ? `${n.zamestnanec.jmeno} ${n.zamestnanec.prijmeni}` : '?';
                                                const tc = TYP_CONFIG[n.typ] || TYP_CONFIG.jine;
                                                return `${jm} (${tc.label})`;
                                            });
                                            return (
                                                <div style={{ ...glassCard, borderRadius: 7, padding: '10px 12px', marginBottom: 6, borderLeft: '3px dashed #EF4444', background: 'rgba(239,68,68,0.08)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <UserX size={14} style={{ color: '#EF4444', flexShrink: 0 }} />
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>{label}</span>
                                                    </div>
                                                    <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                                                        {fullNames.join(' · ')}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        {/* Směny */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                                            {denSmeny.map(s => renderSmenaDlazdice(s, 'full'))}
                                        </div>
                                    </div>
                                );
                            })}
                            {isMobile && (
                                <div style={{ textAlign: 'center', fontSize: 11.5, color: mutedColor, padding: '14px 0 4px' }}>
                                    Táhni dál pro {sousedniMesic(1)}
                                </div>
                            )}
                        </div>
                        </div>
                        </div>
                    ) : pohled === 'seznam' && (upravujePlan || hasSubordinates) ? (
                        <div style={{ ...glassCard, borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                            ...(editorFullHeight ? { flex: 1, minHeight: 0 } : null) }}>
                            <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead><tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` }}>
                                        {['Datum', 'Den', 'Směna', 'Čas', 'Osoba', 'Stav'].map(h => (
                                            <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase',
                                                position: 'sticky', top: 0, zIndex: 1, background: isDark ? '#14121c' : 'var(--surface-raised)' }}>{h}</th>
                                        ))}
                                    </tr></thead>
                                    <tbody>
                                        {/* Sloučený seznam: směny + dny obsahující jen nepřítomnost (bez směn).
                                            Ikonka u data zobrazuje tooltip se jmény nepřítomných. */}
                                        {(() => {
                                            const nedostByDatum = {};
                                            myNedostupnosti.forEach(n => {
                                                const start = new Date(n.od + 'T12:00:00');
                                                const end = new Date(n.do + 'T12:00:00');
                                                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                                    const dat = d.toISOString().split('T')[0];
                                                    if (!nedostByDatum[dat]) nedostByDatum[dat] = [];
                                                    nedostByDatum[dat].push(n);
                                                }
                                            });
                                            const smenyByDatum = {};
                                            filtrovane.forEach(s => {
                                                const dat = s.datum?.split?.('T')?.[0] || String(s.datum).split('T')[0];
                                                if (!smenyByDatum[dat]) smenyByDatum[dat] = true;
                                            });
                                            const items = filtrovane.map(s => ({
                                                kind: 'shift',
                                                datum: s.datum?.split?.('T')?.[0] || String(s.datum).split('T')[0],
                                                cas: s.cas_od || '',
                                                s,
                                            }));
                                            Object.keys(nedostByDatum).forEach(dat => {
                                                if (!smenyByDatum[dat]) {
                                                    items.push({ kind: 'absenceOnly', datum: dat, cas: '00:00', list: nedostByDatum[dat] });
                                                }
                                            });
                                            items.sort((a, b) => (a.datum + a.cas).localeCompare(b.datum + b.cas));

                                            const renderNedostIkona = (dat) => {
                                                const list = nedostByDatum[dat];
                                                if (!list || list.length === 0) return null;
                                                return (
                                                    <span title={nedostTooltip(list)}
                                                          onClick={e => e.stopPropagation()}
                                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6, padding: '1px 5px 1px 4px', borderRadius: 99, background: 'rgba(239,68,68,0.15)', color: '#EF4444', fontSize: 10, fontWeight: 700, cursor: 'help', verticalAlign: 'middle' }}>
                                                        <UserX size={10} strokeWidth={2.5} />
                                                        {list.length}
                                                    </span>
                                                );
                                            };

                                            // První řádek dneška nese značku pro auto-scroll; ostatní řádky
                                            // dne se jen podbarví, ať je „dnes" v dlouhém seznamu vidět.
                                            const prvniDnesniIdx = items.findIndex(it => it.datum === todayStr());
                                            return items.map((it, i) => {
                                                const d2 = new Date(it.datum + 'T12:00:00');
                                                const jeDnes = it.datum === todayStr();
                                                const dnesAttrs = jeDnes
                                                    ? { 'data-prehled-dnes': i === prvniDnesniIdx ? '1' : undefined, style: { scrollMarginTop: 60 } }
                                                    : {};
                                                const dnesBg = jeDnes ? `${SECTION_COLOR}12` : null;
                                                if (it.kind === 'absenceOnly') {
                                                    return (
                                                        <tr key={`ao-${it.datum}`} {...dnesAttrs} style={{ ...(dnesAttrs.style || {}), borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`, background: dnesBg || (isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)') }}>
                                                            <td style={{ padding: '8px 12px', fontWeight: 500, color: jeDnes ? SECTION_COLOR : 'var(--text-primary)' }}>
                                                                {d2.getDate()}.{d2.getMonth()+1}.
                                                                {renderNedostIkona(it.datum)}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: mutedColor }}>{dnyLabels[d2.getDay() === 0 ? 7 : d2.getDay()]}</td>
                                                            <td colSpan={4} style={{ padding: '8px 12px', color: mutedColor, fontStyle: 'italic', fontSize: 12 }}>Žádné směny — nepřítomnost</td>
                                                        </tr>
                                                    );
                                                }
                                                const s = it.s;
                                                const sab2 = s.sablon || (s.sablon_id ? sablony.find(sb => sb.id === s.sablon_id) : null);
                                                const barva2 = sab2?.barva || '#6B7280';
                                                const isOtev = !!s.otevrena;
                                                const karIdRow = isOtev ? (s.kariera_id || sab2?.kariera_id) : null;
                                                const karNazevRow = karIdRow ? (kariery.find(k => k.id === karIdRow)?.nazev || '?') : '?';
                                                const urKodRow = isOtev ? (s.min_uroven_kod || sab2?.min_uroven_kod) : null;
                                                const rowClickable = isOtev ? upravujePlan : (canEditS(s) || canViewS(s));
                                                return (
                                                    <tr key={`ls-${s.id}-${i}`} {...dnesAttrs} onClick={() => isOtev ? (upravujePlan ? handleEditOtevrenu(s) : null) : (canEditS(s) || canViewS(s)) ? setDetailSmena(s) : null}
                                                        style={{ ...(dnesAttrs.style || {}), borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`, cursor: rowClickable ? 'pointer' : 'default', background: dnesBg || undefined }}
                                                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                                                        onMouseLeave={e => e.currentTarget.style.background = dnesBg || ''}>
                                                        <td style={{ padding: '8px 12px', fontWeight: 500, color: jeDnes ? SECTION_COLOR : 'var(--text-primary)' }}>
                                                            {d2.getDate()}.{d2.getMonth()+1}.
                                                            {renderNedostIkona(it.datum)}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', color: mutedColor }}>{dnyLabels[d2.getDay() === 0 ? 7 : d2.getDay()]}</td>
                                                        <td style={{ padding: '8px 12px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: barva2 }} /><span style={{ color: 'var(--text-primary)' }}>{sab2?.nazev || sab2?.kod || '\u2014'}</span></span></td>
                                                        <td style={{ padding: '8px 12px', color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>{(s.cas_od||'').slice(0,5)}-{(s.cas_do||'').slice(0,5)}</td>
                                                        <td style={{ padding: '8px 12px', fontWeight: isOtev ? 700 : 500, color: isOtev ? SECTION_COLOR : 'var(--text-primary)' }}>{isOtev ? `K dispozici pro ${karNazevRow} (${urovenLabel(urKodRow)})` : (s.zamestnanec ? s.zamestnanec.jmeno + ' ' + s.zamestnanec.prijmeni : '\u2014')}</td>
                                                        <td style={{ padding: '8px 12px' }}>
                                                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: isOtev ? `${SECTION_COLOR}18` : 'rgba(16,185,129,0.12)', color: isOtev ? SECTION_COLOR : '#10B981' }}>{isOtev ? 'Otevřená' : 'Přiřazená'}</span>
                                                            {s.zaskok && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: barva2, color: '#000', fontSize: 9, fontWeight: 800, alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Záskok">Z</span>}
                                                            {s.manazer_smeny && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#FF8A00', color: '#000', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Manažer směny"><Crown size={9} strokeWidth={2.5} /></span>}
                                                            {smenaPozadujeAuto(s) && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#3B82F6', color: '#fff', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Vyžaduje auto"><Truck size={9} /></span>}
                                            {smenaPozadujePokladnu(s) && <span style={{ marginLeft: 6, display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', background: '#10B981', color: '#fff', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }} title="Vyžaduje pokladnu"><Banknote size={9} /></span>}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div style={{ ...glassCard, borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                            ...(editorFullHeight ? { flex: 1, minHeight: 0 } : { minHeight: 'calc(100vh - 260px)' }) }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}`, flexShrink: 0 }}>
                                {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map(d => (
                                    <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: mutedColor, padding: '8px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{d}</div>
                                ))}
                            </div>
                            {/* Řádky se dělí o výšku rovným dílem (`minmax(0,1fr)`), ať jich je
                                pět nebo šest — `gridAutoRows: minmax(100px, 1fr)` dělal z šestiřádkového
                                měsíce mřížku vyšší než stránka. Přeteče-li den směnami, roluje buňka. */}
                            <div ref={kalendarMrizkaRef} data-radku={radkuKalendare}
                                style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                                gridTemplateRows: `repeat(${radkuKalendare}, minmax(0, 1fr))`,
                                flex: 1, minHeight: 0 }}>
                                {calCells2.map((dat, i) => {
                                    if (!dat) return <div key={`pe-${i}`} style={{ minHeight: 0, borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}` }} />;
                                    const den = Number(dat.slice(8));
                                    const denS = grouped[dat] || [];
                                    const dd = new Date(dat + 'T12:00:00');
                                    const isWk = dd.getDay() === 0 || dd.getDay() === 6;
                                    const isToday = dat === todayStr();
                                    const isSv = jeSvatek(dat);
                                    const denNedost = getNedostupnostiProDen(dat);
                                    // Šrafura jen když má vlastní nepřítomnost — kolegové se zobrazí jen v ikoně/přehledu.
                                    const myDenNedost = denNedost.filter(n => n.zamestnanec_id === myZamId);
                                    const stripeOrder = ['nemoc', 'osobni', 'dovolena', 'jine'];
                                    const dominTyp = stripeOrder.find(t => myDenNedost.some(n => n.typ === t)) || null;
                                    const stripeColor = dominTyp ? ({
                                        dovolena: 'rgba(59,130,246,0.10)',
                                        nemoc:    'rgba(239,68,68,0.12)',
                                        osobni:   'rgba(245,158,11,0.10)',
                                        jine:     'rgba(107,114,128,0.10)',
                                    }[dominTyp]) : null;
                                    const cellBg = isToday ? `${SECTION_COLOR}08` : (isWk || isSv) ? (isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)') : 'transparent';
                                    return (
                                        <div key={dat} data-prehled-dnes={isToday ? '1' : undefined} style={{
                                            minHeight: 0, padding: '6px 4px 4px',
                                            display: 'flex', flexDirection: 'column',
                                            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                                            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                                            scrollMarginTop: 12,
                                            background: cellBg,
                                            ...(stripeColor ? { backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 7px, ${stripeColor} 7px, ${stripeColor} 13px)` } : {}),
                                        }}>
                                            <div style={{ marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                                <span style={{ fontSize: 12, fontWeight: 700, width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: isToday ? SECTION_COLOR : 'transparent', color: isToday ? '#000' : (isWk || isSv) ? mutedColor : 'var(--text-primary)' }}>{den}</span>
                                                {denNedost.length > 0 && (
                                                    <span title={nedostTooltip(denNedost)}
                                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 5px 1px 4px', borderRadius: 99, background: 'rgba(239,68,68,0.15)', color: '#EF4444', fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}>
                                                        <UserX size={10} strokeWidth={2.5} />
                                                        {denNedost.length}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Směny — jen co se do řádku vejde. */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                                {denS.slice(0, maxDlazdic).map(s => renderSmenaDlazdice(s, 'compact'))}
                                                {denS.length > maxDlazdic && (
                                                    <button onClick={() => { setTydenOd(pondeliTydne(dat)); setEditorMode('tyden'); }}
                                                        title={`Zobrazit týden ${denS.length - maxDlazdic === 1 ? 'a další směnu' : `a další směny (${denS.length - maxDlazdic})`}`}
                                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '1px 4px',
                                                            fontSize: 10, fontWeight: 700, color: SECTION_COLOR, textAlign: 'left', flexShrink: 0 }}>
                                                        + {denS.length - maxDlazdic} další
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>);
            })()}

            </div>
            {/* Sloupec kopíruje výšku zobrazení vedle — graf a časová osa si vezmou,
                co zbyde, a až když se obsah nevejde, roluje se uvnitř karty.
                Drží **jen** čísla období: detail a editace směny jedou v inspektoru
                (viz `detailSmena` níž), protože formulář ve sloupci nemá kam růst
                a na mobilu neexistuje vůbec. */}
            {zobrazitPanel && (
                <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column',
                    ...(editorFullHeight ? { alignSelf: 'stretch', minHeight: 0 } : { alignSelf: 'flex-start' }) }}>
                    {panelObdobi}
                </div>
            )}
            </div>
            )}

            {/* Hromadný výběr patří celému Plánu, ne jen mřížce — v kalendáři
                se směny vybírají stejně dobře. */}
            {jePlan && !isMobile && selectionMode && (
                <div style={{ position: 'fixed', bottom: isMobile ? 80 : 24, left: '50%', transform: 'translateX(-50%)', zIndex: 100, display: 'flex', alignItems: 'center', gap: 10, background: isDark ? 'rgba(20,20,30,0.98)' : 'white', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 9, padding: '10px 16px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 'calc(100vw - 32px)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {selectedShiftIds.length > 0 ? `${selectedShiftIds.length} vybráno` : 'Klikáním se směny vybírají'}
                    </span>
                    {[
                        { id: 'uvolnit', label: 'Uvolnit', Ikona: Unlock, barva: '#F59E0B', onClick: handleBatchUvolnit },
                        { id: 'smazat', label: 'Smazat', Ikona: Trash2, barva: '#EF4444', onClick: handleBatchSmazat },
                        { id: 'presun', label: 'Přesunout k…', Ikona: ArrowLeftRight, barva: SECTION_COLOR, onClick: () => setMoveTargetModal(true) },
                    ].map(a => {
                        const prazdno = selectedShiftIds.length === 0;
                        return (
                            <button key={a.id} onClick={a.onClick} disabled={prazdno}
                                style={{ padding: '7px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                                    cursor: prazdno ? 'not-allowed' : 'pointer', opacity: prazdno ? 0.5 : 1,
                                    background: prazdno ? 'transparent' : `${a.barva}20`, color: prazdno ? mutedColor : a.barva,
                                    display: 'flex', alignItems: 'center', gap: 4 }}>
                                <a.Ikona size={14} /> {a.label}
                            </button>
                        );
                    })}
                    <button onClick={exitSelectionMode}
                        style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer', fontSize: 12, fontWeight: 500, background: 'transparent', color: mutedColor }}>
                        Zrušit
                    </button>
                </div>
            )}

            {inspektorSablon}

            {/* Detail a editace směny. Inspektor vyjede zprava přes pravý sloupec —
                mřížka vedle zůstane vidět i ovladatelná (`modal={false}`, bez
                backdropu a bez zámku scrollu). Na mobilu je to modál přes celou
                plochu: tam žádný sloupec k překrytí není. Dřív detail stál v tom
                sloupci, takže pod 1200 px klik na směnu nedělal vůbec nic. */}
            {detailSmena && (
                <SidePanel open onClose={() => setDetailSmena(null)}
                    title={upravujePlan && !detailSmena.otevrena ? 'Upravit směnu' : 'Detail směny'}
                    isDark={isDark} isMobile={isMobile} width={400} modal={false}
                    accent={SECTION_COLOR} topOffset={inspektorTop}>
                    <div style={{ padding: 16 }}>{detailSmenyObsah(detailSmena, true)}</div>
                </SidePanel>
            )}

            {jePlan && moveTargetModal && (
                <Modal title={`Přesunout ${selectedShiftIds.length} směn k…`} onClose={() => setMoveTargetModal(false)} isDark={isDark} isMobile={isMobile}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
                        {zamestnanci.filter(z => z.stav !== 'neaktivni').map(z => (
                            <button key={z.id} onClick={() => handleBatchPresun(z.id)}
                                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, background: 'transparent', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <span>{z.jmeno} {z.prijmeni}</span>
                                <span style={{ fontSize: 11, color: mutedColor }}>{(z.kariery || []).map(k => k.kod).join(', ')}</span>
                            </button>
                        ))}
                    </div>
                </Modal>
            )}

            {/* ── ŠABLONY tab ── */}
            {/* ── ŠABLONY & AUTOMATIKA tab ── */}
            {tab === 'dochazka' && <Dochazka isDark={isDark} glassCard={glassCard} mutedColor={mutedColor} iStyle={iStyle} isAdmin={muze(user, 'smeny.dochazka', 'upravuje')} isSuperadmin={user?.role === 'superadmin'} plnaVyska={plnaVyska} />}

            {/* ── TAB: NEPŘÍTOMNOST ── */}
            {tab === 'nepritomnost' && <Absence isDark={isDark} glassCard={glassCard} mutedColor={mutedColor} iStyle={iStyle} isAdmin={muze(user, 'smeny.nepritomnost', 'upravuje')} user={user} />}

            </div>

            {/* Formulář směny — zakládání i editace, přiřazené i otevřené.
                Otevírá ho „+ Směna" v liště, „Vlastní směna" v buňce mřížky,
                duplikace i proklik na chip. Blok zmizel s odstraněním záložky
                Náklady (51f5aa34) — stav `smenaModal` se dál nastavoval, jen
                ho nebylo čím vykreslit, takže obě cesty k nové směně mlčely.

                Jede jako **inspektor**, ne modal: zakládání směny je práce nad
                vybranou buňkou mřížky a člověk při něm potřebuje vidět, koho
                na ten den už má (norma standard-rozvrzeni.md §1f). Detail
                směny je inspektor taky, takže obojí teď vypadá a sedí stejně.

                Výběry jedou přes `Dropdown`, ne nativní `<select>` (norma OS).
                Ten neumí `required`, takže co dřív hlídal prohlížeč, hlídá teď
                `zkontrolujAOdesli` — jinak by prázdný formulář spadl až na
                validaci backendu. */}
            {smenaModal && (() => {
                const autoHodin = vypocetHodin(smenaForm.cas_od, smenaForm.cas_do, smenaForm.pauzy);
                const updateFormField = (field, val) => {
                    const next = { ...smenaForm, [field]: val };
                    next.hodin = vypocetHodin(next.cas_od, next.cas_do, next.pauzy) || next.hodin;
                    setSmenaForm(next);
                };
                const updatePauzy = (pauzy) => setSmenaForm(s => ({ ...s, pauzy, hodin: vypocetHodin(s.cas_od, s.cas_do, pauzy) || s.hodin }));
                const zavri = () => { setSmenaModal(false); setEditingSmena(null); setError(null); };

                const zkontrolujAOdesli = (e) => {
                    e.preventDefault();
                    if (!editingSmena && !smenaForm.datum) return setError('Vyber datum.');
                    if (!smenaForm.kariera_id) return setError('Vyber kariéru.');
                    if (!smenaForm.otevrena && !smenaForm.zamestnanec_id) {
                        return setError('Vyber zaměstnance, nebo směnu označ jako otevřenou.');
                    }
                    if (!smenaForm.cas_od || !smenaForm.cas_do) return setError('Vyplň čas od a do.');
                    handlePridatSmenu(e);
                };

                // Bez záskoku nabízíme jen lidi s vybranou kariérou — kdo ji nemá,
                // stejně neprojde kontrolou kvalifikace na backendu.
                const nabidkaZamestnancu = zamestnanci.filter(z => {
                    if (z.stav === 'neaktivni') return false;
                    if (smenaForm._zaskok || !smenaForm.kariera_id) return true;
                    return (z.kariery || []).some(k => String(k.id) === String(smenaForm.kariera_id));
                });

                // Tlačítka stojí v patičce panelu, ne na konci formuláře —
                // ten je na výšku přes dvě obrazovky a „Vytvořit směnu" by se
                // muselo doscrollovat. `form` má id, aby na něj submit dosáhl
                // i zvenčí.
                const patickaFormulare = (
                    <ModalActions form={ID_FORMULARE_SMENY} onCancel={zavri}
                        submitLabel={editingSmena ? 'Uložit změny' : 'Vytvořit směnu'} loading={saving}
                        onSmazat={editingSmena ? async () => { const s = editingSmena; if (await handleSmazatSmenu(s) !== false) zavri(); } : null} />
                );

                return (
                <SidePanel open onClose={zavri}
                    title={editingSmena ? 'Upravit směnu' : 'Nová směna'}
                    isDark={isDark} isMobile={isMobile} width={460} modal={false}
                    accent={SECTION_COLOR} topOffset={inspektorTop} footer={patickaFormulare}>
                    <form id={ID_FORMULARE_SMENY} onSubmit={zkontrolujAOdesli} style={{ padding: 16 }}>
                        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#EF4444' }}>{error}</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {/* Otevřená směna */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 14px', borderRadius: 7, background: smenaForm.otevrena ? `${SECTION_COLOR}12` : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'), border: `1px solid ${smenaForm.otevrena ? SECTION_COLOR + '40' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, transition: 'all 0.15s' }}>
                                <input type="checkbox" checked={smenaForm.otevrena || false}
                                    onChange={e => setSmenaForm({ ...smenaForm, otevrena: e.target.checked, zamestnanec_id: e.target.checked ? '' : smenaForm.zamestnanec_id })}
                                    className="accent-amber-500" style={{ width: 16, height: 16 }} />
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Otevřená směna</div>
                                    <div style={{ fontSize: 11, color: mutedColor }}>Bez přiřazeného zaměstnance — kdokoli s kvalifikací si ji může vzít</div>
                                </div>
                            </label>

                            {!editingSmena && (
                                <div><Label>Datum *</Label><input type="date" required value={smenaForm.datum} onChange={e => setSmenaForm({ ...smenaForm, datum: e.target.value })} style={iStyle} /></div>
                            )}

                            {/* Kariéra → Role → Seniorita */}
                            <div style={{ padding: '12px 14px', borderRadius: 7, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <FormGrid cols={isMobile ? 1 : 2}>
                                    <div><Label>Kariéra *</Label>
                                        <Dropdown
                                            value={smenaForm.kariera_id ? String(smenaForm.kariera_id) : ''}
                                            onChange={(v) => { setSmenaForm({ ...smenaForm, kariera_id: v, role_ids: [] }); setError(null); }}
                                            options={kariery.map(k => ({ value: String(k.id), label: `${k.ikona || ''} ${k.nazev}`.trim() }))}
                                            placeholder="Vyber kariéru…" isDark={isDark} accent={SECTION_COLOR} height={38}
                                        />
                                    </div>
                                    <div><Label>Min. seniorita</Label>
                                        <Dropdown
                                            value={smenaForm.min_uroven_kod || 'NOVICE'}
                                            onChange={(v) => setSmenaForm({ ...smenaForm, min_uroven_kod: v })}
                                            options={['NOVICE', 'JUNIOR', 'MEDIOR', 'SENIOR', 'LEADER']
                                                .map(u => ({ value: u, label: u.charAt(0) + u.slice(1).toLowerCase() }))}
                                            isDark={isDark} accent={SECTION_COLOR} height={38}
                                        />
                                    </div>
                                </FormGrid>
                                {(() => {
                                    const activeRoles = vsechnyRole.filter(r => r.aktivni);
                                    if (activeRoles.length === 0) return null;
                                    const karId = String(smenaForm.kariera_id || '');
                                    const matched = activeRoles.filter(r => karId && String(r.kariera_id) === karId);
                                    const rest = activeRoles.filter(r => !karId || String(r.kariera_id) !== karId);
                                    const sorted = [...matched, ...rest];
                                    return (
                                        <div>
                                            <Label>Role</Label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {sorted.map(r => {
                                                    const sel = (smenaForm.role_ids || []).includes(r.id);
                                                    const isMatch = karId && String(r.kariera_id) === karId;
                                                    return (
                                                        <button key={r.id} type="button"
                                                            onClick={() => setSmenaForm(f => ({ ...f, role_ids: sel ? f.role_ids.filter(id => id !== r.id) : [...(f.role_ids || []), r.id] }))}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 4,
                                                                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: sel ? 600 : 400,
                                                                background: sel ? `${r.barva}20` : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                                                border: `1px solid ${sel ? r.barva : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                                color: sel ? r.barva : 'var(--text-secondary)',
                                                                opacity: isMatch || sel || !karId ? 1 : 0.5,
                                                                cursor: 'pointer', transition: 'all 0.15s',
                                                            }}>
                                                            {r.ikona} {r.nazev}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            <FormGrid cols={isMobile ? 1 : 2}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Label>Zaměstnanec {smenaForm.otevrena ? '' : '*'}</Label>
                                        {!smenaForm.otevrena && smenaForm.kariera_id && (
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: mutedColor, cursor: 'pointer', marginBottom: 6 }}>
                                                <input type="checkbox" checked={smenaForm._zaskok || false}
                                                    onChange={e => setSmenaForm({ ...smenaForm, _zaskok: e.target.checked })}
                                                    className="accent-amber-500" />
                                                Záskok
                                            </label>
                                        )}
                                    </div>
                                    {/* Otevřená směna zaměstnance nemá — místo vypnutého
                                        výběru stojí na tom místě jen popiska, ať je jasné,
                                        že se tu nic vybírat nemá. */}
                                    {smenaForm.otevrena ? (
                                        <div style={{ ...iStyle, color: mutedColor, display: 'flex', alignItems: 'center' }}>— otevřená —</div>
                                    ) : (
                                        <Dropdown
                                            value={smenaForm.zamestnanec_id ? String(smenaForm.zamestnanec_id) : ''}
                                            onChange={(v) => { setSmenaForm({ ...smenaForm, zamestnanec_id: v }); setError(null); }}
                                            options={nabidkaZamestnancu.map(z => ({ value: String(z.id), label: `${z.jmeno} ${z.prijmeni}` }))}
                                            placeholder={nabidkaZamestnancu.length ? 'Vyber…' : 'Nikdo s touhle kariérou — zapni Záskok'}
                                            isDark={isDark} accent={SECTION_COLOR} height={38}
                                        />
                                    )}
                                </div>
                            </FormGrid>

                            {/* Čas */}
                            <FormGrid cols={isMobile ? 1 : 3}>
                                <div><Label>Od *</Label><input type="time" required value={smenaForm.cas_od} onChange={e => updateFormField('cas_od', e.target.value)} style={iStyle} /></div>
                                <div><Label>Do *</Label><input type="time" required value={smenaForm.cas_do} onChange={e => updateFormField('cas_do', e.target.value)} style={iStyle} /></div>
                                <div>
                                    <Label>Odpracováno</Label>
                                    <div style={{ ...iStyle, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 600, color: autoHodin ? SECTION_COLOR : mutedColor }}>{autoHodin ? fmtH(autoHodin) : '—'}</span>
                                    </div>
                                </div>
                            </FormGrid>

                            {/* ── Manažer směny ── */}
                            <div>
                                {(() => {
                                    const blocked = !!existujiciManazer;
                                    const manJmeno = existujiciManazer ? `${existujiciManazer.jmeno || ''} ${existujiciManazer.prijmeni || ''}`.trim() : '';
                                    return (
                                        <label style={{
                                            display: 'flex', alignItems: 'flex-start', gap: 10,
                                            padding: '10px 12px', borderRadius: 7,
                                            background: smenaForm.manazer_smeny ? 'rgba(255,138,0,0.08)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                            border: `1px solid ${smenaForm.manazer_smeny ? 'rgba(255,138,0,0.3)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                            cursor: blocked ? 'not-allowed' : 'pointer',
                                            opacity: blocked ? 0.75 : 1,
                                        }}>
                                            <input type="checkbox" disabled={blocked}
                                                checked={!!smenaForm.manazer_smeny}
                                                onChange={e => setSmenaForm({ ...smenaForm, manazer_smeny: e.target.checked })}
                                                style={{ marginTop: 2 }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600 }}>Manažer směny</div>
                                                {blocked ? (
                                                    <div style={{ fontSize: 11, color: '#FF8A00', marginTop: 4, lineHeight: 1.4 }}>
                                                        ⚠ Manažer pro tento den: <strong>{manJmeno}</strong>
                                                        {existujiciManazer.cas_od ? ` (${existujiciManazer.cas_od}–${existujiciManazer.cas_do})` : ''}.<br />
                                                        Nejdřív mu odeberte tuto roli v jeho směně.
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>Tento zaměstnanec potvrzuje výplaty záloh PINem. Jen jeden manažer na den.</div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })()}
                            </div>

                            {/* ── Vyžaduje vozidlo ── */}
                            <div>
                                <label style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 10,
                                    padding: '10px 12px', borderRadius: 7,
                                    background: smenaForm.pozaduje_vozidlo ? 'rgba(59,130,246,0.10)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                    border: `1px solid ${smenaForm.pozaduje_vozidlo ? 'rgba(59,130,246,0.35)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                    cursor: 'pointer',
                                }}>
                                    <input type="checkbox"
                                        checked={!!smenaForm.pozaduje_vozidlo}
                                        onChange={e => setSmenaForm({ ...smenaForm, pozaduje_vozidlo: e.target.checked })}
                                        style={{ marginTop: 2 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>Vyžaduje vozidlo</div>
                                        <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>Při startu si zaměstnanec vybere auto z dostupných. Defaultně se přebírá ze šablony.</div>
                                    </div>
                                </label>
                            </div>

                            {/* ── Vyžaduje pokladnu ── */}
                            <div>
                                <label style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 10,
                                    padding: '10px 12px', borderRadius: 7,
                                    background: (smenaForm.manazer_smeny || smenaForm.pozaduje_pokladnu) ? 'rgba(16,185,129,0.10)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                    border: `1px solid ${(smenaForm.manazer_smeny || smenaForm.pozaduje_pokladnu) ? 'rgba(16,185,129,0.35)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                    cursor: smenaForm.manazer_smeny ? 'not-allowed' : 'pointer',
                                    opacity: smenaForm.manazer_smeny ? 0.85 : 1,
                                }}>
                                    <input type="checkbox"
                                        checked={!!smenaForm.manazer_smeny || !!smenaForm.pozaduje_pokladnu}
                                        disabled={!!smenaForm.manazer_smeny}
                                        onChange={e => setSmenaForm({ ...smenaForm, pozaduje_pokladnu: e.target.checked })}
                                        style={{ marginTop: 2 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>Vyžaduje pokladnu</div>
                                        <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                                            {smenaForm.manazer_smeny
                                                ? 'Manažer směny vždy přebírá hlavní pokladnu — automaticky.'
                                                : 'Při startu si zaměstnanec převezme pokladnu. Defaultně se přebírá ze šablony.'}
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {/* ── Přestávky ── */}
                            <PauzyEditor
                                pauzy={smenaForm.pauzy}
                                isDark={isDark}
                                mutedColor={mutedColor}
                                iStyle={iStyle}
                                onChange={updatePauzy}
                            />

                            {/* ── Fáze směny — Zahájení a Ukončení ── */}
                            <div>
                                <Label>Fáze směny</Label>
                                <div style={{ fontSize: 11, color: mutedColor, marginBottom: 8 }}>
                                    Nastavujete Zahájení a Ukončení (délka + aktivity). Průběh je zbytek směny.
                                </div>
                                {(smenaForm.faze || []).filter(f => EDITOVATELNE_FAZE.includes(f.id)).map(f => (
                                    <FazeEditor
                                        key={f.id}
                                        faze={f}
                                        allFaze={smenaForm.faze}
                                        smenaOd={smenaForm.cas_od}
                                        smenaDo={smenaForm.cas_do}
                                        checklistSablony={checklistSablony}
                                        isDark={isDark}
                                        mutedColor={mutedColor}
                                        iStyle={iStyle}
                                        onChange={(updated) => {
                                            setSmenaForm(s => ({
                                                ...s,
                                                faze: (s.faze || []).map(x => (x.id === updated.id ? updated : x)),
                                            }));
                                        }}
                                    />
                                ))}
                            </div>

                            <div><Label>Poznámka</Label><input type="text" value={smenaForm.poznamka} onChange={e => setSmenaForm({ ...smenaForm, poznamka: e.target.value })} style={iStyle} placeholder="Volitelná poznámka…" /></div>
                        </div>
                    </form>
                </SidePanel>
                );
            })()}

            {/* Otevřená směna modal */}
            {otevrenaModal && (
                <Modal title={editingOtevrena ? 'Upravit otevřenou směnu' : 'Nová otevřená směna'} onClose={() => { setOtevrenaModal(false); setEditingOtevrena(null); setError(null); }} isDark={isDark} isMobile={isMobile} accentColor={SECTION_COLOR}>
                    <form onSubmit={handlePridatOtevrenu}>
                        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#EF4444' }}>{error}</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {!editingOtevrena && (
                                <div><Label>Datum *</Label><input type="date" required value={otevrenaForm.datum} onChange={e => setOtevrenaForm({ ...otevrenaForm, datum: e.target.value })} style={iStyle} /></div>
                            )}
                            {/* Kariéra → Role → Seniorita */}
                            <div style={{ padding: '12px 14px', borderRadius: 7, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <FormGrid cols={isMobile ? 1 : 2}>
                                    <div><Label>Kariéra *</Label>
                                        <select required value={otevrenaForm.kariera_id} onChange={e => setOtevrenaForm({ ...otevrenaForm, kariera_id: e.target.value, role_ids: [] })} style={iStyle}>
                                            <option value="">Vyber…</option>
                                            {kariery.map(g => <option key={g.id} value={g.id}>{g.ikona} {g.nazev}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>Min. seniorita</Label>
                                        <select value={otevrenaForm.min_uroven_kod || 'NOVICE'} onChange={e => setOtevrenaForm({ ...otevrenaForm, min_uroven_kod: e.target.value })} style={iStyle}>
                                            {['NOVICE','JUNIOR','MEDIOR','SENIOR','LEADER'].map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                </FormGrid>
                                {(() => {
                                    const activeRoles = vsechnyRole.filter(r => r.aktivni);
                                    if (activeRoles.length === 0) return null;
                                    const karId = String(otevrenaForm.kariera_id || '');
                                    const matched = activeRoles.filter(r => karId && String(r.kariera_id) === karId);
                                    const rest = activeRoles.filter(r => !karId || String(r.kariera_id) !== karId);
                                    const sorted = [...matched, ...rest];
                                    return (
                                        <div>
                                            <Label>Role</Label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {sorted.map(r => {
                                                    const sel = (otevrenaForm.role_ids || []).includes(r.id);
                                                    const isMatch = karId && String(r.kariera_id) === karId;
                                                    return (
                                                        <button key={r.id} type="button"
                                                            onClick={() => setOtevrenaForm(f => ({ ...f, role_ids: sel ? f.role_ids.filter(id => id !== r.id) : [...(f.role_ids || []), r.id] }))}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 4,
                                                                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: sel ? 600 : 400,
                                                                background: sel ? `${r.barva}20` : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                                                border: `1px solid ${sel ? r.barva : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                                color: sel ? r.barva : 'var(--text-secondary)',
                                                                opacity: isMatch || sel || !karId ? 1 : 0.5,
                                                                cursor: 'pointer', transition: 'all 0.15s',
                                                            }}>
                                                            {r.ikona} {r.nazev}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            <FormGrid cols={isMobile ? 1 : 2}>
                                <div><Label>Kapacita *</Label><input type="number" min="1" max="20" required value={otevrenaForm.kapacita} onChange={e => setOtevrenaForm({ ...otevrenaForm, kapacita: parseInt(e.target.value) })} style={iStyle} /></div>
                                <div><Label>Od *</Label><input type="time" required value={otevrenaForm.cas_od} onChange={e => setOtevrenaForm({ ...otevrenaForm, cas_od: e.target.value })} style={iStyle} /></div>
                                <div><Label>Do *</Label><input type="time" required value={otevrenaForm.cas_do} onChange={e => setOtevrenaForm({ ...otevrenaForm, cas_do: e.target.value })} style={iStyle} /></div>
                                <div><Label>Hodin *</Label><input type="number" step="0.5" required value={otevrenaForm.hodin} onChange={e => setOtevrenaForm({ ...otevrenaForm, hodin: e.target.value })} style={iStyle} /></div>
                            </FormGrid>
                            <div><Label>Poznámka</Label><input type="text" value={otevrenaForm.poznamka} onChange={e => setOtevrenaForm({ ...otevrenaForm, poznamka: e.target.value })} style={iStyle} placeholder="např. Potřeba 2 řidiči na večer" /></div>
                        </div>
                        <ModalActions onCancel={() => { setOtevrenaModal(false); setEditingOtevrena(null); setError(null); }} submitLabel={editingOtevrena ? 'Uložit změny' : 'Vytvořit'} loading={saving}
                            onSmazat={editingOtevrena ? async () => { if (await handleSmazatSmenu({ ...editingOtevrena, otevrena: true }) !== false) { setOtevrenaModal(false); setEditingOtevrena(null); } } : null} />
                    </form>
                </Modal>
            )}

            {/* Šablona modal */}
            {sablonModal && (
                <Modal title={typeof sablonModal === 'number' ? 'Upravit šablonu' : 'Nová šablona'} onClose={() => setSablonModal(false)} isDark={isDark} isMobile={isMobile} wide>
                    <form onSubmit={async (e) => {
                        e.preventDefault(); setSaving(true);
                        const isEdit = typeof sablonModal === 'number';
                        const url = isEdit ? `/api/shifts/sablony/${sablonModal}` : '/api/shifts/sablony';
                        const method = isEdit ? 'PUT' : 'POST';
                        const body = {
                            ...sablonForm,
                            kod: sablonForm.kod?.toUpperCase().slice(0, 6),
                            pauzy: sablonForm.pauzy || [],
                        };
                        const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(body) });
                        setSaving(false);
                        if (res.ok) { setSablonModal(false); setSablonForm(emptySablon); fetchSablony(); }
                        else { const d = await res.json(); setError(d.error || d.message || 'Chyba'); }
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {/* 1. Identifikace */}
                            <FormGrid cols={isMobile ? 1 : 3}>
                                <div><Label>Název *</Label><input type="text" required value={sablonForm.nazev} onChange={e => setSablonForm({ ...sablonForm, nazev: e.target.value })} style={iStyle} /></div>
                                <div><Label>Kód (max 6 znaků) *</Label><input type="text" required maxLength={6} value={sablonForm.kod} onChange={e => setSablonForm({ ...sablonForm, kod: e.target.value.toUpperCase() })} style={{ ...iStyle, fontFamily: 'monospace' }} disabled={typeof sablonModal === 'number'} /></div>
                                <div><Label>Barva</Label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input type="color" value={sablonForm.barva || '#F59E0B'} onChange={e => setSablonForm({ ...sablonForm, barva: e.target.value })}
                                            style={{ width: 36, height: 32, borderRadius: 5, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, cursor: 'pointer' }} />
                                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: mutedColor }}>{sablonForm.barva || '#F59E0B'}</span>
                                    </div>
                                </div>
                            </FormGrid>

                            {/* 2. Zařazení — Kariéra → Role → Seniorita */}
                            <div style={{ padding: '12px 14px', borderRadius: 7, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <FormGrid cols={isMobile ? 1 : 2}>
                                    <div><Label>Kariéra *</Label>
                                        <select required value={sablonForm.kariera_id} onChange={e => setSablonForm({ ...sablonForm, kariera_id: e.target.value, role_ids: [] })} style={iStyle}>
                                            <option value="">Vyber…</option>
                                            {kariery.map(k => <option key={k.id} value={k.id}>{k.ikona} {k.nazev}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>Min. úroveň *</Label>
                                        <select required value={sablonForm.min_uroven_kod || 'NOVICE'} onChange={e => setSablonForm({ ...sablonForm, min_uroven_kod: e.target.value })} style={iStyle}>
                                            {['NOVICE','JUNIOR','MEDIOR','SENIOR','LEADER'].map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                </FormGrid>
                                {(() => {
                                    const activeRoles = vsechnyRole.filter(r => r.aktivni);
                                    if (activeRoles.length === 0) return null;
                                    const karId = String(sablonForm.kariera_id || '');
                                    const matched = activeRoles.filter(r => karId && String(r.kariera_id) === karId);
                                    const rest = activeRoles.filter(r => !karId || String(r.kariera_id) !== karId);
                                    const sorted = [...matched, ...rest];
                                    return (
                                        <div>
                                            <Label>Role</Label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {sorted.map(r => {
                                                    const sel = (sablonForm.role_ids || []).includes(r.id);
                                                    const isMatch = karId && String(r.kariera_id) === karId;
                                                    return (
                                                        <button key={r.id} type="button"
                                                            onClick={() => setSablonForm(f => ({ ...f, role_ids: sel ? f.role_ids.filter(id => id !== r.id) : [...(f.role_ids || []), r.id] }))}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 4,
                                                                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: sel ? 600 : 400,
                                                                background: sel ? `${r.barva}20` : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                                                border: `1px solid ${sel ? r.barva : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                                color: sel ? r.barva : 'var(--text-secondary)',
                                                                opacity: isMatch || sel || !karId ? 1 : 0.5,
                                                                cursor: 'pointer', transition: 'all 0.15s',
                                                            }}>
                                                            {r.ikona} {r.nazev}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* 3. Čas */}
                            <FormGrid cols={isMobile ? 1 : 3}>
                                <div><Label>Od *</Label><input type="time" required value={sablonForm.cas_od} onChange={e => { const v = e.target.value; const h = vypocetHodin(v, sablonForm.cas_do, sablonForm.pauzy); setSablonForm({ ...sablonForm, cas_od: v, ...(h !== '' ? { hodin: h } : {}) }); }} style={iStyle} /></div>
                                <div><Label>Do *</Label><input type="time" required value={sablonForm.cas_do} onChange={e => { const v = e.target.value; const h = vypocetHodin(sablonForm.cas_od, v, sablonForm.pauzy); setSablonForm({ ...sablonForm, cas_do: v, ...(h !== '' ? { hodin: h } : {}) }); }} style={iStyle} /></div>
                                <div><Label>Hodin *</Label><input type="number" step="0.5" required value={sablonForm.hodin} readOnly style={{ ...iStyle, opacity: 0.7 }} /></div>
                            </FormGrid>

                            {/* 4. Plánování */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                    <Label style={{ marginBottom: 0 }}>Dny v týdnu</Label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: mutedColor, cursor: 'pointer' }}>
                                        <span style={{ fontSize: 11, color: (sablonForm.delka_cyklu || 1) > 1 ? SECTION_COLOR : mutedColor, fontWeight: 600 }}>S cyklem</span>
                                        <div onClick={() => {
                                            const novy = (sablonForm.delka_cyklu || 1) > 1 ? 1 : 2;
                                            const noveDny = novy > 1 ? { '1': sablonForm.dny_v_tydnu || [1,2,3,4,5], '2': [] } : null;
                                            setSablonForm({ ...sablonForm, delka_cyklu: novy, dny_v_cyklu: noveDny });
                                        }} style={{ width: 36, height: 20, borderRadius: 7, background: (sablonForm.delka_cyklu || 1) > 1 ? SECTION_COLOR : isDark ? 'rgba(255,255,255,0.15)' : '#ccc', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                                            <div style={{ width: 16, height: 16, borderRadius: 6, background: '#fff', position: 'absolute', top: 2, left: (sablonForm.delka_cyklu || 1) > 1 ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                        </div>
                                    </label>
                                    {(sablonForm.delka_cyklu || 1) > 1 && (
                                        <select value={sablonForm.delka_cyklu} onChange={e => {
                                            const novaDelka = parseInt(e.target.value);
                                            const stare = sablonForm.dny_v_cyklu || {};
                                            const nove = {};
                                            for (let t = 1; t <= novaDelka; t++) nove[String(t)] = stare[String(t)] || [];
                                            setSablonForm({ ...sablonForm, delka_cyklu: novaDelka, dny_v_cyklu: nove });
                                        }} style={{ ...iStyle, width: 80, padding: '4px 8px', fontSize: 12 }}>
                                            <option value="2">2 týdny</option>
                                            <option value="3">3 týdny</option>
                                            <option value="4">4 týdny</option>
                                        </select>
                                    )}
                                </div>

                                {(sablonForm.delka_cyklu || 1) <= 1 ? (
                                    /* Jednoduchá šablona — checkboxy dnů */
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {[1, 2, 3, 4, 5, 6, 7].map(d => {
                                            const active = (sablonForm.dny_v_tydnu || []).includes(d);
                                            return (
                                                <button key={d} type="button" onClick={() => { const dny = active ? sablonForm.dny_v_tydnu.filter(x => x !== d) : [...(sablonForm.dny_v_tydnu || []), d]; setSablonForm({ ...sablonForm, dny_v_tydnu: dny }); }}
                                                    style={{ width: 40, height: 40, borderRadius: 7, border: `1px solid ${active ? SECTION_COLOR + '50' : isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, background: active ? `${SECTION_COLOR}18` : 'transparent', color: active ? SECTION_COLOR : mutedColor, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                    {dnyLabels[d]}
                                                </button>
                                            );
                                        })}
                                        <div style={{ width: 1, height: 24, background: isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)', margin: '0 2px' }} />
                                        <button type="button" onClick={() => setSablonForm({ ...sablonForm, svatek: !sablonForm.svatek })}
                                            title="Svátek — šablona se generuje ve státní svátky bez ohledu na den v týdnu"
                                            style={{ width: 40, height: 40, borderRadius: 7, border: `1px solid ${sablonForm.svatek ? '#EF444450' : isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, background: sablonForm.svatek ? 'rgba(239,68,68,0.12)' : 'transparent', color: sablonForm.svatek ? '#EF4444' : mutedColor, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                            Sv
                                        </button>
                                    </div>
                                ) : (
                                    /* Cyklická šablona — Week × Day grid */
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 50, fontSize: 10, fontWeight: 600, color: mutedColor, padding: '4px 6px', textAlign: 'left' }}></th>
                                                    {[1,2,3,4,5,6,7].map(d => (
                                                        <th key={d} style={{ fontSize: 10, fontWeight: 600, color: d >= 6 ? '#EF4444' : mutedColor, padding: '4px 6px', textAlign: 'center', width: 44 }}>{dnyLabels[d]}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.from({ length: sablonForm.delka_cyklu }, (_, ti) => {
                                                    const tyden = ti + 1;
                                                    const tydenKey = String(tyden);
                                                    const dnyProTyden = (sablonForm.dny_v_cyklu || {})[tydenKey] || [];
                                                    return (
                                                        <tr key={tyden}>
                                                            <td style={{ fontSize: 11, fontWeight: 600, color: SECTION_COLOR, padding: '4px 6px' }}>T{tyden}</td>
                                                            {[1,2,3,4,5,6,7].map(d => {
                                                                const active = dnyProTyden.includes(d);
                                                                return (
                                                                    <td key={d} style={{ padding: '3px 4px', textAlign: 'center' }}>
                                                                        <button type="button" onClick={() => {
                                                                            const noveDny = active ? dnyProTyden.filter(x => x !== d) : [...dnyProTyden, d];
                                                                            const noveCyklu = { ...(sablonForm.dny_v_cyklu || {}), [tydenKey]: noveDny };
                                                                            setSablonForm({ ...sablonForm, dny_v_cyklu: noveCyklu });
                                                                        }} style={{
                                                                            width: 34, height: 34, borderRadius: 6,
                                                                            border: `1px solid ${active ? SECTION_COLOR + '50' : isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                                                            background: active ? `${SECTION_COLOR}18` : 'transparent',
                                                                            color: active ? SECTION_COLOR : mutedColor,
                                                                            fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                                                        }}>{active ? '✓' : ''}</button>
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                                            <button type="button" onClick={() => setSablonForm({ ...sablonForm, svatek: !sablonForm.svatek })}
                                                title="Svátek — šablona se generuje ve státní svátky bez ohledu na den v týdnu"
                                                style={{ width: 40, height: 40, borderRadius: 7, border: `1px solid ${sablonForm.svatek ? '#EF444450' : isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, background: sablonForm.svatek ? 'rgba(239,68,68,0.12)' : 'transparent', color: sablonForm.svatek ? '#EF4444' : mutedColor, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                Sv
                                            </button>
                                            <span style={{ fontSize: 11, color: mutedColor }}>Platí ve státní svátky (místo běžného dne)</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* ── Manažer směny ── */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, background: sablonForm.manazer_smeny ? 'rgba(255,138,0,0.08)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${sablonForm.manazer_smeny ? 'rgba(255,138,0,0.3)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={!!sablonForm.manazer_smeny} onChange={e => setSablonForm({ ...sablonForm, manazer_smeny: e.target.checked })} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>Manažer směny</div>
                                        <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>Zaměstnanec této směny potvrzuje výplaty záloh PINem. Jen jeden manažer na den.</div>
                                    </div>
                                </label>
                            </div>

                            {/* ── Vyžaduje vozidlo ── */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, background: sablonForm.pozaduje_vozidlo ? 'rgba(12,192,223,0.10)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${sablonForm.pozaduje_vozidlo ? 'rgba(12,192,223,0.35)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={!!sablonForm.pozaduje_vozidlo} onChange={e => setSablonForm({ ...sablonForm, pozaduje_vozidlo: e.target.checked })} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>🚗 Vyžaduje vozidlo</div>
                                        <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>Zaměstnanec při zahájení vybere auto z dostupných. Auto se uvolní automaticky při ukončení směny.</div>
                                    </div>
                                </label>
                            </div>

                            {/* ── Vyžaduje pokladnu ── */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, background: (sablonForm.manazer_smeny || sablonForm.pozaduje_pokladnu) ? 'rgba(16,185,129,0.10)' : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${(sablonForm.manazer_smeny || sablonForm.pozaduje_pokladnu) ? 'rgba(16,185,129,0.35)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, cursor: sablonForm.manazer_smeny ? 'not-allowed' : 'pointer', opacity: sablonForm.manazer_smeny ? 0.85 : 1 }}>
                                    <input type="checkbox" checked={!!sablonForm.manazer_smeny || !!sablonForm.pozaduje_pokladnu} disabled={!!sablonForm.manazer_smeny} onChange={e => setSablonForm({ ...sablonForm, pozaduje_pokladnu: e.target.checked })} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>💵 Vyžaduje pokladnu</div>
                                        <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                                            {sablonForm.manazer_smeny
                                                ? 'Manažer směny vždy přebírá hlavní pokladnu — automaticky.'
                                                : 'Zaměstnanec při zahájení převezme pokladnu. Pokladna se uzavře automaticky při ukončení směny.'}
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {/* ── Přestávky ── */}
                            <PauzyEditor
                                pauzy={sablonForm.pauzy}
                                isDark={isDark}
                                mutedColor={mutedColor}
                                iStyle={iStyle}
                                onChange={(pauzy) => setSablonForm(s => {
                                    const h = vypocetHodin(s.cas_od, s.cas_do, pauzy);
                                    return { ...s, pauzy, ...(h !== '' ? { hodin: h } : {}) };
                                })}
                            />

                            {/* ── Fáze směny — Zahájení a Ukončení ── */}
                            <div>
                                <Label>Fáze směny</Label>
                                <div style={{ fontSize: 11, color: mutedColor, marginBottom: 8 }}>
                                    Nastavujete Zahájení a Ukončení (délka + aktivity). Průběh je zbytek směny.
                                </div>
                                {(sablonForm.faze || []).filter(f => EDITOVATELNE_FAZE.includes(f.id)).map(f => (
                                    <FazeEditor
                                        key={f.id}
                                        faze={f}
                                        allFaze={sablonForm.faze}
                                        smenaOd={sablonForm.cas_od}
                                        smenaDo={sablonForm.cas_do}
                                        checklistSablony={checklistSablony}
                                        isDark={isDark}
                                        mutedColor={mutedColor}
                                        iStyle={iStyle}
                                        onChange={(updated) => {
                                            setSablonForm(s => ({
                                                ...s,
                                                faze: (s.faze || []).map(x => (x.id === updated.id ? updated : x)),
                                            }));
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <ModalActions onCancel={() => setSablonModal(false)} submitLabel={typeof sablonModal === 'number' ? 'Uložit' : 'Vytvořit šablonu'} loading={saving} />
                    </form>
                </Modal>
            )}

            {/* Generovat modal */}
            {genModal && (
                <Modal title="Generovat směny" onClose={() => { setGenModal(false); setGenVysledek(null); }} isDark={isDark} isMobile={isMobile} wide>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* Mode toggle. Vedle něj referenční datum cyklu — stávalo
                            v hlavičce Šablon, kam nikdo nechodil, a přitom se na něj
                            kouká právě teď: od něj se počítají vícetýdenní cykly. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: 4, background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)', borderRadius: 7, padding: 3, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                {[{ id: 'sablony', label: 'Ze šablony' }, { id: 'prirazeni', label: 'Z přiřazení' }].map(m => (
                                    <button key={m.id} onClick={() => { setGenMode(m.id); setGenVysledek(null); }} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, background: genMode === m.id ? `${SECTION_COLOR}20` : 'transparent', color: genMode === m.id ? SECTION_COLOR : mutedColor, transition: 'all 0.15s' }}>{m.label}</button>
                                ))}
                            </div>
                            {refDatumCyklu}
                        </div>

                        <FormGrid cols={isMobile ? 1 : 2}>
                            <div><Label>Od *</Label><input type="date" value={genForm.od} onChange={e => setGenForm({ ...genForm, od: e.target.value })} style={iStyle} /></div>
                            <div><Label>Do *</Label><input type="date" value={genForm.do} onChange={e => setGenForm({ ...genForm, do: e.target.value })} style={iStyle} /></div>
                        </FormGrid>

                        {genMode === 'sablony' ? (
                            /* Ad-hoc: šablona → zaměstnanec páry */
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Label>Šablona → zaměstnanec</Label>
                                    <button type="button" onClick={() => setGenPrirazeni([...genPrirazeni, { sablon_id: '', zamestnanec_id: '' }])} style={{ fontSize: 12, color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Řádek</button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {genPrirazeni.map((p, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <select value={p.sablon_id} onChange={e => { const a = [...genPrirazeni]; a[i].sablon_id = e.target.value; setGenPrirazeni(a); }} style={{ ...iStyle, flex: 1 }}>
                                                <option value="">Šablona…</option>
                                                {sablony.filter(s => s.aktivni).map(s => <option key={s.id} value={s.id}>{s.nazev} ({s.cas_od?.slice(0, 5)}–{s.cas_do?.slice(0, 5)}){(s.delka_cyklu || 1) > 1 ? ` · ${s.delka_cyklu}T` : ''}</option>)}
                                            </select>
                                            <select value={p.zamestnanec_id} onChange={e => { const a = [...genPrirazeni]; a[i].zamestnanec_id = e.target.value; setGenPrirazeni(a); }} style={{ ...iStyle, flex: 1 }} title="Prázdné = otevřená směna">
                                                <option value="">— Otevřená směna —</option>
                                                {zamestnanci.filter(z => z.stav !== 'neaktivni').map(z => <option key={z.id} value={z.id}>{z.jmeno} {z.prijmeni}</option>)}
                                            </select>
                                            {genPrirazeni.length > 1 && (
                                                <button type="button" onClick={() => setGenPrirazeni(genPrirazeni.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', flexShrink: 0 }}><X size={16} /></button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* Z přiřazení: multi-select zaměstnanců */
                            <div>
                                <Label>Zaměstnanci s přiřazením</Label>
                                {(() => {
                                    const aktivni = prirazeni.filter(p => !p.platnost_do);
                                    const zamIds = [...new Set(aktivni.map(p => p.zamestnanec_id))];
                                    if (zamIds.length === 0) return <div style={{ fontSize: 12, color: mutedColor, padding: '8px 0' }}>Žádní zaměstnanci nemají přiřazené šablony. Přiřaďte je v záložce Šablony.</div>;

                                    // Výpočet aktuálního týdnu cyklu
                                    const tydenCyklu = (delka) => {
                                        if (delka <= 1) return null;
                                        const ref = new Date(referencniDatum + 'T12:00:00');
                                        const ted = new Date();
                                        const dow = ted.getDay();
                                        const monday = new Date(ted);
                                        monday.setDate(ted.getDate() - (dow === 0 ? 6 : dow - 1));
                                        const diff = Math.floor((monday - ref) / (7 * 24 * 60 * 60 * 1000));
                                        return ((diff % delka) + delka) % delka + 1;
                                    };

                                    return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {zamIds.map(zamId => {
                                                const zam = zamestnanci.find(z => z.id === zamId);
                                                const zamPr = aktivni.filter(p => p.zamestnanec_id === zamId);
                                                const checked = genSelZam.includes(zamId);
                                                const maxCyklus = Math.max(...zamPr.map(p => p.sablon?.delka_cyklu || 1));
                                                const tyden = tydenCyklu(maxCyklus);
                                                return (
                                                    <label key={zamId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 7, background: checked ? `${SECTION_COLOR}10` : 'transparent', border: `1px solid ${checked ? SECTION_COLOR + '30' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={checked} onChange={e => setGenSelZam(e.target.checked ? [...genSelZam, zamId] : genSelZam.filter(id => id !== zamId))} />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{zam?.jmeno} {zam?.prijmeni}</div>
                                                            <div style={{ fontSize: 11, color: mutedColor }}>{zamPr.map(p => p.sablon?.nazev).join(', ')}</div>
                                                        </div>
                                                        {tyden && <span style={{ fontSize: 10, fontWeight: 700, color: SECTION_COLOR, background: `${SECTION_COLOR}15`, padding: '2px 8px', borderRadius: 5 }}>T{tyden}</span>}
                                                    </label>
                                                );
                                            })}
                                            <button type="button" onClick={() => setGenSelZam(genSelZam.length === zamIds.length ? [] : [...zamIds])} style={{ alignSelf: 'flex-start', fontSize: 11, color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, marginTop: 2 }}>
                                                {genSelZam.length === zamIds.length ? 'Odznačit vše' : 'Vybrat vše'}
                                            </button>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {genVysledek && (
                            <div style={{ background: genVysledek.chyby?.length > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)', border: `1px solid ${genVysledek.chyby?.length > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'}`, borderRadius: 7, padding: '10px 14px' }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#10B981', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <CheckCircle2 size={14} /> Vytvořeno {genVysledek.vytvoreno} směn
                                </div>
                                {genVysledek.chyby?.map((ch, i) => <div key={i} style={{ fontSize: 12, color: SECTION_COLOR, marginTop: 4 }}>⚠ {ch}</div>)}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                            <button onClick={() => handleGenerovat(false)} disabled={genLoading || !genForm.od || !genForm.do} style={{ background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 7, padding: '10px 20px', fontWeight: 600, fontSize: 13, cursor: genLoading ? 'not-allowed' : 'pointer', opacity: genLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <RefreshCw size={14} />{genLoading ? 'Generuji…' : 'Generovat'}
                            </button>
                            <button onClick={() => { setGenModal(false); setGenVysledek(null); }} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-default)', borderRadius: 7, padding: '10px 16px', fontSize: 13, cursor: 'pointer' }}>Zavřít</button>
                        </div>
                    </div>
                </Modal>
            )}

            {vratitSmenaAdmin && (
                <VratitSmenuModal
                    smena={vratitSmenaAdmin}
                    onClose={() => setVratitSmenaAdmin(null)}
                    onSuccess={handleVratitSuccess}
                    isDark={isDark}
                />
            )}

            {prohoditSmena && (
                <ProhoditSmenuModal
                    smena={prohoditSmena}
                    onClose={() => setProhoditSmena(null)}
                    onSuccess={refreshTab}
                    isDark={isDark}
                />
            )}

            {publikovatModal && (() => {
                // Co se publikací reálně stane. Počítá se z **nefiltrovaných** sad
                // a z rozsahu v modálu, ne z období na pozadí — rozsah se dá přepsat.
                const vRozsahu = (x) => {
                    const d = x.datum?.split?.('T')?.[0] || String(x.datum).split('T')[0];
                    return d >= publikovatForm.od && d <= publikovatForm.do;
                };
                const vsechnyDotcene = [...smeny, ...otevrene.map(o => ({ ...o, otevrena: true }))].filter(vRozsahu);

                const nove = vsechnyDotcene.filter(x => x.draft && !x.draft_smazano && !x.publikovano_at);
                const zmenene = vsechnyDotcene.filter(x => x.draft && !x.draft_smazano && x.publikovano_at);
                const keSmazani = vsechnyDotcene.filter(x => x.draft_smazano);
                const dnesStr = todayStr();
                // Zpráva odejde jen za směnu, kterou už někdo viděl a ještě nebyla.
                const dostaneZpravu = (x) => x.draft === false && !!x.zamestnanec_id
                    && (x.datum?.split?.('T')?.[0] || String(x.datum).split('T')[0]) >= dnesStr;
                const komuOdejde = new Set(keSmazani.filter(dostaneZpravu).map(x => x.zamestnanec_id)).size;

                const radekSmeny = (x) => {
                    const d = x.datum?.split?.('T')?.[0] || String(x.datum).split('T')[0];
                    const sab = x.sablon || (x.sablon_id ? sablony.find(y => y.id === x.sablon_id) : null);
                    return (
                        <div key={`${x.otevrena ? 'o' : 's'}-${x.id}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', fontSize: 11.5,
                                color: 'var(--text-secondary)',
                                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                            <span style={{ color: mutedColor, fontVariantNumeric: 'tabular-nums', minWidth: 42, flexShrink: 0 }}>
                                {new Date(d + 'T12:00:00').toLocaleDateString('cs', { day: 'numeric', month: 'numeric' })}
                            </span>
                            {sab?.kod && <span style={{ fontWeight: 700, color: sab.barva || mutedColor, flexShrink: 0 }}>{sab.kod}</span>}
                            <span style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                {(x.cas_od || '').slice(0, 5)}–{(x.cas_do || '').slice(0, 5)}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {x.otevrena
                                    ? <i style={{ color: mutedColor }}>neobsazená</i>
                                    : x.zamestnanec ? `${x.zamestnanec.jmeno} ${x.zamestnanec.prijmeni}` : <i style={{ color: mutedColor }}>nepřiřazená</i>}
                            </span>
                            {x.draft_smazano && (
                                <span style={{ fontSize: 9.5, fontWeight: 600, padding: '1px 7px', borderRadius: 99, flexShrink: 0,
                                    ...(dostaneZpravu(x)
                                        ? { background: 'rgba(239,68,68,0.14)', color: '#EF4444' }
                                        : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: mutedColor }) }}>
                                    {dostaneZpravu(x) ? 'dostane zprávu' : 'nikdo ji neviděl'}
                                </span>
                            )}
                        </div>
                    );
                };

                const skupina = (id, znak, barva, label, polozky) => polozky.length > 0 && (
                    <div key={id} style={{ borderRadius: 8, overflow: 'hidden',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                        <button onClick={() => setRozbalenoVPublikaci(p => p === id ? null : id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                                padding: '9px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: `${barva}2e`, color: barva }}>{znak}</span>
                            <span style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 600 }}>
                                {polozky.length} {label}
                            </span>
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: mutedColor }}>
                                {rozbalenoVPublikaci === id ? '▾ skrýt' : '▸ vypsat'}
                            </span>
                        </button>
                        {rozbalenoVPublikaci === id && (
                            <div style={{ maxHeight: 190, overflowY: 'auto',
                                background: isDark ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.02)' }}>
                                {polozky
                                    .slice()
                                    .sort((a, b) => String(a.datum).localeCompare(String(b.datum)))
                                    .map(radekSmeny)}
                            </div>
                        )}
                    </div>
                );

                return (
                <Modal title="Publikovat směny" onClose={() => setPublikovatModal(false)} isDark={isDark} isMobile={isMobile}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* Rozsah od–do */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Od</label>
                                <input type="date" value={publikovatForm.od} onChange={e => setPublikovatForm(f => ({ ...f, od: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-elevated)', color: 'var(--text-primary)', fontSize: 13 }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Do</label>
                                <input type="date" value={publikovatForm.do} onChange={e => setPublikovatForm(f => ({ ...f, do: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-elevated)', color: 'var(--text-primary)', fontSize: 13 }} />
                            </div>
                        </div>

                        {/* Soupis změn — publikace maže natvrdo, tak ať je předem vidět co
                            a komu o tom odejde zpráva. */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {skupina('nove', '+', '#10B981', nove.length === 1 ? 'nová směna' : 'nových směn', nove)}
                            {skupina('zmenene', '±', '#F59E0B', zmenene.length === 1 ? 'změněná směna' : 'změněných směn', zmenene)}
                            {skupina('smazani', '−', '#EF4444', keSmazani.length === 1 ? 'směna ke smazání' : 'směn ke smazání', keSmazani)}
                            {nove.length + zmenene.length + keSmazani.length === 0 && (
                                <div style={{ fontSize: 12, color: mutedColor, padding: '10px 0', textAlign: 'center' }}>
                                    V tomhle rozsahu není co publikovat.
                                </div>
                            )}
                            {komuOdejde > 0 && (
                                <div style={{ fontSize: 11.5, lineHeight: 1.5, padding: '8px 10px', borderRadius: 8,
                                    background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.24)', color: '#ff9a9a' }}>
                                    Zrušení směny se dozví {komuOdejde === 1 ? 'jeden člověk' : `${komuOdejde} lidí`} — odejde jim zpráva.
                                </div>
                            )}
                        </div>

                        {/* Režim: teď vs naplánovat */}
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Kdy publikovat</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {[{ id: 'teď', label: 'Ihned' }, { id: 'naplan', label: 'Naplánovat' }].map(m => (
                                    <button key={m.id} onClick={() => setPublikovatForm(f => ({ ...f, mode: m.id }))}
                                        style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: publikovatForm.mode === m.id ? `1.5px solid #10B981` : '1px solid var(--border-default)', background: publikovatForm.mode === m.id ? '#10B98118' : 'transparent', color: publikovatForm.mode === m.id ? '#10B981' : 'var(--text-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Datetime pokud naplánovat */}
                        {publikovatForm.mode === 'naplan' && (
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Datum a čas publikace</label>
                                <input type="datetime-local" value={publikovatForm.naplanovat_na} onChange={e => setPublikovatForm(f => ({ ...f, naplanovat_na: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-elevated)', color: 'var(--text-primary)', fontSize: 13 }} />
                                <div style={{ fontSize: 11, color: mutedColor, marginTop: 4 }}>V určený okamžik systém automaticky spustí publikaci.</div>
                            </div>
                        )}

                        {/* Akce */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setPublikovatModal(false)} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}>Zrušit</button>
                            <button
                                onClick={async () => {
                                    if (!publikovatForm.od || !publikovatForm.do) { alert('Vyplň rozsah'); return; }
                                    if (publikovatForm.mode === 'naplan' && !publikovatForm.naplanovat_na) { alert('Zvol datum a čas publikace'); return; }
                                    const body = { od: publikovatForm.od, do: publikovatForm.do };
                                    if (publikovatForm.mode === 'naplan') {
                                        // datetime-local vrací lokální čas bez TZ ("2026-04-21T14:55")
                                        // Převedeme na ISO UTC aby backend interpretoval správně
                                        body.naplanovat_na = new Date(publikovatForm.naplanovat_na).toISOString();
                                    }
                                    const res = await fetch('/api/shifts/publikovat', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                                        body: JSON.stringify(body),
                                    });
                                    if (!res.ok) {
                                        const e = await res.json().catch(() => ({}));
                                        if (Array.isArray(e.dny_bez_manazera) && e.dny_bez_manazera.length > 0) {
                                            const fmt = e.dny_bez_manazera.map(d => {
                                                const dt = new Date(d + 'T12:00:00');
                                                return dt.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
                                            }).join(', ');
                                            alert(`Nelze publikovat — v těchto dnech chybí manažer směny:\n\n${fmt}\n\nPřidejte manažera do jedné ze směn v daném dni.`);
                                        } else {
                                            alert(e.error || 'Publikace selhala');
                                        }
                                        return;
                                    }
                                    setPublikovatModal(false);
                                    refreshTab();
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 7, border: 'none', background: '#10B981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                                <CheckCircle2 size={14} />
                                {publikovatForm.mode === 'naplan' ? 'Naplánovat' : 'Publikovat ihned'}
                            </button>
                        </div>
                    </div>
                </Modal>
                );
            })()}
        </div>
    );
}
